import {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {
  Search,
  RefreshCw,
  Film,
  Eye,
  Tag,
  Languages,
  X,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  Folder as FolderIcon,
  Check,
  Clock,
  CalendarClock,
  Pencil,
  MoreHorizontal,
  MoreVertical,
  FolderInput,
  Plus,
  AlertCircle,
  ShieldAlert,
  Images,
  EyeOff,
  RotateCcw,
  Share2,
  Play,
  Copy,
  Maximize2,
  Wand2
} from 'lucide-react';
import {
  Alert,
  AlertAction,
  AlertTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Progress,
  ProgressLabel,
  ProgressValue,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {formValueFor} from './InvestigationFormFields';
import {NewInvestigationDialog} from './NewInvestigationDialog';
import {ImageSearchLeads} from './ImageSearchLeads';
import {ClassifyDialog} from './ClassifyDialog';
import {FollowerNetworkGraph} from './FollowerNetworkGraph';
import {ScheduleDialog} from './ScheduleDialog';
import {ShareDialog} from './ShareDialog';
import {
  CONTEXT_MENU,
  DROPDOWN_MENU,
  FolderTreeRow,
  folderRowTooltip,
  InvestigationTreeRow,
  JOB_TYPE_CONFIG,
  RAIL_CARDS_INSET,
  RAIL_ROW,
  RAIL_ROW_DROP,
  RAIL_ROW_IDLE,
  RailInvestigationCount,
  investigationCountLabel,
  railIndent,
  type RailMenu
} from './InvestigationTree';
import {
  type BadgeScope,
  folderBadgeCount,
  groupJobsByFolder,
  rollUpSubtreeCounts
} from './sidebarCounts';
import {
  filtersKey,
  type JobScope,
  jobListQuery,
  type JobServerFilters,
  chunked,
  JOB_IDS_PER_REQUEST,
  mergeJobs,
  pageSizeFor,
  parseScopeKey,
  type ScopePageState,
  scopeIsFiltered,
  scopeKey,
  UNFILED_SCOPE
} from './jobScopes';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {
  useUiStore,
  type ThumbnailSize,
  type ItemFilterState,
  type SmartFilterSettings,
  type SmartFilterIncludeMode,
  THUMBNAIL_SIZE_MIN,
  THUMBNAIL_SIZE_MAX,
  DEFAULT_ITEM_FILTERS,
  DEFAULT_SMART_FILTER,
  INVESTIGATIONS_RAIL_WIDTH_MIN,
  INVESTIGATIONS_RAIL_WIDTH_MAX,
  INVESTIGATIONS_RAIL_WIDTH_DEFAULT
} from '@/store/uiStore';
import {parseSearchQuery, matchesItemSearchQuery} from '@/lib/searchQuery';
import {CreateP0AlertDialog, useP0AlertDialog} from '@/components/p0alerts/CreateP0AlertDialog';
import {getApiErrorMessage} from '@/api/client';
import {
  type ImageMatchTier,
  imageMatchTierLabels,
  type Job,
  jobDisplayLabel,
  jobsApi,
  JOBS_PAGE_SIZE
} from '@/api/jobs';
import {
  type Folder,
  foldersApi,
  flattenFolders,
  folderSubtreeIds,
  withFolderBodies
} from '@/api/folders';
import {type Classification} from '@/api/classifications';
import {
  type ChannelAggregate,
  type Item,
  type ItemsPageWithoutTotal,
  type ItemsQuery,
  type SubmissionStatusGroup,
  DEFAULT_ITEM_SORT,
  ITEM_FLAGS_MAX_IDS,
  itemsApi
} from '@/api/items';
import {itemFilterQuery} from '@/components/items/ItemFiltersMenu';
import {
  ActiveFilterChips,
  useActiveFilterChips
} from '@/components/investigations/ActiveFilterChips';
import {InvestigationFilters} from '@/components/investigations/InvestigationFilters';
import {ItemDetailsSheet} from '@/components/items/ItemDetailPanel';
import {ItemsCollection} from '@/components/items/ItemsCollection';
import {GRID_COLS_BY_SIZE} from '@/components/items/gridColumns';
import {ItemsBulkBar, ItemsViewToggle} from '@/components/items/ItemsToolbar';
import {getSubmissionStatusTags, SubmissionStatusTags} from '@/components/items/ItemBadges';
import {INVESTIGATION_HEADER_COMPACT_BELOW} from '@/lib/filterBar';
import {useBelowWidth} from '@/lib/useBelowWidth';
import {FilterBar} from '@/components/items/FilterBar';
import {ResponsiveThumbnailSizeControl} from '@/components/shared/ThumbnailSizeControl';
import {matchesFlagFilter, type FlagFilter} from '@/lib/flagFilter';
import {SortMenu} from '@/components/items/SortMenu';
import {SensitiveMediaButton, SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {jobStatusTone} from '@/lib/status';
import {describeCron} from '@/components/shared/ScheduleInput';
import {type ConfigItem} from '@/api/config';
import {type ChannelStatus} from '@/api/channels';
import {
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
  useDeleteJob,
  useSetJobFolder,
  useSetItemStatus,
  useBulkSetItemStatus,
  useBulkSetItemStatusByAuthor,
  useRerunJob,
  useJobFacets,
  useJobImage,
  useJobImageMatches,
  useCheckChannelStatuses,
  useChannelAggregates,
  type ChannelAggregateScope
} from '@/api/hooks';
import {ImageLightbox} from '@/components/shared/ImageLightbox';
import {MultiSelectFilterBody} from '@/components/shared/MultiSelectFilterBody';
import {useTranslationStore} from '@/store/translationStore';
import {imageMatchIdFor} from '@/lib/imageMatchId';
import {itemIdsForClipboard} from '@/lib/itemIds';
import {useDebounce} from '@/lib/useDebounce';
import {
  DEFAULT_AFFILIATE,
  PLATFORM_ID,
  PLATFORM_NAME,
  CONTENT_LABEL,
  ITEM_NOUN,
  ITEM_NOUN_PLURAL
} from '@/lib/platform';
import {getDeclaredLanguageSubtag, getTranslatableSegments} from '@/lib/itemMetadata';
import {buildChannelItemMetadataFields} from '@/lib/videoItemMetadata';
import {cn} from '@/lib/utils';
import {useAuth} from '@/context/useAuth';
import {type FlagColor} from '@/api/flags';
import {ChannelFlagPicker} from '@/components/flags/ChannelFlagPicker';
import {showToast} from '@/lib/toast';

const PAGE_SIZE = 50;
// Caps the auto-load-more effect below. Without a bound, a job that's mostly triaged (most items
// hidden by the showClassified/showDismissed filters, which default to false) can never reach a
// full visible page, so the effect would chain-fetch every remaining page on every open.
const MAX_AUTO_LOAD_ROUNDS = 3;
// The page size of a selected job's channel prefetch (shouldPrefetchJobChannels). The API's
// maximum, since each request has a fixed cost.
// Must stay an exact multiple of PAGE_SIZE: itemPage counts PAGE_SIZE pages, and advances by
// GROUP_PAGES_PER_ROUND per round, so a non-multiple would misalign the next plain "Load more".
const GROUP_PAGE_SIZE = 1000;
const GROUP_PAGES_PER_ROUND = GROUP_PAGE_SIZE / PAGE_SIZE;
// Single-fetch page size for one expanded channel's items in a selected JOB (ensureChannelItemsLoaded)
// - comfortably above what one channel realistically holds, well under the API's own 1,000 cap, and
// with no "Load more" of its own: a channel this busy is rare enough that paging its own item list
// isn't worth the complexity yet. Its own header count can still exceed this, so a truncation note
// (channelItemsTruncatedKeys) says so rather than the two silently disagreeing.
const CHANNEL_ITEMS_PAGE_SIZE = 200;
// Channel group headers rendered per "Show more channels" step. A folder can aggregate tens of
// thousands of channels, and mounting every header in one commit freezes the tab.
const CHANNEL_GROUPS_PAGE_SIZE = 100;
// The selection's item total for display: "…" while its count is in flight (or failed), and
// "N+" when a folder's count stopped at its cap.
const formatItemsTotal = (total: number | null, capped: boolean) =>
  total === null ? '…' : `${total.toLocaleString()}${capped ? '+' : ''}`;

// The job-mode smart-filter prefetch (below) isn't guessing at an open-ended total - a job's exact
// item count is already sitting in `itemsTotal` from its own count request. So it asks for only as
// many rounds as that count actually needs - a 5,000-item cap silently stopped a 10,000-
// item job's prefetch halfway, leaving the back half of channels un-hideable until individually
// expanded. 200 rounds (200,000 items) is still a hard stop against a pathological job, not a
// realistic ceiling for one.
export const JOB_GROUP_PREFETCH_MAX_ROUNDS = 200;

// ShouldPrefetchJobChannels re-fires with a full re-walk of the job's whole current total (see its
// own doc comment for why a delta-sized fetch isn't safe), so a flat GROUP_PAGE_SIZE growth
// threshold makes the *number* of re-fires over a job's life scale linearly with its size - for a
// job that grows steadily to 50,000 items, ~50 re-fires each re-walking more of the job than the
// last adds up to roughly quadratic total request cost (review on #352: ~1,250 requests watched to
// completion). Scaling the threshold by this fraction of what was last prefetched instead makes
// re-fires roughly logarithmic in the job's size, which keeps total cost roughly linear - at the
// reported scale (a few thousand items) GROUP_PAGE_SIZE itself still dominates, so this changes
// nothing there; it only tapers re-fire frequency once a job grows large enough for the difference
// to matter.
const JOB_GROUP_REFIRE_GROWTH_FRACTION = 0.1;

// The growth thresholds above bound how many re-walks fire, but not how close together. A job that
// grows a round's worth while the previous walk is still in flight re-fires the moment that walk
// lands, so a large, fast crawl kept this in a continuous back-to-back loop. Each round is a
// setItems over the whole job and a full re-render, so that pinned the main thread for the rest of
// the crawl with no input from the analyst (GT-16811). Measured from when the previous walk
// finished. Like the growth threshold, it only applies while the job is active.
export const JOB_GROUP_REFIRE_COOLDOWN_MS = 30_000;

// Matches JobsController.MaxBulkItemIds. A job's channel prefetch can grow `items` past it, and
// "Select all" -> Dismiss then hands this every id in it - so the request itself still needs
// chunking rather than assuming one call is always small enough.
const MAX_BULK_ITEM_IDS = 5000;

export type ChannelGroup = {
  readonly key: string;
  readonly label: string;
  readonly channelId: string | null;
  // Under the current showClassified/showDismissed triage AND (in job mode, once every one of the
  // channel's items is known - see computeChannelDisplayCount) the smart/match-tier filters, not
  // the channel's raw total - a channel this filters down to zero is filtered out of the group
  // list entirely (see serverChannelGroups) rather than kept around with count 0, the same way a
  // fully-filtered-away item never renders in the flat list either.
  readonly count: number;
  readonly items: readonly Item[];
};

// Channel identity for grouping: the channel id when the scrape carried one, else the handle or
// name, so items from a channel with no id don't all collapse into a single bucket.
export const channelKeyOf = (item: Item) =>
  item.author_id ?? item.author_handle ?? item.author_name ?? 'unknown';

// Groups only the items already loaded - paging is server-side, so a tab's count is "in what's
// been loaded so far", not the whole job. Busiest channel first; that's the one worth opening.
// One pass builds both the counts/label used for the header and the per-channel item bucket used
// to render an expanded group - these used to be two separate memos each walking the full item
// list, which mattered once that list could be thousands of items long. Exported for direct
// testing.
export function groupItemsByChannel(items: readonly Item[]): ChannelGroup[] {
  const groups = new Map<
    string,
    {label: string; channelId: string | null; count: number; items: Item[]}
  >();
  for (const item of items) {
    const key = channelKeyOf(item);
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.items.push(item);
    } else {
      groups.set(key, {
        label: item.author_name ?? item.author_handle ?? key,
        channelId: item.author_id,
        count: 1,
        items: [item]
      });
    }
  }

  return [...groups]
    .map(([key, group]) => ({key, ...group}))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// The classified/dismissed triage filter - whether an item should be visible given the two
// "Show classified"/"Show dismissed" toggles. Shared between the flat list (afterTriageItems) and
// an expanded channel group's own items: those are fetched straight from the server
// (ensureChannelItemsLoaded) rather than sourced from `items`, so without this they never passed
// through the toggle at all and a fully-dismissed channel stayed fully visible, greyed out, even
// with "Show dismissed" off.
export function passesTriage(
  item: Pick<Item, 'submission_status' | 'item_status'>,
  showClassified: boolean,
  showDismissed: boolean
): boolean {
  const isClassified =
    item.submission_status === 'in hopper' || item.submission_status === 'sent to client';
  const isDismissed = item.item_status === 'dismissed';
  return (!isClassified || showClassified) && (!isDismissed || showDismissed);
}

// The full filter chain an expanded channel group's body renders through, plus the one number the
// header can't get anywhere else: how many of the group's items the SMART filter alone took out.
// Those items are fetched straight from the server (ensureChannelItemsLoaded) and never pass
// through the visibleItems memo, so its own smartFilteredCount says nothing about them - and
// without this count an expanded group whose every item carries an excluded term rendered as a
// blank panel under a header still claiming 74 videos, with no hint that a filter did it.
// smartHidden counts only items that survived triage, mirroring how afterTriageItems keeps the
// flat list's "N hidden by smart filter" from double-counting rows triage already removed.
export function filterGroupItems(
  items: readonly Item[],
  showClassified: boolean,
  showDismissed: boolean,
  rejects: {
    isSmartFilterRejected: (item: Item) => boolean;
    isMatchTierRejected: (item: Item) => boolean;
  }
): {visible: Item[]; smartHidden: number} {
  const visible: Item[] = [];
  let smartHidden = 0;
  for (const item of items) {
    if (!passesTriage(item, showClassified, showDismissed)) {
      continue;
    }

    if (rejects.isSmartFilterRejected(item)) {
      smartHidden++;
      continue;
    }

    if (!rejects.isMatchTierRejected(item)) {
      visible.push(item);
    }
  }

  return {visible, smartHidden};
}

// A server channel aggregate row's visible count under the current showClassified/showDismissed
// toggles - the channel's total minus whatever's currently triaged away. dismissed_count and
// classified_count overlap (classifying an item and then dismissing it is a normal flow), so this
// is inclusion-exclusion, not a plain sum: subtracting both counts double-subtracts the overlap,
// which could otherwise drop a channel's count to 0 (dropping it out of the group list entirely -
// see serverChannelGroups' own `.filter(c => c.count > 0)`) while it still has untriaged active
// items. dismissed_and_classified_count only needs adding back when BOTH toggles are hiding their
// dimension - if either is showing, that dimension no longer contributes to hiddenCount at all,
// overlap included. Extracted (and tested) on its own since it's the subtlest arithmetic in this
// file and was previously inline and uncovered.
export function computeChannelVisibleCount(
  channel: Pick<
    ChannelAggregate,
    'total' | 'dismissed_count' | 'classified_count' | 'dismissed_and_classified_count'
  >,
  showClassified: boolean,
  showDismissed: boolean
): number {
  const hideDismissed = !showDismissed;
  const hideClassified = !showClassified;
  const hiddenCount =
    (hideDismissed ? channel.dismissed_count : 0) +
    (hideClassified ? channel.classified_count : 0) -
    (hideDismissed && hideClassified ? channel.dismissed_and_classified_count : 0);
  return Math.max(0, channel.total - hiddenCount);
}

// The count a channel group's header badge should show, and whether that channel is empty
// specifically because the smart filter emptied it (as opposed to triage or the match-tier
// filter) - the one further fact the empty-channel banner needs that filterGroupItems' own
// smartHidden doesn't carry on its own. `knownItems` is whatever's actually been fetched for this
// channel so far (see knownChannelItemsByKey) - possibly incomplete, since a job's channel items
// normally load only on expand. Falls back to the server's own triage-only count whenever it's
// incomplete: a page not yet fetched could still hold a survivor, so there's no way to safely
// claim the smart filter emptied a channel we haven't fully seen. Exported for direct testing.
export function computeChannelDisplayCount(
  channel: Pick<
    ChannelAggregate,
    'total' | 'dismissed_count' | 'classified_count' | 'dismissed_and_classified_count'
  >,
  knownItems: readonly Item[],
  triage: {showClassified: boolean; showDismissed: boolean},
  rejects: {
    isSmartFilterRejected: (item: Item) => boolean;
    isMatchTierRejected: (item: Item) => boolean;
  }
): {count: number; smartEmptied: boolean} {
  const serverCount = computeChannelVisibleCount(
    channel,
    triage.showClassified,
    triage.showDismissed
  );
  if (knownItems.length < channel.total) {
    return {count: serverCount, smartEmptied: false};
  }

  const {visible, smartHidden} = filterGroupItems(
    knownItems,
    triage.showClassified,
    triage.showDismissed,
    rejects
  );
  return {
    count: visible.length,
    // A channel triage alone already zeroed isn't the smart filter's doing - serverCount > 0
    // excludes that case, matching the same "N hidden by triage" carve-out filterGroupItems'
    // own doc comment already makes.
    smartEmptied: visible.length === 0 && smartHidden > 0 && serverCount > 0
  };
}

// Whether the job-mode channel prefetch effect below should (re-)fire this render, and how much
// work to ask for if so. Pulled out as its own pure function - review on #352 flagged that this
// file has no component test, so the one-shot-vs-re-fire behaviour was only verifiable by hand;
// this makes it directly testable the same way computeChannelDisplayCount already is.
//
// `jobItemCount` must be `selected.item_count`, not `itemsTotal`: the 3-second job-status poll (see
// withFreshStatus) is what actually keeps a still-running job's count fresh, and it only ever
// touches the job object, never `itemsTotal`. `itemsTotal` is written solely by item-list fetches,
// which in job-mode grouping means solely by this same prefetch - comparing against it self-defeats,
// since a non-truncated prefetch always ends with items.length === itemsTotal and nothing else in
// this mode ever moves itemsTotal again to signal the job grew further.
//
// A re-fire walks from page 1 again (`replaceOnFirstRound: false`, so loadRemainingPagesForGrouping
// merges instead of its usual round-1 replace) rather than resuming from wherever a previous run
// left off. That's not optional: ExecutePagedAsync orders `created_at DESC, id DESC`, so a newly
// *observed* row lands at offset 0, shifting every already-held row to a higher offset - resuming
// from the tail would ask for exactly the rows already held and never see it.
//
// It also always re-walks the job's *whole* current total, not just enough pages to cover the
// delta - `item_count` growing doesn't mean every new row is actually new content. Ingress links an
// already-known item to a job (ItemIngestStore) without touching that item's CreatedAt
// (ItemReadModelProjector.ApplyRefresh leaves it alone on re-observation), so a video first seen
// last week can re-surface in this job today, bumping item_count while sorting deep in the list at
// its original, much older created_at - nowhere near the head a delta-sized fetch would cover. A
// full re-walk has no such blind spot: dedupe-on-append (see loadRemainingPagesForGrouping) makes
// the overlap with everything already held cheap to discard, and the one-full-round throttle below
// keeps a full re-walk from firing on every single poll tick, so the extra page fetches only happen
// when there's genuinely at least a round's worth of change to account for.
//
// Once a job has had its first prefetch (`lastPrefetchedItemCount > 0`) *and* is still active
// (status pending/running), a re-fire needs at least a full GROUP_PAGE_SIZE of new growth -
// otherwise a job trickling in one item every few seconds would fire a fresh round on every single
// poll tick. That threshold also scales up by JOB_GROUP_REFIRE_GROWTH_FRACTION of what was last
// prefetched (see its own doc comment on the cost shape this avoids for a large, steadily-growing
// job) - GROUP_PAGE_SIZE alone still wins at the sizes this was reported against, so nothing changes
// there. The throttle is dropped entirely once the job is no longer active: it stops polling at
// that point (see the 3-second poll's own pendingIds filter), so a completed job sitting just under
// the threshold would otherwise never get its last few hundred items caught up at all. The very
// first prefetch for a job always fires regardless of size, same as before.
export function shouldPrefetchJobChannels(params: {
  groupByChannel: boolean;
  hasClientSideFilter: boolean;
  loadingAllForGrouping: boolean;
  jobItemCount: number;
  jobIsActive: boolean;
  lastPrefetchedItemCount: number;
  msSinceLastPrefetch: number;
}): {shouldFetch: false} | {shouldFetch: true; rounds: number; replaceOnFirstRound: boolean} {
  const delta = params.jobItemCount - params.lastPrefetchedItemCount;
  const isFirstPrefetch = params.lastPrefetchedItemCount === 0;
  const minGrowthToRefire = Math.max(
    GROUP_PAGE_SIZE,
    Math.ceil(params.lastPrefetchedItemCount * JOB_GROUP_REFIRE_GROWTH_FRACTION)
  );
  if (
    !params.groupByChannel ||
    !params.hasClientSideFilter ||
    params.loadingAllForGrouping ||
    delta <= 0 ||
    (!isFirstPrefetch &&
      params.jobIsActive &&
      (delta < minGrowthToRefire || params.msSinceLastPrefetch < JOB_GROUP_REFIRE_COOLDOWN_MS))
  ) {
    return {shouldFetch: false};
  }

  // Always the job's whole current total (see above for why a delta-sized fetch isn't safe),
  // capped against JOB_GROUP_PREFETCH_MAX_ROUNDS - a hard stop against a pathological job turning
  // this into an unbounded fetch loop.
  const roundsNeeded = Math.ceil(params.jobItemCount / GROUP_PAGE_SIZE);
  return {
    shouldFetch: true,
    rounds: Math.min(Math.max(1, roundsNeeded), JOB_GROUP_PREFETCH_MAX_ROUNDS),
    replaceOnFirstRound: isFirstPrefetch
  };
}

// Until a selection's server channel aggregates load there is nothing to fall back on for this:
// channelGroups is built straight from visibleItems (already triage- and smart-filtered), so a channel the smart
// filter empties is already gone by the time groupItemsByChannel runs - this is the only way to
// learn how many were. One more pass over afterTriageItems (the pre-smart-filter base set),
// counting a channel that has at least one triage-surviving item and ends up with none once the
// smart filter runs, and where the smart filter (not just the match-tier filter) is actually to
// blame for at least one of those rejections.
export function countSmartHiddenChannels(
  afterTriageItems: readonly Item[],
  rejects: {
    isSmartFilterRejected: (item: Item) => boolean;
    isMatchTierRejected: (item: Item) => boolean;
  }
): number {
  const channels = new Map<string, {anyVisible: boolean; anySmartRejected: boolean}>();
  for (const item of afterTriageItems) {
    const key = channelKeyOf(item);
    let entry = channels.get(key);
    if (!entry) {
      entry = {anyVisible: false, anySmartRejected: false};
      channels.set(key, entry);
    }

    const smartRejected = rejects.isSmartFilterRejected(item);
    if (smartRejected) {
      entry.anySmartRejected = true;
    } else if (!rejects.isMatchTierRejected(item)) {
      entry.anyVisible = true;
    }
  }

  let hidden = 0;
  for (const entry of channels.values()) {
    if (!entry.anyVisible && entry.anySmartRejected) {
      hidden++;
    }
  }

  return hidden;
}

// Synthesizes the channel's own actor item on the fly, for a channel this job only ever saw as
// an author on its videos (never scraped as its own record). Same shape SingleVideoSubmissionPage
// builds for a channel resolved by handle/id - a content_type "actor" item with a real author_id
// takes the same Actor submission path server-side as a scraped one, no backend changes needed.
// `status`, when available, hydrates title/thumbnail/handle/country from a live channel lookup;
// without it (lookup failed, or skipped) the group's own label is the only thing to go on.
function buildActorItem(channelId: string, fallbackLabel: string, status?: ChannelStatus): Item {
  return {
    id: crypto.randomUUID(),
    external_id: channelId,
    platform: 'youtube',
    content_type: 'actor',
    title: status?.title ?? fallbackLabel,
    url: status?.custom_url
      ? `https://www.youtube.com/${status.custom_url}`
      : `https://www.youtube.com/channel/${channelId}`,
    thumbnail_url: status?.thumbnail_url ?? null,
    author_handle: status?.custom_url ?? null,
    author_id: channelId,
    author_name: status?.title ?? fallbackLabel,
    published_at: status?.published_at ?? null,
    metadata: status ? {Fields: buildChannelItemMetadataFields(status)} : {},
    affiliate: DEFAULT_AFFILIATE,
    created_at: new Date().toISOString(),
    submission_status: null,
    submission_policy_violations: [],
    submission_status_groups: [],
    item_status: null,
    is_new_item: null
  };
}

/**
 Calls onVisible when it scrolls into view. Zero height, with a negative top margin cancelling the
 gap a `space-y-0.5` list puts before its last child, so a paged list lays out exactly as it would
 without it.
 */
function ScopeSentinel({onVisible}: {readonly onVisible: () => void}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onVisibleRef = useRef(onVisible);
  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        onVisibleRef.current();
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  return <div ref={ref} aria-hidden="true" className="-mt-0.5 h-0" />;
}

// Direct investigations go to Unfiled and subfolders move up a level (see FoldersController);
// counted from the unscoped job_count so the Mine toggle never understates what moves.
function folderDeleteDescription(folder: Folder, hasSubfolders: boolean): string {
  const n = folder.job_count;
  const jobs = `${n} investigation${n === 1 ? '' : 's'} in it will move to Unfiled`;
  return `${jobs}${hasSubfolders ? ' and its subfolders will move up a level' : ''}. This can’t be undone.`;
}

// "AM" for Alex Morgan: the Mine toggle is the analyst's own avatar, per the rail design.
function initialsOf(user: {displayName: string; username: string}): string {
  const words = user.displayName.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 0 ? words.slice(0, 2).map((w) => w[0]) : [user.username[0]];
  return letters.join('').toUpperCase();
}

// Why the header's Schedule is disabled for this investigation, or undefined when it can be
// scheduled. Mirrors what PUT /jobs/{id}/schedule refuses.
function unschedulableReason(job: Job): string | undefined {
  switch (job.strategy) {
    case 'image_search': {
      return 'An image search needs its image uploaded each time, so it can’t be scheduled';
    }

    case 'google_search': {
      return 'The scheduler can’t run a web search, so it can’t be scheduled';
    }

    default: {
      return undefined;
    }
  }
}

// The header's first fact about a run, per the design's "Last ran 2h ago": what state it is in and
// how long ago it got there.
function jobRunLabel(job: Job): string {
  const ago = `${formatDistanceToNow(new Date(job.completed_at ?? job.created_at))} ago`;
  switch (job.status) {
    case 'pending': {
      return 'Queued';
    }

    case 'running': {
      return 'Running now';
    }

    case 'failed': {
      return `Run failed ${ago}`;
    }

    case 'timed_out': {
      return `Timed out ${ago}`;
    }

    case 'completed': {
      return `Last ran ${ago}`;
    }
  }
}

// One of the header's "N classified hidden · Show" facts: what the triage toggles hide, with the
// toggle beside the count rather than in a banner of its own.
function HiddenCount({
  count,
  what,
  shown,
  onToggle
}: {
  readonly count: number;
  readonly what: string;
  readonly shown: boolean;
  readonly onToggle: () => void;
}) {
  const noun = count === 1 ? ITEM_NOUN : ITEM_NOUN_PLURAL;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {shown ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
      {shown ? `Showing ${count} ${what} ${noun}` : `${count} ${what} ${noun} hidden`}
      <Button variant="link" size="xs" className="h-auto p-0" onClick={onToggle}>
        {shown ? 'Hide' : 'Show'}
      </Button>
    </span>
  );
}

// Only these strategies have a free-text search_term with keyword operators to enforce - the rest
// (youtube_video, youtube_playlist, youtube_channel, image_search, and every pinterest_* strategy,
// which carries a handle/path/id rather than a query) have nothing for smart filtering to parse,
// so it stays hidden for them.
const FREE_TEXT_SEARCH_STRATEGIES = new Set([
  'youtube_search',
  'youtube_channel_search',
  'google_search',
  'pinterest_search',
  'pinterest_pin_search'
]);

// Filter chips are platform-scoped (unlike JOB_TYPE_CONFIG above, which stays complete so a
// legacy row from the other platform still renders a sensible label) - a Falcon deployment has no
// use for a "Channel URL" chip, and vice versa. image_search and google_search belong to neither set:
// both platforms search by image, and by Google.
const PINTEREST_ONLY_STRATEGIES = new Set([
  'pinterest_profile',
  'pinterest_profile_followers',
  'pinterest_profile_followers_of_followers',
  'pinterest_board',
  'pinterest_search',
  'pinterest_profile_pins',
  'pinterest_profile_boards',
  'pinterest_pin_search'
]);
const YOUTUBE_ONLY_STRATEGIES = new Set([
  'youtube_search',
  'youtube_playlist',
  'youtube_channel',
  'youtube_channel_search'
]);
const JOB_TYPE_FILTERS = Object.entries(JOB_TYPE_CONFIG)
  .filter(([strategy]) =>
    PLATFORM_ID === 'pinterest'
      ? !YOUTUBE_ONLY_STRATEGIES.has(strategy)
      : !PINTEREST_ONLY_STRATEGIES.has(strategy)
  )
  .map(([strategy, config]) => ({
    strategy,
    ...config
  }));

function getDismissedJobIds(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem('iwi_dismissed_jobs') ?? '[]'));
  } catch {
    return new Set();
  }
}

// Applies a job status refetch onto local state without clobbering fields the analyst can edit
// locally (e.g. folder_id via drag-and-drop) - a background poll or status refresh has no business
// overwriting an optimistic edit made while its request was in flight.
//
// Returns `local` unchanged (same reference) when the poll brought back nothing new, rather than
// always allocating a fresh object - the 3-second poll below runs this over every pending/running
// job on every tick, and a changed job reference is what makes `jobs`/`selected` a new array/object
// identity and re-renders every mounted item card, even while nothing analyst-visible changed.
export function withFreshStatus(local: Job, fresh: Job): Job {
  if (
    local.status === fresh.status &&
    local.item_count === fresh.item_count &&
    local.completed_at === fresh.completed_at &&
    local.search_pages_done === fresh.search_pages_done &&
    local.search_videos_found === fresh.search_videos_found
  ) {
    return local;
  }

  return {
    ...local,
    status: fresh.status,
    item_count: fresh.item_count,
    completed_at: fresh.completed_at,
    search_pages_done: fresh.search_pages_done,
    search_pages_total: fresh.search_pages_total,
    search_videos_found: fresh.search_videos_found
  };
}

/**
 Which state a google_search job's empty results area should explain, or null when the ordinary item
 list (or its own empty state) applies. A search takes minutes, and its outcomes are otherwise
 indistinguishable from "No media items available": still searching, handing what it found to the
 spider, failed outright, or Google genuinely found none.
 */
export function googleSearchState(
  job: Job
): 'searching' | 'collecting' | 'failed' | 'none-found' | null {
  if (job.strategy !== 'google_search') {
    return null;
  }

  switch (job.status) {
    case 'pending': {
      return 'searching';
    }

    case 'running': {
      return 'collecting';
    }

    case 'failed': {
      return job.item_count === 0 ? 'failed' : null;
    }

    // The runner records the final count before it completes a job, so an explicit 0 means Google
    // returned nothing - not a job whose items are merely filtered out.
    case 'completed': {
      return job.search_videos_found === 0 ? 'none-found' : null;
    }

    // A timed-out job has its own (generic) banner; nothing Google-specific to add.
    case 'timed_out': {
      return null;
    }
  }
}

/**
 What a google_search job shows while it has no items, so a search that takes minutes - or fails -
 doesn't read as a crashed "No media items" job: first the Google search itself
 (GoogleSearchRunnerService, page by page), then the platform's spider collecting what it found.
 */
export function GoogleSearchProgress({job}: {readonly job: Job}) {
  const state = googleSearchState(job);
  const found = job.search_videos_found ?? 0;
  const done = job.search_pages_done ?? 0;
  const total = job.search_pages_total ?? 0;
  const searching = state === 'searching';
  // What Google finds here is the platform's content - videos, or pins - rather than the generic item.
  const content = CONTENT_LABEL.toLowerCase();
  const noun = (count: number) => (count === 1 ? content : `${content}s`);

  const title = {
    searching: 'Searching the web…',
    collecting: `Collecting ${found.toLocaleString()} ${noun(found)} from ${PLATFORM_NAME}…`,
    failed: 'Web search failed',
    'none-found': `No ${PLATFORM_NAME} ${content}s found`
  }[state ?? 'failed'];
  const description = {
    searching: `${found.toLocaleString()} ${PLATFORM_NAME} ${noun(found)} found so far. This usually takes a few minutes.`,
    collecting: `Results appear here as each ${content} is collected.`,
    failed:
      "The search didn't complete - usually a temporary problem with the search provider. Re-run to try again.",
    'none-found':
      'The search returned no results for this query. Try different keywords, more engines or a wider time range.'
  }[state ?? 'failed'];

  // The same Empty layout as the "No media items" state this replaces, with the design system's own
  // Progress (as ImportScrapyCollectionsDialog uses it) rather than a hand-rolled bar.
  return (
    <Empty className="h-96" role="status">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          {state === 'failed' ? <AlertCircle /> : state === 'none-found' ? <Film /> : <Spinner />}
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {searching && total > 0 ? (
        <EmptyContent>
          <Progress value={done} max={total} aria-label="Web search progress">
            <ProgressLabel>Pages searched</ProgressLabel>
            <ProgressValue>{() => `${done} of ${total}`}</ProgressValue>
          </Progress>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}

type Props = {
  readonly jobs: Job[];
  readonly jobsHasMore: boolean;
  readonly folders: Folder[];
  readonly selectedId?: string;
  readonly classifications: Classification[];
  readonly languages: ConfigItem[];
  readonly topics: ConfigItem[];
  readonly locations: ConfigItem[];
};

export function InvestigationsWorkspace({
  jobs: initialJobs,
  jobsHasMore: initialJobsHasMore,
  folders: initialFolders,
  selectedId,
  classifications,
  languages,
  topics,
  locations
}: Props) {
  const createFolderMut = useCreateFolder();
  const updateFolderMut = useUpdateFolder();
  const deleteFolderMut = useDeleteFolder();
  const deleteJobMut = useDeleteJob();
  const setJobFolderMut = useSetJobFolder();
  const rerunJobMut = useRerunJob();
  const jobFacets = useJobFacets();
  const [rerunning, setRerunning] = useState(false);

  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const jobsSentinelRef = useRef<HTMLDivElement | null>(null);
  const [folders, setFolders] = useState<Folder[]>(initialFolders);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const selectedJobIdRef = useRef<string | null>(null);
  const selectedFolderIdRef = useRef<string | null>(null);
  const [selected, setSelected] = useState<Job | null>(
    selectedId ? (initialJobs.find((j) => j.id === selectedId) ?? null) : null
  );

  // So an analyst can see what image an investigation was actually searched with, rather than
  // having to remember or re-upload it.
  // Falcon-only: the follower network exists only for the two follower strategies.
  const showNetworkView =
    PLATFORM_ID === 'pinterest' &&
    (selected?.strategy === 'pinterest_profile_followers' ||
      selected?.strategy === 'pinterest_profile_followers_of_followers');
  const selectedJobImage = useJobImage(selected?.id, selected?.strategy === 'image_search');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedJobImage.data) {
      setSelectedImageUrl(null);
      return;
    }

    const url = URL.createObjectURL(selectedJobImage.data);
    setSelectedImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedJobImage.data]);
  // The evidence behind each result. Vision's response mixes exact thumbnail matches with crops and
  // with pages that merely displayed someone else's matching thumbnail, so without this an analyst has
  // no way to tell a weak result from a strong one.
  const selectedJobImageMatches = useJobImageMatches(
    selected?.id,
    selected?.strategy === 'image_search'
  );
  const imageMatchTiersByVideoId = useMemo(() => {
    const tiers = new Map<string, ImageMatchTier>();
    for (const match of selectedJobImageMatches.data?.matches ?? []) {
      tiers.set(match.video_id, match.tier);
    }

    return tiers;
  }, [selectedJobImageMatches.data]);

  // Deliberately counts every recorded tier while listing only the ones this job still uses, so a
  // tightened ImageSearch:MinMatchTier reads as "Vision found these, we no longer trust them" rather
  // than results quietly disappearing.
  const imageSearchFunnel = useMemo(() => {
    const scan = selectedJobImageMatches.data;
    if (!scan) {
      return null;
    }

    const breakdown = TIER_ORDER.map((tier) => ({tier, count: scan.tier_counts[tier] ?? 0}))
      .filter(({count}) => count > 0)
      .map(({tier, count}) => `${count} ${imageMatchTierLabels[tier].toLowerCase()}`);
    const matchedCount = `${scan.matches.length.toLocaleString()} ${ITEM_NOUN_PLURAL}`;

    return {
      pagesMatched: scan.pages_matched,
      summary: breakdown.length > 0 ? `${matchedCount} · ${breakdown.join(' · ')}` : matchedCount
    };
  }, [selectedJobImageMatches.data]);

  // Tiers the analyst has switched off. Excluded-rather-than-included so the default (empty) is
  // "no filtering", matching every other filter in this view.
  const [excludedMatchTiers, setExcludedMatchTiers] = useState<ReadonlySet<ImageMatchTier>>(
    new Set()
  );

  const [imageLightboxOpen, setImageLightboxOpen] = useState(false);
  // Whether the analyst had unblurred or colourised the header image when they opened it, so the
  // lightbox doesn't apply either again.
  const [imageLightboxRevealed, setImageLightboxRevealed] = useState(false);
  const [imageLightboxColourised, setImageLightboxColourised] = useState(false);

  const [items, setItems] = useState<Item[]>([]);
  // State, not a ref: the "Load more" fallback button below needs to re-render once the budget
  // is spent, since showClassified/showDismissed toggling (client-side, no refetch) never resets it.
  const [autoLoadRounds, setAutoLoadRounds] = useState(0);
  // Set once an observer-triggered fetch comes back without making the list any taller, which means
  // scrolling can't make progress and the manual "Load more" fallback has to take over. Kept
  // separate from autoLoadRounds so a spent short-page budget can't disable the sentinel on a job
  // that scrolling is perfectly able to page through.
  const [autoLoadStalled, setAutoLoadStalled] = useState(false);
  // How many items were visible as of the last observer-triggered fetch, so the observer can tell
  // whether that fetch produced anything the analyst can see. -1 (not 0) so a first fetch against
  // a legitimately empty list still reads as unmeasured rather than as "no progress".
  const lastAutoLoadVisibleCountRef = useRef(-1);
  const itemsSentinelRef = useRef<HTMLDivElement | null>(null);
  const [loadingItems, setLoadingItemsState] = useState(false);
  // Mirrored into a ref for the same reason loadingAllForGroupingRef/loadingMoreRef exist: the
  // job-mode channel prefetch effect reads this synchronously to avoid racing selectJob/
  // selectFolder/the filter-change effect's own page-1 fetch (review on #352, discussion
  // r4014259297) - a render-snapshot `loadingItems` can still read stale/false when that effect and
  // this one are both passive effects flushed in the same commit, since a setState from one doesn't
  // become visible to the other until React re-renders. Wrapping the setter (rather than editing
  // each of the three setLoadingItems call sites) makes it impossible to add a fourth that forgets
  // to keep the ref in sync.
  const loadingItemsRef = useRef(false);
  const setLoadingItems = useCallback((value: boolean) => {
    loadingItemsRef.current = value;
    setLoadingItemsState(value);
  }, []);
  const [loadingMore, setLoadingMore] = useState(false);
  // Two independent callers now reach loadMore (the filtered-short-page effect and the scroll
  // observer), and `loadingMore` is only a render snapshot: the observer's queued initial
  // notification can land before React has re-rendered with `loadingMore: true`, so both would
  // fetch the same page and append it twice. This ref flips synchronously inside loadMore itself,
  // which is the only guard that actually closes that window.
  const loadingMoreRef = useRef(false);
  const [itemPage, setItemPage] = useState(1);
  const [itemHasMore, setItemHasMore] = useState(false);
  // Null until the selection's count arrives (see the count effect below); shown as "…" meanwhile,
  // and stays null if the count fails.
  const [itemsTotal, setItemsTotal] = useState<number | null>(null);
  // A folder's total stops counting at a cap; when set, itemsTotal is a floor and is shown as "N+".
  const [itemsTotalCapped, setItemsTotalCapped] = useState(false);
  // React state setters don't take effect until the next render, but loadRemainingPagesForGrouping
  // can start right after applyItemsPage sets this for a brand-new job/filter - reading the `itemsTotal` closure
  // there would see the *previous* selection's total, not the one just fetched. Updated everywhere
  // setItemsTotal is, so the loop can read this instead and always get the current value.
  const itemsTotalRef = useRef(0);
  const [itemQuery, setItemQuery] = useState('');
  const debouncedQuery = useDebounce(itemQuery, 400);
  const view = useUiStore((s) => s.itemsView);
  // The follower network is the third view beside grid and list, for follower investigations only.
  // It replaces the items while shown; the persisted grid/list choice is what picking it again
  // returns to.
  const [networkOpen, setNetworkOpen] = useState(false);
  const networkShown = networkOpen && showNetworkView;
  // Client-side: the job items endpoint has no flag filter, so this narrows what is already loaded.
  const [flagFilter, setFlagFilter] = useState<FlagFilter[]>([]);
  // Starts empty so every channel group renders collapsed - a job with many channels used to
  // mount every loaded item across every group the instant grouping was toggled on, which is
  // what made "Group by channel" (and everything downstream of it, like Dismiss channel) laggy
  // on a job with a lot of items. Headers still show accurate per-channel counts; an analyst
  // opens only the channels they actually want to look at.
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
  const [renderedChannelGroupCount, setRenderedChannelGroupCount] =
    useState(CHANNEL_GROUPS_PAGE_SIZE);
  // A job's channel counts come from the server (useJobChannels below); a channel's actual items
  // are fetched only once expanded, by author_id, and cached here so collapsing and re-expanding
  // doesn't re-fetch.
  const [channelItemsByKey, setChannelItemsByKey] = useState<Map<string, Item[]>>(new Map());
  // Keys where the CHANNEL_ITEMS_PAGE_SIZE fetch below reported more beyond what it returned - so
  // the group's own body can say "showing first N" instead of silently rendering fewer items than
  // its own header count promises.
  const [channelItemsTruncatedKeys, setChannelItemsTruncatedKeys] = useState<Set<string>>(
    new Set()
  );
  // Keys where ensureChannelItemsLoaded's fetch failed - without this a network error left the
  // group silently rendering as empty (indistinguishable from a channel that genuinely has no
  // visible items), with no way to retry short of switching jobs and back.
  const [channelItemsErrorKeys, setChannelItemsErrorKeys] = useState<Set<string>>(new Set());
  const [channelItemsLoadingKeys, setChannelItemsLoadingKeys] = useState<Set<string>>(new Set());
  const channelItemsLoadingKeysRef = useRef<Set<string>>(new Set());
  // Bumped by the selection and channelFilters reset effects below. ensureChannelItemsLoaded
  // captures this at the start of a fetch and compares again once it resolves - a bump in between
  // means the filters (or job) changed mid-flight, so an older, since-superseded fetch resolving
  // late can't overwrite a newer one's result. Without this, a slow request from before a filter
  // change landing after a fast one from after it would put the stale items back - the exact race
  // the reset effects otherwise leave open.
  const channelItemsGenerationRef = useRef(0);
  const railWidth = useUiStore((s) => s.investigationsRailWidth);
  const setRailWidth = useUiStore((s) => s.setInvestigationsRailWidth);
  // Tracks the live width during a drag without re-rendering this page on every pointer move; it is
  // committed to the store once the drag ends.
  const railWidthRef = useRef<number | null>(null);
  const railPanelRef = useRef<{resize: (size: number) => void} | null>(null);
  // The item whose Show more is open in the details sidebar, and the items it was opened among.
  const [detailsItem, setDetailsItem] = useState<Item | null>(null);
  const [detailsItems, setDetailsItems] = useState<readonly Item[]>([]);
  const [headerElement, setHeaderElement] = useState<HTMLElement | null>(null);
  const headerCompact = useBelowWidth(headerElement, INVESTIGATION_HEADER_COMPACT_BELOW);
  const thumbnailSize = useUiStore((s) => s.thumbnailSize);
  const setThumbnailSize = useUiStore((s) => s.setThumbnailSize);
  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  const p0Dialog = useP0AlertDialog();
  const [shareTarget, setShareTarget] = useState<{
    kind: 'job' | 'folder';
    id: string;
    label: string;
  } | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [jobQuery, setJobQuery] = useState('');
  // Empty set = no filter applied, every type shows.
  const [jobTypeFilter, setJobTypeFilter] = useState<Set<string>>(new Set());
  // Empty set = no filter applied, every policy-violation category shows.
  const [policyViolationFilter, setPolicyViolationFilter] = useState<Set<string>>(new Set());
  // Empty set = no filter applied, every language shows (including investigations with none).
  const [languageFilter, setLanguageFilter] = useState<Set<string>>(new Set());
  // Empty set = no filter applied. Emails, lower-cased, as the facets serve them; narrows both the
  // investigations and the folder tree server-side.
  const [creatorFilter, setCreatorFilter] = useState<Set<string>>(new Set());
  const {user} = useAuth();
  const isBroadView = user?.role === 'admin' || user?.role === 'analyst_admin';
  // Only meaningful for broad-view roles - everyone else is already server-side scoped to their
  // own + shared-with-them jobs (see JobsController.ListJobs), so the toggle would be a no-op.
  const [mineOnly, setMineOnly] = useState(false);
  const [scheduledOnly, setScheduledOnly] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [newFolderName, setNewFolderName] = useState('');
  // Which folder the inline "new folder" input will create inside; null creates at the top level.
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const newFolderRef = useRef<HTMLInputElement>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [renameFolderError, setRenameFolderError] = useState<string | null>(null);
  const renameFolderRef = useRef<HTMLInputElement>(null);

  const [pendingDelete, setPendingDelete] = useState<
    | {kind: 'investigation'; job: Job}
    | {kind: 'folder'; folder: Folder; hasSubfolders: boolean}
    | null
  >(null);
  const [confirmingTranslateSelected, setConfirmingTranslateSelected] = useState(false);
  const [translatingSelected, setTranslatingSelected] = useState(false);
  const translateMany = useTranslationStore((s) => s.translateMany);
  const [checkedJobs, setCheckedJobs] = useState<Set<string>>(new Set());
  const lastClickedIdxRef = useRef<number>(-1);

  const [draggingJobId, setDraggingJobId] = useState<string | null>(null);
  // Set instead of draggingJobId when a folder row is the thing being dragged - handleDrop tells
  // the two payloads apart by which one is set.
  const [draggingFolderId, setDraggingFolderId] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);

  // Unfiled is drawn as a folder, so it collapses like one; open by default so nothing hides.
  const [unfiledExpanded, setUnfiledExpanded] = useState(true);
  // Which folder the New Investigation dialog files into: a folder id, null for Unfiled, and
  // undefined while the dialog is closed. One dialog serves every menu that can open it.
  const [newInvestigationIn, setNewInvestigationIn] = useState<string | null | undefined>();
  // The investigation the header's Duplicate action starts its dialog from.
  const [duplicateOf, setDuplicateOf] = useState<Job | null>(null);
  // The investigation the Schedule dialog is open for.
  const [scheduling, setScheduling] = useState<Job | null>(null);

  // A folder as a selection, aggregating every investigation filed under it - see selectFolder.
  // Mutually exclusive with `selected`.
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const dragGhostRef = useRef<HTMLDivElement | null>(null);

  // ── Sidebar job loading ──
  // Each sidebar section pages its own jobs from the server, and only once it is on screen: the
  // unfiled list from the start, a folder when it is expanded, and a folder's whole subtree when the folder is selected. Every page lands in the
  // one `jobs` list, so the grouping, filtering, polling and mutation code below is unchanged by
  // where a job came from. The state drives rendering; the ref is what the async loader reads, so a
  // request never acts on a stale closure.
  const [scopePages, setScopePagesState] = useState<ReadonlyMap<string, ScopePageState>>(
    () =>
      new Map([[scopeKey(UNFILED_SCOPE), {page: 1, hasMore: initialJobsHasMore, loading: false}]])
  );
  const scopePagesRef = useRef(scopePages);
  const setScopePage = useCallback((key: string, next: ScopePageState | null) => {
    const map = new Map(scopePagesRef.current);
    if (next) {
      map.set(key, next);
    } else {
      map.delete(key);
    }

    scopePagesRef.current = map;
    setScopePagesState(map);
  }, []);

  // The folder tree is filtered by the server too, so a creator change re-reads it. The first run
  // keeps the folders the page loaded with. The cleanup drops a response that lands after a newer
  // creator change started, so a slow earlier request can't overwrite the tree with a stale one.
  const foldersFilteredRef = useRef(false);
  useEffect(() => {
    if (!foldersFilteredRef.current && creatorFilter.size === 0) {
      return;
    }

    foldersFilteredRef.current = true;
    let stale = false;
    foldersApi
      .list([...creatorFilter])
      .then((next) => {
        if (!stale) {
          setFolders(next);
        }
      })
      .catch((error: unknown) => {
        if (!stale) {
          setFolderError(getApiErrorMessage(error, 'Could not refresh folders.'));
        }
      });
    return () => {
      stale = true;
    };
  }, [creatorFilter]);

  const debouncedJobQuery = useDebounce(jobQuery, 300);
  const serverFilters = useMemo<JobServerFilters>(
    () => ({
      q: debouncedJobQuery,
      strategies: jobTypeFilter,
      policyViolations: policyViolationFilter,
      languages: languageFilter,
      mine: mineOnly && isBroadView,
      scheduled: scheduledOnly,
      creators: creatorFilter
    }),
    [
      debouncedJobQuery,
      jobTypeFilter,
      policyViolationFilter,
      languageFilter,
      mineOnly,
      isBroadView,
      scheduledOnly,
      creatorFilter
    ]
  );
  const serverFiltersKey = filtersKey(serverFilters);
  const serverFiltersRef = useRef(serverFilters);
  const serverFiltersKeyRef = useRef(serverFiltersKey);

  /**
   Fetches a scope's jobs: `first` loads page 1 unless the scope is already loaded or loading,
   `more` the next page, and `all` every page (a folder's subtree, which "Re-run all" acts on
   whole). A response that lands after the filters changed is dropped - the filter change already
   started over with the new filters.
   */
  /** The paging loop behind loadScope. Resolves with every job it fetched, failed or not. */
  const fetchScopePages = useCallback(
    async (
      scope: JobScope,
      mode: 'first' | 'more' | 'all',
      key: string,
      current: ScopePageState | undefined
    ): Promise<Job[]> => {
      const fetched: Job[] = [];

      const requestFiltersKey = serverFiltersKeyRef.current;
      const filters = serverFiltersRef.current;
      const pageSize = pageSizeFor(scope, JOBS_PAGE_SIZE);
      let page = (current?.page ?? 0) + 1;
      // Whether there is more is unknown until a page lands, so a scope's first load never
      // reports any - otherwise the unfiled list's scroll sentinel would mount and could ask for
      // page 2 while page 1 is still in flight.
      setScopePage(key, {
        page: current?.page ?? 0,
        hasMore: current?.hasMore ?? false,
        loading: true
      });
      try {
        // Sequential by design: each page needs the previous one's has_more, and a subtree read is
        // a background load that should not flood the API.
        let keepGoing = true;
        while (keepGoing) {
          const data = await jobsApi.list(jobListQuery(scope, filters, page, pageSize));
          if (serverFiltersKeyRef.current !== requestFiltersKey && scopeIsFiltered(scope)) {
            return fetched;
          }

          // Jobs the analyst dismissed stay out of both the rail and what callers get back, so
          // Re-run all over a still-loading folder can't pick them up again.
          const dismissed = getDismissedJobIds();
          fetched.push(...data.results.filter((j) => !dismissed.has(j.id)));
          setJobs((previous) => mergeJobs(previous, data.results, dismissed));
          keepGoing = mode === 'all' && data.has_more;
          setScopePage(key, {page, hasMore: data.has_more, loading: keepGoing});
          page += 1;
        }
      } catch {
        if (serverFiltersKeyRef.current === requestFiltersKey || !scopeIsFiltered(scope)) {
          setScopePage(key, {page: page - 1, hasMore: false, loading: false, failed: true});
        }
      }

      return fetched;
    },
    [setScopePage]
  );

  const scopeRequestsRef = useRef(new Map<string, Promise<Job[]>>());
  const loadScope = useCallback(
    async (scope: JobScope, mode: 'first' | 'more' | 'all' = 'first'): Promise<Job[]> => {
      const key = scopeKey(scope);
      const current = scopePagesRef.current.get(key);
      // A request already in flight is shared, so a caller that needs its result (Re-run all
      // waiting on a folder's subtree) can await it. A failed scope is always retryable, and
      // resumes after the last page that did load.
      const inFlight = scopeRequestsRef.current.get(key);
      if (current?.loading && inFlight) {
        return inFlight;
      }

      if (
        current?.loading ||
        (mode !== 'more' && current && !current.failed) ||
        (mode === 'more' && !current?.hasMore && !current?.failed)
      ) {
        return [];
      }

      const request = fetchScopePages(scope, mode, key, current);
      scopeRequestsRef.current.set(key, request);
      try {
        return await request;
      } finally {
        if (scopeRequestsRef.current.get(key) === request) {
          scopeRequestsRef.current.delete(key);
        }
      }
    },
    [fetchScopePages]
  );

  // A filter change starts every filtered scope over, keeping the jobs already loaded on screen
  // until the new pages land - the client-side filter hides the ones that no longer match, so
  // nothing flashes empty. The effects below then reload whichever scopes are on screen.
  useEffect(() => {
    if (serverFiltersKeyRef.current === serverFiltersKey) {
      return;
    }

    serverFiltersKeyRef.current = serverFiltersKey;
    serverFiltersRef.current = serverFilters;
    for (const key of scopePagesRef.current.keys()) {
      const scope = parseScopeKey(key);
      if (!scope || scopeIsFiltered(scope)) {
        setScopePage(key, null);
      }
    }
  }, [serverFiltersKey, serverFilters, setScopePage]);

  useEffect(() => {
    void loadScope(UNFILED_SCOPE);
    for (const id of expandedFolders) {
      void loadScope({kind: 'folder', id});
    }
  }, [expandedFolders, serverFiltersKey, loadScope]);

  const subtreeFolderId = selectedFolder?.id;
  useEffect(() => {
    if (subtreeFolderId) {
      void loadScope({kind: 'subtree', id: subtreeFolderId}, 'all');
    }
  }, [subtreeFolderId, loadScope]);

  const unfiledPages = scopePages.get(scopeKey(UNFILED_SCOPE));

  // The foot of a paged sidebar section: an invisible sentinel that asks for the next page once it
  // scrolls into view, the same way the unfiled list pages. Nothing is drawn, so a folder
  // reads exactly as it did when every job was loaded up front.
  const renderScopeMore = (scope: JobScope, pages: ScopePageState | undefined) =>
    pages?.hasMore && !pages.loading ? (
      <ScopeSentinel
        onVisible={() => {
          void loadScope(scope, 'more');
        }}
      />
    ) : null;

  // Per-investigation, persisted in uiStore (localStorage) keyed by job/folder id - reopening
  // this investigation later restores the filters it was left with. Destructured to primitives
  // (not read as `itemFilters.foo`) so buildFilterParams' deps below, and the two debounces here,
  // stay on stable primitive values rather than churning every time any sibling filter changes.
  const itemFiltersKey = selected
    ? `job:${selected.id}`
    : selectedFolder
      ? `folder:${selectedFolder.id}`
      : null;
  const itemFilters = useUiStore((s) =>
    itemFiltersKey
      ? (s.itemFiltersByKey[itemFiltersKey] ?? DEFAULT_ITEM_FILTERS)
      : DEFAULT_ITEM_FILTERS
  );
  const setItemFiltersByKey = useUiStore((s) => s.setItemFilters);
  const setItemFilters = useCallback(
    (patch: Partial<ItemFilterState>) => {
      if (itemFiltersKey) {
        setItemFiltersByKey(itemFiltersKey, patch);
      }
    },
    [itemFiltersKey, setItemFiltersByKey]
  );
  const {
    ageRestrictedFilter,
    monetizedFilter,
    verifiedFilter,
    madeForKidsFilter,
    isShortFilter,
    safeForMinorsFilter,
    contentWarningFilter,
    thumbnailChangedFilter,
    commentsAllowedFilter,
    newOnlyFilter,
    minSubscribersFilter,
    minViewsFilter,
    maxSubscribersFilter,
    maxViewsFilter,
    minPinsFilter,
    minBoardsFilter,
    minSavesFilter,
    minDurationFilter,
    maxDurationFilter,
    lastPostSinceFilter,
    publishedSinceFilter,
    publishedUntilFilter,
    contentLanguageFilter,
    contentCountryFilter,
    rdFilters,
    sortOrder
  } = itemFilters;
  const debouncedMinSubscribersFilter = useDebounce(minSubscribersFilter, 400);
  const debouncedMinViewsFilter = useDebounce(minViewsFilter, 400);
  const debouncedMaxSubscribersFilter = useDebounce(maxSubscribersFilter, 400);
  const debouncedMaxViewsFilter = useDebounce(maxViewsFilter, 400);
  const debouncedMinPinsFilter = useDebounce(minPinsFilter, 400);
  const debouncedMinBoardsFilter = useDebounce(minBoardsFilter, 400);
  const debouncedMinSavesFilter = useDebounce(minSavesFilter, 400);
  const debouncedMinDurationFilter = useDebounce(minDurationFilter, 400);
  const debouncedMaxDurationFilter = useDebounce(maxDurationFilter, 400);

  // Same per-investigation key as the item filters above, persisted in uiStore (localStorage) -
  // reopening this investigation restores the smart-filter settings it was left with too.
  const smartFilterSettings = useUiStore((s) =>
    itemFiltersKey
      ? (s.smartFilterByKey[itemFiltersKey] ?? DEFAULT_SMART_FILTER)
      : DEFAULT_SMART_FILTER
  );
  const setSmartFilterByKey = useUiStore((s) => s.setSmartFilter);
  const setSmartFilter = useCallback(
    (patch: Partial<SmartFilterSettings>) => {
      if (itemFiltersKey) {
        setSmartFilterByKey(itemFiltersKey, patch);
      }
    },
    [itemFiltersKey, setSmartFilterByKey]
  );
  const {
    enabled: smartFilterEnabled,
    includeMode: smartFilterIncludeMode,
    includesEnabled: smartFilterIncludesEnabled,
    stemming: smartFilterStemming
  } = smartFilterSettings;
  const setSmartFilterEnabled = useCallback(
    (next: boolean) => {
      setSmartFilter({enabled: next});
    },
    [setSmartFilter]
  );
  const setSmartFilterIncludeMode = useCallback(
    (next: SmartFilterIncludeMode) => {
      setSmartFilter({includeMode: next});
    },
    [setSmartFilter]
  );
  const setSmartFilterIncludesEnabled = useCallback(
    (next: boolean) => {
      setSmartFilter({includesEnabled: next});
    },
    [setSmartFilter]
  );
  const setSmartFilterStemming = useCallback(
    (next: boolean) => {
      setSmartFilter({stemming: next});
    },
    [setSmartFilter]
  );

  // Same per-investigation key again: grouped-by-channel is how an analyst wants to read *this*
  // investigation, not a global preference - enabling it on one job left every job afterwards
  // grouped, including ones with a single channel where the grouping is pure noise.
  const groupByChannel = useUiStore((s) =>
    itemFiltersKey ? (s.groupByChannelByKey[itemFiltersKey] ?? false) : false
  );
  const setGroupByChannelByKey = useUiStore((s) => s.setGroupByChannel);
  const setGroupByChannel = useCallback(
    (next: boolean) => {
      if (itemFiltersKey) {
        setGroupByChannelByKey(itemFiltersKey, next);
      }
    },
    [itemFiltersKey, setGroupByChannelByKey]
  );

  useEffect(() => {
    const dismissed = getDismissedJobIds();
    if (dismissed.size > 0) {
      setJobs((previous) => previous.filter((j) => !dismissed.has(j.id)));
    }
  }, []);

  useEffect(() => {
    if (showNewFolder) {
      newFolderRef.current?.focus();
    }
  }, [showNewFolder]);

  // The number filters are debounced so each keystroke doesn't fire its own /api/items round trip -
  // each identity change here also recounts the total over the same filters (/api/items/count),
  // which on a large folder is the slow query.
  const buildFilterParams = useCallback(
    (): Partial<ItemsQuery> =>
      itemFilterQuery({
        ageRestrictedFilter,
        monetizedFilter,
        verifiedFilter,
        madeForKidsFilter,
        isShortFilter,
        safeForMinorsFilter,
        contentWarningFilter,
        thumbnailChangedFilter,
        commentsAllowedFilter,
        newOnlyFilter,
        minSubscribersFilter: debouncedMinSubscribersFilter,
        minViewsFilter: debouncedMinViewsFilter,
        maxSubscribersFilter: debouncedMaxSubscribersFilter,
        maxViewsFilter: debouncedMaxViewsFilter,
        minPinsFilter: debouncedMinPinsFilter,
        minBoardsFilter: debouncedMinBoardsFilter,
        minSavesFilter: debouncedMinSavesFilter,
        minDurationFilter: debouncedMinDurationFilter,
        maxDurationFilter: debouncedMaxDurationFilter,
        lastPostSinceFilter,
        publishedSinceFilter,
        publishedUntilFilter,
        contentLanguageFilter,
        contentCountryFilter,
        rdFilters
      }),
    [
      ageRestrictedFilter,
      monetizedFilter,
      verifiedFilter,
      madeForKidsFilter,
      isShortFilter,
      safeForMinorsFilter,
      contentWarningFilter,
      thumbnailChangedFilter,
      commentsAllowedFilter,
      newOnlyFilter,
      debouncedMinSubscribersFilter,
      debouncedMinViewsFilter,
      debouncedMaxSubscribersFilter,
      debouncedMaxViewsFilter,
      debouncedMinPinsFilter,
      debouncedMinBoardsFilter,
      debouncedMinSavesFilter,
      debouncedMinDurationFilter,
      debouncedMaxDurationFilter,
      lastPostSinceFilter,
      publishedSinceFilter,
      publishedUntilFilter,
      contentLanguageFilter,
      contentCountryFilter,
      rdFilters
    ]
  );

  // The item fetches' params: the filters plus the sort. Kept apart from buildFilterParams because
  // the channel counts reuse that too, and re-sorting can't change a count, so mustn't refetch them.
  const buildItemParams = useCallback(
    (): Partial<ItemsQuery> => ({
      ...buildFilterParams(),
      ...(sortOrder === DEFAULT_ITEM_SORT ? {} : {sort: sortOrder})
    }),
    [buildFilterParams, sortOrder]
  );

  // The total isn't part of a page: the count effect below owns it.
  const applyItemsPage = (data: ItemsPageWithoutTotal) => {
    setItems(data.results);
    setItemHasMore(data.has_more);
  };

  const clearItemsPage = () => {
    setItems([]);
    setItemHasMore(false);
  };

  // Read from an effect below instead of the job-switch/filter-change call sites depending on
  // groupByChannel directly - that would re-run their own effects (an extra page-1 refetch) on
  // every toggle of the button, which has nothing to do with either of them.
  const groupByChannelRef = useRef(false);
  useEffect(() => {
    groupByChannelRef.current = groupByChannel;
  }, [groupByChannel]);

  const [loadingAllForGrouping, setLoadingAllForGroupingState] = useState(false);
  // Mirrored into a ref for the same reason loadingMoreRef exists: the scroll observer fires on its
  // own schedule now, so a render-snapshot check can miss a bulk grouping fetch that started in the
  // same tick. loadMore reads the ref synchronously - interleaving the two is not benign, since the
  // grouping loop tracks itemPage in GROUP_PAGE_SIZE units and loadMore in PAGE_SIZE ones.
  const loadingAllForGroupingRef = useRef(false);
  const setLoadingAllForGrouping = useCallback((value: boolean) => {
    loadingAllForGroupingRef.current = value;
    setLoadingAllForGroupingState(value);
  }, []);
  // Set right before each round's request starts (to what that round is aiming to reach), not
  // after it returns - items.length alone would sit frozen at whatever the previous round loaded
  // for the entire duration of a slow request, reading as stuck rather than in progress.
  const [groupingLoadTarget, setGroupingLoadTarget] = useState(0);

  // Bumped by every pagination reset (job switch, folder switch, filter/query change) and by
  // this loop's own start, so a loop started for a since-superseded job/filter/toggle can be told
  // apart from the current one. The job identity check below isn't enough on its own - a
  // filter or search change resets pagination without changing job identity at all, so an
  // in-flight loop would otherwise keep appending pages fetched with the *old* filter into the
  // freshly-reset `items`, and overwrite `itemPage` out from under the new page 1 the reset just
  // applied.
  const groupingGenerationRef = useRef(0);

  // How much of the job the prefetch effect below has already fetched for - not just whether it
  // has run once. A boolean latch here originally left a still-growing job (status pending/running)
  // forever stuck on its first, early snapshot: every channel whose items arrived after that
  // snapshot was permanently "incomplete" (computeChannelDisplayCount can't safely hide what it
  // hasn't fully seen), correctly falling back to the server's raw count - and staying that way
  // until the analyst expanded it, since ensureChannelItemsLoaded's own per-channel fetch isn't
  // gated by any one-shot latch.
  //
  // Tracks `selected.item_count`, not `itemsTotal` - the 3-second job-status poll (see
  // withFreshStatus) is what actually keeps a running job's count fresh, and it only ever touches
  // the job object, never `itemsTotal`. `itemsTotal` is written solely by item-list fetches, which
  // in job-mode grouping means solely by this same prefetch - so comparing against it self-defeats:
  // a non-truncated prefetch always ends with items.length === itemsTotal, and nothing else in this
  // mode ever moves itemsTotal again to signal that the job grew further. Reset to 0 alongside every
  // other pagination reset: a new job, a new folder, or a filter/query change all mean the
  // prefetch (if any) needs to run again against the new item set.
  //
  // Set optimistically to the target *before* that target's fetch even starts (see the effect
  // below), not after it succeeds - a failed or superseded run simply isn't retried until the job
  // grows past this value again, rather than being retried on every render in the meantime.
  const jobGroupingPrefetchedItemCountRef = useRef(0);
  // When the last job-mode prefetch walk finished, for JOB_GROUP_REFIRE_COOLDOWN_MS.
  const jobGroupingPrefetchEndedAtRef = useRef(0);

  // Grouping only ever covers what's already in `items`. Rather than make an analyst mash "Load
  // more" to get an accurate picture while grouped, fetch the rest of this job's pages up front, at
  // GROUP_PAGE_SIZE rather than the plain list's PAGE_SIZE - every call pays a fixed per-request
  // cost regardless of page size (it used to include a full COUNT pass), so batching bigger cuts
  // how often that is paid, which is what actually made this slow on a 2,000+ item job. Capped so a
  // job with hundreds of thousands of items can't turn one toggle into an unbounded fetch loop (same
  // concern ExecutePagedAsync's doc comment raises about this endpoint under heavy paging).
  //
  // `replaceOnFirstRound` controls whether round 1 replaces `items` outright or merges into it: a
  // re-fire always re-walks from page 1 (see
  // shouldPrefetchJobChannels's own doc comment for why - `created_at DESC, id DESC` ordering means
  // new rows land at the front, so resuming from the tail would never see them), but must merge
  // rather than replace, since `items` already holds everything a previous run fetched and a round-1
  // replace would throw that away. True everywhere else: the caller's own already-fetched page 1
  // (always fetched at the plain PAGE_SIZE, before this loop ever runs) is a strict subset of this
  // loop's bigger first page, so appending on top of it would duplicate those rows instead.
  const loadRemainingPagesForGrouping = useCallback(
    async (options: {jobId: string; maxRounds: number; replaceOnFirstRound: boolean}) => {
      const {jobId, maxRounds, replaceOnFirstRound} = options;
      const myGeneration = ++groupingGenerationRef.current;
      setLoadingAllForGrouping(true);
      try {
        let groupPage = 0;
        let hasMore = true;
        let roundsLeft = maxRounds;
        let firstRound = true;
        while (hasMore && roundsLeft > 0) {
          const nextGroupPage = groupPage + 1;
          setGroupingLoadTarget(
            itemsTotalRef.current > 0
              ? Math.min(nextGroupPage * GROUP_PAGE_SIZE, itemsTotalRef.current)
              : nextGroupPage * GROUP_PAGE_SIZE
          );
          const parameters: ItemsQuery = {
            page: nextGroupPage,
            page_size: GROUP_PAGE_SIZE,
            ...(debouncedQuery ? {q: debouncedQuery} : {}),
            ...buildItemParams(),
            job_id: jobId
          };
          const data = await itemsApi.listWithoutTotal(parameters);
          if (
            groupingGenerationRef.current !== myGeneration ||
            selectedJobIdRef.current !== jobId
          ) {
            return;
          }

          if (firstRound && replaceOnFirstRound) {
            setItems(data.results);
          } else {
            // Dedupe on append, same as loadMore - a page can legitimately re-return a row already
            // fetched by an earlier round: a job-mode re-fire deliberately re-walks from page 1 (see
            // shouldPrefetchJobChannels), so its later rounds routinely overlap into rows the first
            // prefetch already held.
            setItems((previous) => {
              const existing = new Set(previous.map((i) => i.id));
              return [...previous, ...data.results.filter((i) => !existing.has(i.id))];
            });
          }

          setItemPage(nextGroupPage * GROUP_PAGES_PER_ROUND);
          setItemHasMore(data.has_more);
          groupPage = nextGroupPage;
          hasMore = data.has_more;
          roundsLeft -= 1;
          firstRound = false;
        }
      } catch {
        // A failed walk leaves the channels it didn't reach on their server counts; the next
        // re-fire (shouldPrefetchJobChannels) tries again.
      } finally {
        if (groupingGenerationRef.current === myGeneration) {
          setLoadingAllForGrouping(false);
        }
      }
    },
    [debouncedQuery, buildItemParams, setLoadingAllForGrouping]
  );

  const selectJob = useCallback(
    async (job: Job) => {
      selectedJobIdRef.current = job.id;
      selectedFolderIdRef.current = null;
      setSelected(job);
      setSelectedFolder(null);
      setClassifyItems([]);
      setSelectedItemIds(new Set());
      setItemQuery('');
      setItemPage(1);
      setAutoLoadRounds(0);
      setAutoLoadStalled(false);
      lastAutoLoadVisibleCountRef.current = -1;
      const myGeneration = ++groupingGenerationRef.current;
      setLoadingAllForGrouping(false);
      jobGroupingPrefetchedItemCountRef.current = 0;
      setGroupingLoadTarget(0);
      clearItemsPage();
      setLoadingItems(true);
      try {
        const [data, freshJob] = await Promise.all([
          itemsApi.listWithoutTotal({
            job_id: job.id,
            page: 1,
            page_size: PAGE_SIZE,
            ...buildItemParams()
          }),
          jobsApi.get(job.id).catch(() => job)
        ]);
        // A superseded response (another job/filter change fired and resolved first) must not
        // apply its page 1 over the newer one already in state, nor hand the loop below a
        // now-stale generation to bump past whatever request actually is current - see PR #190
        // review comment https://github.com/kroll-resolver-crisp/pri/pull/190#discussion_r3828419260.
        if (selectedJobIdRef.current !== job.id || groupingGenerationRef.current !== myGeneration) {
          return;
        }

        setJobs((previous) =>
          previous.map((j) => (j.id === job.id ? withFreshStatus(j, freshJob) : j))
        );
        setSelected((previous) =>
          previous?.id === job.id ? withFreshStatus(previous, freshJob) : previous
        );
        applyItemsPage(data);
      } catch {
        if (selectedJobIdRef.current !== job.id) {
          return;
        }

        clearItemsPage();
      } finally {
        setLoadingItems(false);
      }
    },
    [buildItemParams, setLoadingAllForGrouping, setLoadingItems]
  );

  // A folder aggregate has server-side channel counts (useChannelAggregates below, same as a job),
  // so it has nothing to kick off after applyItemsPage.
  const selectFolder = useCallback(
    async (folder: Folder) => {
      selectedFolderIdRef.current = folder.id;
      selectedJobIdRef.current = null;
      setSelectedFolder(folder);
      setSelected(null);
      setClassifyItems([]);
      setSelectedItemIds(new Set());
      setItemQuery('');
      setItemPage(1);
      setAutoLoadRounds(0);
      setAutoLoadStalled(false);
      lastAutoLoadVisibleCountRef.current = -1;
      const myGeneration = ++groupingGenerationRef.current;
      setLoadingAllForGrouping(false);
      jobGroupingPrefetchedItemCountRef.current = 0;
      setGroupingLoadTarget(0);
      clearItemsPage();
      setLoadingItems(true);
      try {
        const data = await itemsApi.listWithoutTotal({
          folder_id: folder.id,
          page: 1,
          page_size: PAGE_SIZE,
          ...buildItemParams()
        });
        if (
          selectedFolderIdRef.current !== folder.id ||
          groupingGenerationRef.current !== myGeneration
        ) {
          return;
        }

        applyItemsPage(data);
      } catch {
        if (selectedFolderIdRef.current !== folder.id) {
          return;
        }

        clearItemsPage();
      } finally {
        setLoadingItems(false);
      }
    },
    [buildItemParams, setLoadingAllForGrouping, setLoadingItems]
  );

  useEffect(() => {
    if (selected) {
      void selectJob(selected);
    } else if (selectedFolder) {
      void selectFolder(selectedFolder);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeJobId = selected?.id;
  const activeFolderId = selectedFolder?.id;
  // The header reads the server's count, which is right before the subtree has finished loading.
  const selectedFolderTotal = selectedFolder
    ? (folders.find((f) => f.id === selectedFolder.id)?.total_job_count ??
      selectedFolder.total_job_count)
    : 0;
  // Whether the selected folder's subtree has finished loading. "Re-run all" acts on the whole
  // subtree, so handleRerunAll waits for (or retries) the load rather than acting on part of it.
  const selectedFolderPages = selectedFolder
    ? scopePages.get(scopeKey({kind: 'subtree', id: selectedFolder.id}))
    : undefined;
  const selectedFolderIncomplete =
    selectedFolder !== null &&
    (!selectedFolderPages || selectedFolderPages.loading || selectedFolderPages.failed === true);
  // Every investigation currently filed under the selected folder, straight off `jobs` rather than
  // `filteredJobs` - the folder aggregate view and "Re-run all" act on the whole folder regardless
  // of whatever search/type/language chips happen to be narrowing the sidebar's own job list.
  const selectedFolderJobs = useMemo(() => {
    if (!selectedFolder) {
      return [];
    }

    // The whole subtree, matching what the server aggregates for a folder scope - otherwise the
    // header count, "Re-run all" and the poll-while-active check all disagree with the item list
    // as soon as anything is filed into a subfolder.
    const scope = folderSubtreeIds(folders, selectedFolder.id);
    return jobs.filter((j) => j.folder_id && scope.has(j.folder_id));
  }, [jobs, folders, selectedFolder]);

  // The one place both scope kinds funnel into an ItemsQuery scope param - job_id/folder_id are
  // mutually exclusive on the wire (see ItemsController.ResolveScopeAsync), matching how
  // selectJob/selectFolder keep the two selections mutually exclusive client-side.
  const scopeParams = useCallback((): ItemsQuery => {
    if (activeFolderId) {
      return {folder_id: activeFolderId};
    }

    return {job_id: activeJobId!};
  }, [activeFolderId, activeJobId]);

  // The selection's total, fetched beside its pages so a big folder's slow count never holds the
  // page back. Keyed on what can change it (scope, filters, search), not sort or paging. A running
  // job's item_count is in the key so the total follows its growth; the server caches counts for
  // 30s, so the 3-second poll doesn't recount every tick.
  const totalGenerationRef = useRef(0);
  const totalKeyRef = useRef('');
  const runningJobItemCount = selected?.status === 'running' ? selected.item_count : undefined;
  useEffect(() => {
    if (!activeJobId && !activeFolderId) {
      return;
    }

    const parameters = {
      ...(debouncedQuery ? {q: debouncedQuery} : {}),
      ...buildFilterParams(),
      ...scopeParams()
    };
    const myGeneration = ++totalGenerationRef.current;
    // Only a new question blanks the total; a running job's recount shows the last one meanwhile.
    const key = JSON.stringify(parameters);
    if (key !== totalKeyRef.current) {
      totalKeyRef.current = key;
      setItemsTotal(null);
      setItemsTotalCapped(false);
      itemsTotalRef.current = 0;
    }

    itemsApi
      .count(parameters)
      .then((count) => {
        if (totalGenerationRef.current !== myGeneration) {
          return;
        }

        setItemsTotal(count.total);
        setItemsTotalCapped(count.total_capped);
        itemsTotalRef.current = count.total;
      })
      .catch(() => {
        // Left as "…": the items are still listed, and a failed count shouldn't fail the page.
      });
    // The active job/folder ids are read through scopeParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeParams, buildFilterParams, debouncedQuery, runningJobItemCount]);
  const isFirstQueryRender = useRef(true);

  useEffect(() => {
    if (isFirstQueryRender.current) {
      isFirstQueryRender.current = false;
      return;
    }

    if (!activeJobId && !activeFolderId) {
      return;
    }

    clearItemsPage();
    setItemPage(1);
    setAutoLoadRounds(0);
    setAutoLoadStalled(false);
    lastAutoLoadVisibleCountRef.current = -1;
    const myGeneration = ++groupingGenerationRef.current;
    setLoadingAllForGrouping(false);
    jobGroupingPrefetchedItemCountRef.current = 0;
    setGroupingLoadTarget(0);
    setLoadingItems(true);
    const parameters: ItemsQuery = {
      page: 1,
      page_size: PAGE_SIZE,
      ...(debouncedQuery ? {q: debouncedQuery} : {}),
      ...buildItemParams(),
      ...scopeParams()
    };
    itemsApi
      .listWithoutTotal(parameters)
      .then((data) => {
        // Unlike selectJob/selectFolder this effect has no selection-identity ref to check -
        // two filter changes in a row racing is the exact case this guard exists for, so the
        // generation check is the only thing standing between a stale response and clobbering the
        // list a newer request already populated.
        if (groupingGenerationRef.current !== myGeneration) {
          return;
        }

        applyItemsPage(data);
      })
      .catch(clearItemsPage)
      .finally(() => {
        setLoadingItems(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, buildItemParams]);

  // Polls every pending/running job directly by id (rather than re-listing page 1) while any job
  // is still pending/running, so a job that finishes server-side (processing -> completed) shows
  // up without the analyst having to hit refresh - including one that's since been pushed off
  // page 1 by newer jobs. Uses a ref rather than a `jobs` dependency so the interval isn't torn
  // down and recreated on every poll tick, and skips entirely once nothing is in flight, while a
  // tab is backgrounded, or while the previous tick's requests are still outstanding.
  const jobsRef = useRef<Job[]>(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const pollInFlightRef = useRef(false);
  useEffect(() => {
    const interval = setInterval(async () => {
      const pendingIds = jobsRef.current
        .filter((j) => j.status === 'pending' || j.status === 'running')
        .map((j) => j.id);
      if (pendingIds.length === 0 || document.hidden || pollInFlightRef.current) {
        return;
      }

      pollInFlightRef.current = true;
      try {
        // One request per batch of ids rather than one per job, so a folder of running
        // investigations costs a single round trip each tick. A failed batch is skipped this tick.
        const batches = await Promise.all(
          chunked(pendingIds, JOB_IDS_PER_REQUEST).map(async (ids) =>
            jobsApi
              .list({ids, page_size: ids.length})
              .then((page) => page.results)
              .catch(() => [])
          )
        );
        const fresh = new Map(batches.flat().map((j) => [j.id, j]));

        setJobs((previous) => {
          let changed = false;
          const next = previous.map((j) => {
            const f = fresh.get(j.id);
            const updated = f ? withFreshStatus(j, f) : j;
            if (updated !== j) {
              changed = true;
            }

            return updated;
          });
          // Keep the previous array reference when nothing actually changed, so this tick doesn't
          // re-render every mounted item card for no reason.
          return changed ? next : previous;
        });
        setSelected((previous) => {
          if (!previous) {
            return previous;
          }

          const f = fresh.get(previous.id);
          return f ? withFreshStatus(previous, f) : previous;
        });
      } finally {
        pollInFlightRef.current = false;
      }
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Auto-loads the next page of unfiled jobs once the sentinel at the bottom of the sidebar list
  // scrolls into view. Folders page the same way through their own ScopeSentinel.
  const unfiledHasMore = unfiledPages?.hasMore ?? false;
  useEffect(() => {
    const sentinel = jobsSentinelRef.current;
    if (!sentinel || !unfiledHasMore) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        void loadScope(UNFILED_SCOPE, 'more');
      }
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [unfiledHasMore, loadScope]);

  // What loadMore's in-flight page was fetched under. A sort, filter or search change resets the list
  // to a fresh page 1, and a page fetched under the old params landing afterwards would append
  // rows in the old order (or outside the new filter) and wind itemPage back.
  const latestItemParamsRef = useRef({buildItemParams, debouncedQuery});
  useEffect(() => {
    latestItemParamsRef.current = {buildItemParams, debouncedQuery};
  }, [buildItemParams, debouncedQuery]);

  const loadMore = useCallback(async () => {
    if (
      (!activeJobId && !activeFolderId) ||
      loadingMoreRef.current ||
      loadingAllForGroupingRef.current
    ) {
      return;
    }

    const jobId = activeJobId ?? null;
    const folderId = activeFolderId ?? null;
    const nextPage = itemPage + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const parameters: ItemsQuery = {
        page: nextPage,
        page_size: PAGE_SIZE,
        ...(debouncedQuery ? {q: debouncedQuery} : {}),
        ...buildItemParams(),
        ...scopeParams()
      };
      const data = await itemsApi.listWithoutTotal(parameters);
      // The sidebar selection can change while this request is in flight - applying a page fetched
      // for the previous job/folder would append it into the new selection's list and
      // desync itemPage from what's actually loaded, silently skipping items on the next "Load more".
      if (
        selectedJobIdRef.current !== jobId ||
        selectedFolderIdRef.current !== folderId ||
        latestItemParamsRef.current.buildItemParams !== buildItemParams ||
        latestItemParamsRef.current.debouncedQuery !== debouncedQuery
      ) {
        return;
      }

      // Dedupe on append, same as loadMoreJobs: an item can legitimately arrive on two different
      // pages when Ingress inserts rows underneath the offset mid-session, and a duplicate here
      // means duplicate React keys and an inflated "select all".
      setItems((previous) => {
        const existing = new Set(previous.map((i) => i.id));
        return [...previous, ...data.results.filter((i) => !existing.has(i.id))];
      });
      setItemPage(nextPage);
      setItemHasMore(data.has_more);
    } catch {
      if (selectedJobIdRef.current !== jobId || selectedFolderIdRef.current !== folderId) {
        return;
      }

      // Stop the auto-load-more effect from retrying in a tight loop on a persistent failure.
      setItemHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [activeJobId, activeFolderId, itemPage, debouncedQuery, buildItemParams, scopeParams]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      return;
    }

    setCreatingFolder(true);
    setFolderError(null);
    try {
      const parentFolderId = newFolderParentId;
      const folder = await createFolderMut.mutateAsync({
        name: newFolderName.trim(),
        ...(parentFolderId ? {parentFolderId} : {})
      });
      setFolders((previous) => [...previous, folder]);
      setExpandedFolders(
        (previous) =>
          // The new folder starts open, and so does its parent - otherwise creating a subfolder
          // inside a collapsed folder looks like nothing happened.
          new Set([...previous, folder.id, ...(parentFolderId ? [parentFolderId] : [])])
      );
      setNewFolderName('');
      setNewFolderParentId(null);
      setShowNewFolder(false);
    } catch (error) {
      setFolderError(getApiErrorMessage(error, 'Could not create folder.'));
    } finally {
      setCreatingFolder(false);
    }
  };

  const startRenameFolder = (e: React.MouseEvent, folder: Folder) => {
    e.stopPropagation();
    setRenamingFolderId(folder.id);
    setRenameFolderName(folder.name);
    setRenameFolderError(null);
  };

  const cancelRenameFolder = () => {
    setRenamingFolderId(null);
    setRenameFolderName('');
    setRenameFolderError(null);
  };

  const saveRenameFolder = async (folderId: string) => {
    const name = renameFolderName.trim();
    const current = folders.find((f) => f.id === folderId);
    if (!name || name === current?.name) {
      cancelRenameFolder();
      return;
    }

    try {
      const updated = await updateFolderMut.mutateAsync({id: folderId, name});
      setFolders((previous) => previous.map((f) => (f.id === folderId ? updated : f)));
      setSelectedFolder((previous) => (previous?.id === folderId ? updated : previous));
      cancelRenameFolder();
    } catch (error) {
      setRenameFolderError(getApiErrorMessage(error, 'Could not rename folder.'));
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await deleteFolderMut.mutateAsync(folderId);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to delete folder.'), 'error');
      return;
    }

    // Children are promoted to the deleted folder's own parent server-side, which a local patch
    // cannot reproduce faithfully - so drop the row optimistically for responsiveness and take the
    // real shape from a refetch.
    setFolders((previous) => previous.filter((f) => f.id !== folderId));
    setJobs((previous) =>
      previous.map((j) => (j.folder_id === folderId ? {...j, folder_id: null} : j))
    );
    void foldersApi
      .list([...creatorFilter])
      .then(setFolders)
      .catch((error: unknown) => {
        setFolderError(getApiErrorMessage(error, 'Could not refresh folders.'));
      });
    // Otherwise the detail pane keeps showing the now-deleted folder's name/items with no way back
    // - selectedFolderJobs would self-correct to empty via the folder_id update above, but
    // selectedFolder itself (and the items already loaded into state) would not.
    if (selectedFolderIdRef.current === folderId) {
      selectedFolderIdRef.current = null;
      setSelectedFolder(null);
      clearItemsPage();
    }
  };

  // Folder badges read the server's subtree counts, so anything that changes which folder a job
  // sits in re-reads them. Best effort: a failed refresh leaves the previous counts showing.
  const refreshFolderCounts = useCallback(() => {
    void foldersApi
      .list([...creatorFilter])
      .then(setFolders)
      .catch(() => undefined);
  }, [creatorFilter]);

  const handleMoveJobs = async (jobIds: string[], folderId: string | null) => {
    const toMove = jobIds.filter((id) => jobs.find((j) => j.id === id)?.folder_id !== folderId);
    if (toMove.length === 0) {
      return;
    }

    const priorFolderIds = new Map<string, string | null>();
    for (const id of toMove) {
      const job = jobs.find((j) => j.id === id);
      if (job) {
        priorFolderIds.set(id, job.folder_id);
      }
    }

    setJobs((previous) =>
      previous.map((j) => (toMove.includes(j.id) ? {...j, folder_id: folderId} : j))
    );

    try {
      await Promise.all(toMove.map(async (id) => setJobFolderMut.mutateAsync({id, folderId})));
      refreshFolderCounts();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to move investigations.'), 'error');
      setJobs((previous) =>
        previous.map((j) =>
          toMove.includes(j.id) ? {...j, folder_id: priorFolderIds.get(j.id) ?? null} : j
        )
      );
    }
  };

  const handleMoveFolder = async (folderId: string, parentFolderId: string | null) => {
    const folder = folders.find((f) => f.id === folderId);
    if (!folder || folder.parent_folder_id === parentFolderId) {
      return;
    }

    const prior = folder.parent_folder_id;
    setFolders((previous) =>
      previous.map((f) => (f.id === folderId ? {...f, parent_folder_id: parentFolderId} : f))
    );
    setFolderError(null);
    try {
      await updateFolderMut.mutateAsync(
        parentFolderId === null
          ? {id: folderId, clear_parent: true}
          : {id: folderId, parent_folder_id: parentFolderId}
      );
      if (parentFolderId) {
        setExpandedFolders((previous) => new Set([...previous, parentFolderId]));
      }
    } catch (error) {
      setFolders((previous) =>
        previous.map((f) => (f.id === folderId ? {...f, parent_folder_id: prior} : f))
      );
      // The server rejects a cycle, a cross-owner parent and a too-deep move with a 409 whose
      // detail says which - surface it rather than silently snapping the row back.
      setFolderError(getApiErrorMessage(error, 'Could not move folder.'));
    }
  };

  // Dropping on "Ungrouped" moves a nested folder back to the top level - the only route out of
  // a folder, since every other drop target nests it further. A folder already at the top level
  // has nowhere to go, so the zone stays closed to it.
  const draggingFolderHasParent =
    draggingFolderId !== null &&
    (folders.find((f) => f.id === draggingFolderId)?.parent_folder_id ?? null) !== null;

  const handleDrop = (targetFolderId: string | null) => {
    if (draggingFolderId) {
      const folderId = draggingFolderId;
      setDragOverTarget(null);
      setDraggingFolderId(null);
      if (!invalidFolderDropTargets(folderId).has(targetFolderId ?? '')) {
        void handleMoveFolder(folderId, targetFolderId);
      }

      return;
    }

    if (!draggingJobId) {
      return;
    }

    setDragOverTarget(null);
    setDraggingJobId(null);
    const ids =
      checkedJobs.has(draggingJobId) && checkedJobs.size > 1 ? [...checkedJobs] : [draggingJobId];
    void handleMoveJobs(ids, targetFolderId);
    setCheckedJobs(new Set());
  };

  const handleJobClick = (e: React.MouseEvent | React.KeyboardEvent, job: Job, flatIdx: number) => {
    if (e.shiftKey && lastClickedIdxRef.current >= 0) {
      const lo = Math.min(lastClickedIdxRef.current, flatIdx);
      const hi = Math.max(lastClickedIdxRef.current, flatIdx);
      setCheckedJobs(new Set(filteredJobs.slice(lo, hi + 1).map((j) => j.id)));
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      setCheckedJobs((previous) => {
        const next = new Set(previous);
        if (next.has(job.id)) {
          next.delete(job.id);
        } else {
          next.add(job.id);
        }

        return next;
      });
      lastClickedIdxRef.current = flatIdx;
      return;
    }

    setCheckedJobs(new Set());
    lastClickedIdxRef.current = flatIdx;
    void selectJob(job);
  };

  const allFoldersExpanded = folders.length > 0 && folders.every((f) => expandedFolders.has(f.id));

  const toggleFolder = (id: string) => {
    setExpandedFolders((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  // Distinct policy-violation categories across the currently loaded jobs, for the filter chips -
  // there's no fixed config list of these in the frontend (unlike JOB_TYPE_CONFIG), so it's derived
  // from what's actually present rather than a static catalog.
  const policyViolationOptions = useMemo(() => {
    const values = new Set<string>(jobFacets.data?.policy_violations);
    for (const j of jobs) {
      for (const pv of j.target_policy_violations) {
        values.add(pv);
      }
    }

    return [...values].sort();
  }, [jobs, jobFacets.data]);

  // Same derive-from-what's-loaded reasoning as policyViolationOptions above: the language pack has
  // ~7,000 entries, so a filter listing the catalog rather than what's actually in use is unusable.
  const languageOptions = useMemo(() => {
    const values = new Set<string>(jobFacets.data?.languages);
    for (const j of jobs) {
      if (j.language) {
        values.add(j.language);
      }
    }

    return [...values].sort();
  }, [jobs, jobFacets.data]);

  const typeFilterLabel = jobTypeFilter.size === 0 ? 'Type' : `Type (${jobTypeFilter.size})`;
  const verticalsFilterLabel =
    policyViolationFilter.size === 0 ? 'Verticals' : `Verticals (${policyViolationFilter.size})`;
  const languageFilterLabel =
    languageFilter.size === 0 ? 'Language' : `Language (${languageFilter.size})`;
  const creatorFilterLabel =
    creatorFilter.size === 0 ? 'Created by' : `Created by (${creatorFilter.size})`;
  const creatorOptions = jobFacets.data?.creators ?? [];

  const filteredJobs = useMemo(
    () =>
      jobs.filter((j) => {
        // Search_term alone misses jobs like image_search, whose search_term is a 64-char hex hash
        // never shown to the analyst - match against the same label the sidebar actually renders too.
        const matchesQuery =
          !jobQuery ||
          j.search_term.toLowerCase().includes(jobQuery.toLowerCase()) ||
          jobDisplayLabel(j).toLowerCase().includes(jobQuery.toLowerCase()) ||
          j.intent?.toLowerCase().includes(jobQuery.toLowerCase()) ||
          j.language?.toLowerCase().includes(jobQuery.toLowerCase());
        // Strategies with no JOB_TYPE_CONFIG entry (e.g. youtube_video) get no filter chip and are
        // never filterable, so they must always match rather than silently disappearing once any
        // chip is active.
        const matchesType =
          jobTypeFilter.size === 0 ||
          jobTypeFilter.has(j.strategy) ||
          !(j.strategy in JOB_TYPE_CONFIG);
        const matchesPolicyViolation =
          policyViolationFilter.size === 0 ||
          j.target_policy_violations.some((pv) => policyViolationFilter.has(pv));
        const matchesLanguage =
          languageFilter.size === 0 || (j.language !== null && languageFilter.has(j.language));
        const matchesMine = !mineOnly || !isBroadView || j.created_by === user?.username;
        const matchesScheduled = !scheduledOnly || j.schedule_cron !== null;
        const matchesCreator =
          creatorFilter.size === 0 || creatorFilter.has(j.created_by.toLowerCase());
        return (
          matchesQuery &&
          matchesType &&
          matchesPolicyViolation &&
          matchesLanguage &&
          matchesMine &&
          matchesScheduled &&
          matchesCreator
        );
      }),
    [
      jobs,
      jobQuery,
      jobTypeFilter,
      policyViolationFilter,
      languageFilter,
      creatorFilter,
      mineOnly,
      isBroadView,
      scheduledOnly,
      user?.username
    ]
  );

  // Id -> position in filteredJobs, for handleJobClick's shift-click range selection - a plain
  // `filteredJobs.indexOf(j)` inside each row's render was an O(n) scan per row (O(n^2) per list
  // render), same list this map is built from.
  const filteredJobIndexById = useMemo(() => {
    const map = new Map<string, number>();
    for (const [index, j] of filteredJobs.entries()) {
      map.set(j.id, index);
    }

    return map;
  }, [filteredJobs]);

  const ungrouped = useMemo(() => filteredJobs.filter((j) => !j.folder_id), [filteredJobs]);
  // Direct contents only: a folder's descendants render as their own rows beneath it, so pulling
  // the subtree in here would list every job twice.
  const filteredJobsByFolder = useMemo(() => groupJobsByFolder(filteredJobs), [filteredJobs]);
  const inFolder = (fId: string) => filteredJobsByFolder.get(fId) ?? [];

  // Folders that hold at least one other folder. Their subfolders render as sibling rows below
  // the folder's own body, so that body must not claim the folder is empty just because nothing
  // is filed directly in it.
  const parentFolderIds = useMemo(
    () => new Set(folders.map((f) => f.parent_folder_id).filter((id): id is string => id !== null)),
    [folders]
  );

  // Jobs in a folder and everything beneath it, counted off the loaded filteredJobs. Only the badge
  // fallback while a search or chip is active reads this - unfiltered, the badge takes the server's
  // subtree count (see folderBadgeCount).
  const loadedSubtreeJobCounts = useMemo(
    () =>
      rollUpSubtreeCounts(
        folders,
        new Map([...filteredJobsByFolder].map(([id, list]) => [id, list.length]))
      ),
    [folders, filteredJobsByFolder]
  );
  const badgeScope: BadgeScope = {
    filtersActive:
      jobQuery !== '' ||
      jobTypeFilter.size > 0 ||
      policyViolationFilter.size > 0 ||
      languageFilter.size > 0 ||
      scheduledOnly ||
      creatorFilter.size > 0,
    mineOnly: mineOnly && isBroadView
  };

  // A folder whose subtree holds other analysts' jobs but none of mine - hidden under Mine-only.
  // Both counts come straight off the folder from the API, over its whole subtree server-side, so
  // this doesn't depend on how many pages of jobs the client has loaded (unlike a count taken from
  // `jobs`, which is only the pages fetched so far). A folder with no jobs at all is never hidden,
  // so folder creation and empty drop targets keep working.
  const isHiddenWhenMine = useCallback(
    (folderId: string) => {
      const folder = folders.find((f) => f.id === folderId);
      return (
        folder !== undefined && folder.total_job_count > 0 && folder.total_mine_job_count === 0
      );
    },
    [folders]
  );

  // Folders in tree order with a depth to indent by. Collapsed folders' subtrees are dropped
  // during the walk, so this is the whole render list.
  // With Mine-only on, drop every folder with a hidden ancestor too - an empty subfolder of a
  // hidden folder is not itself hidden by isHiddenWhenMine (it has no jobs of its own to lack),
  // so without the ancestor walk it would render indented under a parent that vanished.
  const folderRows = useMemo(() => {
    const rows = flattenFolders(folders, expandedFolders);
    if (!mineOnly || !isBroadView) {
      return rows;
    }

    const byId = new Map(folders.map((f) => [f.id, f]));
    return rows.filter((row) => {
      let current: string | null = row.folder.id;
      const seen = new Set<string>();
      while (current && !seen.has(current)) {
        seen.add(current);
        if (isHiddenWhenMine(current)) {
          return false;
        }

        current = byId.get(current)?.parent_folder_id ?? null;
      }

      return true;
    });
  }, [folders, expandedFolders, mineOnly, isBroadView, isHiddenWhenMine]);

  // Header rows in tree order, with each expanded folder's own investigations after its subtree.
  const folderRailItems = useMemo(
    () => withFolderBodies(folderRows, expandedFolders),
    [folderRows, expandedFolders]
  );

  // A folder cannot move into itself or anything beneath it, and moving it where it already sits
  // is a no-op. Checked here so the common mis-drag renders as a refused target instead of a 409.
  const invalidFolderDropTargets = useCallback(
    (folderId: string) =>
      new Set([
        ...folderSubtreeIds(folders, folderId),
        folders.find((f) => f.id === folderId)?.parent_folder_id ?? ''
      ]),
    [folders]
  );
  // A job created from the FOLDERS or UNFILED header has no folder, so there is nothing to expand.
  const handleUnfiledJobCreated = (job: Job) => {
    setJobs((previous) => [job, ...previous.filter((j) => j.id !== job.id)]);
    void selectJob(job);
  };

  const [showClassified, setShowClassified] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  // Only a selected job of a free-text search strategy has a query to enforce (see
  // FREE_TEXT_SEARCH_STRATEGIES).
  const smartFilterApplicable = FREE_TEXT_SEARCH_STRATEGIES.has(selected?.strategy ?? '');
  const activeSearchTerm = selected?.search_term ?? '';
  const smartQuery = useMemo(
    () => (smartFilterApplicable ? parseSearchQuery(activeSearchTerm) : {include: [], exclude: []}),
    [smartFilterApplicable, activeSearchTerm]
  );
  const smartMatchOptions = useMemo(
    () => ({
      includeMode: smartFilterIncludeMode,
      applyIncludes: smartFilterIncludesEnabled,
      stem: smartFilterStemming
    }),
    [smartFilterIncludeMode, smartFilterIncludesEnabled, smartFilterStemming]
  );

  // Nothing to enforce when the job has no exclusions and include filtering is off - the common
  // case now that includes are opt-in, and worth short-circuiting because the matcher builds and
  // stems a haystack per item.
  const smartFilterActive =
    smartFilterEnabled &&
    (smartQuery.exclude.length > 0 ||
      (smartFilterIncludesEnabled && smartQuery.include.length > 0));

  // Items after the classified/dismissed triage filter, before smart filtering - the base set the
  // smart filter actually operates on, so its hidden-count and denominator don't double-count items
  // already hidden by triage (which would make "N hidden by smart filter" exceed what smart
  // filtering alone removed from view).
  const afterTriageItems = useMemo(
    () =>
      items.filter(
        (i) =>
          passesTriage(i, showClassified, showDismissed) && matchesFlagFilter(i.flag, flagFilter)
      ),
    [items, showClassified, showDismissed, flagFilter]
  );

  // Only meaningful once we know how each result was matched, which is image_search jobs only.
  // Applicable gates whether the menu renders at all; active additionally requires something to be
  // switched off, so a stale exclusion set can never hide rows on a job that has no tiers.
  const matchTierFilterApplicable = imageMatchTiersByVideoId.size > 0;
  const matchTierFilterActive = matchTierFilterApplicable && excludedMatchTiers.size > 0;

  const matchTierCounts = useMemo(() => {
    const counts = new Map<ImageMatchTier, number>();
    if (!matchTierFilterApplicable) {
      return counts;
    }

    for (const item of afterTriageItems) {
      const youtubeId = imageMatchIdFor(item);
      const tier = youtubeId ? imageMatchTiersByVideoId.get(youtubeId) : undefined;
      if (tier) {
        counts.set(tier, (counts.get(tier) ?? 0) + 1);
      }
    }

    return counts;
  }, [afterTriageItems, matchTierFilterApplicable, imageMatchTiersByVideoId]);

  // Shared with an expanded job-mode channel group's own items below - those come straight from
  // the server (ensureChannelItemsLoaded), bypassing the visibleItems memo entirely, so without
  // reusing these exact predicates a channel's exclusion terms/match-tier toggle would silently
  // stop applying inside its own expanded group.
  const isSmartFilterRejected = useCallback(
    (item: Item) =>
      smartFilterActive && !matchesItemSearchQuery(item, smartQuery, smartMatchOptions),
    [smartFilterActive, smartQuery, smartMatchOptions]
  );
  const isMatchTierRejected = useCallback(
    (item: Item) =>
      matchTierFilterActive &&
      isHiddenByMatchTier(item, imageMatchTiersByVideoId, excludedMatchTiers),
    [matchTierFilterActive, imageMatchTiersByVideoId, excludedMatchTiers]
  );

  // One pass for both lists and both counts: the matcher rebuilds and stems each item's haystack, so
  // running it in two memos over the same list doubled that work for no reason. Returns
  // `afterTriageItems` by reference when NEITHER filter is active, so the downstream grouping memos
  // don't recompute.
  const {visibleItems, smartFilteredCount, matchTierFilteredCount} = useMemo(() => {
    if (!smartFilterActive && !matchTierFilterActive) {
      return {visibleItems: afterTriageItems, smartFilteredCount: 0, matchTierFilteredCount: 0};
    }

    // Counted per filter rather than as one total, so each "N hidden by ..." reads as what THAT
    // filter removed. An item both would hide counts against both, which is what the labels claim.
    let smartHidden = 0;
    let tierHidden = 0;
    const kept = afterTriageItems.filter((i) => {
      const smartRejected = isSmartFilterRejected(i);
      const tierRejected = isMatchTierRejected(i);
      if (smartRejected) {
        smartHidden++;
      }

      if (tierRejected) {
        tierHidden++;
      }

      return !smartRejected && !tierRejected;
    });

    return {
      visibleItems: kept,
      smartFilteredCount: smartHidden,
      matchTierFilteredCount: tierHidden
    };
  }, [
    afterTriageItems,
    smartFilterActive,
    matchTierFilterActive,
    isSmartFilterRejected,
    isMatchTierRejected
  ]);

  // One pass in groupItemsByChannel already builds each group's item bucket alongside its count -
  // group.items below is that bucket, so there's no separate map to look up per group. It's
  // superseded by serverChannelGroups below once that query resolves; kept as the fallback while
  // that query is loading, so toggling "Group by channel"
  // never briefly shows nothing.
  const channelGroups = useMemo(() => groupItemsByChannel(visibleItems), [visibleItems]);

  // Same filter set buildFilterParams already sends to /items - reused here so a filtered view's
  // channel counts match what's actually visible, and re-fetched only when a filter/search
  // actually changes (buildFilterParams and debouncedQuery are themselves stable unless their own
  // inputs changed), not on every render.
  const channelFilters = useMemo(
    () => ({...(debouncedQuery ? {q: debouncedQuery} : {}), ...buildFilterParams()}),
    [debouncedQuery, buildFilterParams]
  );
  // The metadata filters alone: the RD option lists must not be narrowed by the RD selection itself.
  const rdOptionFilters = useMemo(() => {
    const metadata = {...channelFilters};
    delete metadata.rd_filters;
    return metadata;
  }, [channelFilters]);
  // Whether a SERVER-side filter or search is narrowing this job's items right now. "Dismiss
  // channel"'s author_id fast path (below) dismisses every item the job has from that channel with
  // no filtering of its own, so it's only correct to use while this is false: a server filter
  // changes which items the analyst is looking at at all, and dismissing the ones outside it would
  // reach past what they can see.
  //
  // The client-side view filters (smart filter, match-tier filter) deliberately DON'T count. They
  // narrow the same set the fast path acts on, and "Dismiss channel" is a channel-level verb - an
  // item hidden for containing an excluded term is precisely the noise being cleared, and Restore
  // channel puts it all back. Counting them here is what made this unusable: smartFilterEnabled
  // defaults true, so any job whose search term carries a `-exclusion` forced every row onto the
  // id-list path, which acts on the group's loaded-and-visible items - empty until expanded, and
  // still empty afterwards for a channel the smart filter rejects wholesale, so the button sat
  // disabled with no way to ever enable it.
  const channelFiltersActive = Object.keys(channelFilters).length > 0;

  // Shared between this query's own poll-while-active refetch and the job-mode prefetch effect
  // below, so the two can't quietly disagree on what "still growing" means. A folder polls if any
  // of its own investigations are still active - any one of them can still add channels/items to
  // the aggregate the same way a single running job can.
  const jobIsActive = selected?.status === 'pending' || selected?.status === 'running';
  const folderIsActive = selectedFolderJobs.some(
    (j) => j.status === 'pending' || j.status === 'running'
  );

  // Item-scope, not just job-id, so a folder gets the same server-side GROUP BY a job does -
  // ItemsController.ListChannels serves both routes off the same query shape (see
  // ResolveScopeAsync). Polled every 3s
  // (matching the job-status poll's own cadence) while active - see useChannelAggregates' own doc
  // comment for why a one-time snapshot leaves a channel that first appears mid-crawl permanently
  // missing from the grouped view, however completely the item prefetch below covers channels
  // already in this aggregate.
  // Memoized so it's referentially stable across renders where neither selection changed - several
  // useCallbacks below (setItemStatus, bulkSetItemStatus, ensureChannelItemsLoaded, renderItems)
  // depend on it, and a fresh object every render would defeat their own memoization in turn.
  // Keyed on the ids, not the objects: the 3-second status poll replaces `selected` whenever a
  // running job's item_count moves, and depending on the object re-rendered every mounted item card
  // on each tick (GT-16811).
  const selectedJobId = selected?.id;
  const selectedFolderId = selectedFolder?.id;

  const channelAggregateScope: ChannelAggregateScope | undefined = useMemo(
    () =>
      selectedJobId
        ? {kind: 'job', id: selectedJobId}
        : selectedFolderId
          ? {kind: 'folder', id: selectedFolderId}
          : undefined,
    [selectedJobId, selectedFolderId]
  );
  const channelsQuery = useChannelAggregates(
    channelAggregateScope,
    groupByChannel && Boolean(channelAggregateScope),
    channelFilters,
    groupByChannel && (jobIsActive || folderIsActive) ? 3000 : undefined
  );

  // Server channel aggregates know nothing about the client-side smart/match-tier filters, and a
  // job's channel items load only on expand - so without every item's text there is no way to tell
  // which channels those filters empty. Kicks a fetch-the-rest loop
  // (loadRemainingPagesForGrouping), gated on a filter actually being active
  // so a plain grouped job keeps the single GROUP BY the channels endpoint was introduced for.
  //
  // The decision itself lives in shouldPrefetchJobChannels (see its own doc comment for why
  // selected.item_count, not itemsTotal, is the growth signal, and for why a re-fire re-walks from
  // page 1 rather than resuming from the tail) - jobGroupingPrefetchedItemCountRef is what lets it
  // tell "already caught up" from "the job grew since we last looked", re-firing on the latter
  // instead of freezing after the first attempt the way a plain one-shot latch would.
  //
  // `!loadingItemsRef.current` waits for selectJob/the filter-change effect's own
  // page-1 fetch to actually land before this one starts. Without it, selecting a job (or changing
  // a filter) fires both in the same commit - selected.item_count is already known from the job
  // list before that fetch resolves - and this effect's own ++groupingGenerationRef then discards
  // page 1's response as superseded; if this fetch's own first round then fails, nothing retries it,
  // and `items` stays empty until the job grows a full round or a filter changes.
  //
  // The ref, not the `loadingItems` state, is what actually closes this: the filter-change effect
  // is a passive effect declared earlier in this component, and when its own dependencies change in
  // the same render as this effect's, React flushes both in the same commit without a re-render in
  // between - a `setLoadingItems(true)` there hasn't propagated to this effect's `loadingItems`
  // closure yet (review on #352, discussion r4014259297: this genuinely reopened the race for that
  // path even after the state-based gate closed it for selectJob). The ref write
  // inside setLoadingItems happens synchronously in the same tick, so it's visible here regardless
  // of render/commit boundaries - the exact reason loadingAllForGroupingRef exists alongside
  // loadingAllForGrouping above.
  useEffect(() => {
    if (!selected || loadingItemsRef.current) {
      return;
    }

    const decision = shouldPrefetchJobChannels({
      groupByChannel,
      hasClientSideFilter: smartFilterActive || matchTierFilterActive,
      loadingAllForGrouping,
      jobItemCount: selected.item_count,
      jobIsActive,
      lastPrefetchedItemCount: jobGroupingPrefetchedItemCountRef.current,
      msSinceLastPrefetch: Date.now() - jobGroupingPrefetchEndedAtRef.current
    });
    if (!decision.shouldFetch) {
      return;
    }

    jobGroupingPrefetchedItemCountRef.current = selected.item_count;
    void loadRemainingPagesForGrouping({
      jobId: selected.id,
      maxRounds: decision.rounds,
      replaceOnFirstRound: decision.replaceOnFirstRound
    }).finally(() => {
      jobGroupingPrefetchEndedAtRef.current = Date.now();
    });
  }, [
    selected,
    loadingItems,
    groupByChannel,
    smartFilterActive,
    matchTierFilterActive,
    loadingAllForGrouping,
    jobIsActive,
    loadRemainingPagesForGrouping
  ]);

  // Whole-job totals for the "N classified/dismissed video items hidden" banner. Sourced from
  // channelsQuery once it's active (job-mode grouping) rather than scanning `items` - job-mode
  // grouping hides the flat list's own pagination (see showFlatItemPagination), so `items` freezes
  // at whatever was loaded before grouping was toggled on and badly undercounts against the whole
  // job (an analyst dismissing every item of an 800-item job by channel saw "200 dismissed" here,
  // not 800). Falls back to scanning `items` for the flat list, where that's still the only data
  // available. Gated on groupByChannel, not just channelsQuery.data truthiness -
  // react-query keeps the last result around once the query's disabled (grouping toggled back
  // off), which would otherwise freeze this at a stale server total instead of resuming from
  // `items` as that list starts growing again via the flat list's own pagination.
  const classifiedCount = useMemo(() => {
    if (groupByChannel && channelsQuery.data) {
      return channelsQuery.data.results.reduce((sum, c) => sum + c.classified_count, 0);
    }

    return items.filter(
      (i) => i.submission_status === 'in hopper' || i.submission_status === 'sent to client'
    ).length;
  }, [groupByChannel, channelsQuery.data, items]);

  const dismissedCount = useMemo(() => {
    if (groupByChannel && channelsQuery.data) {
      return channelsQuery.data.results.reduce((sum, c) => sum + c.dismissed_count, 0);
    }

    return items.filter((i) => i.item_status === 'dismissed').length;
  }, [groupByChannel, channelsQuery.data, items]);

  // Everything currently known about each channel's items, merged from wherever it might have
  // come from: the flat `items` list (grown either by ordinary paging or by the smart/match-tier
  // prefetch effect above) and channelItemsByKey (an expanded channel's own direct fetch). Deduped
  // by item id, since the same item can legitimately land in both once a prefetch lands after a
  // channel was already expanded. This is "known", not "complete" - computeChannelDisplayCount
  // still checks a channel's own total before trusting it enough to hide anything.
  const knownChannelItemsByKey = useMemo(() => {
    const map = new Map<string, Map<string, Item>>();
    const add = (key: string, item: Item) => {
      let bucket = map.get(key);
      if (!bucket) {
        bucket = new Map();
        map.set(key, bucket);
      }

      bucket.set(item.id, item);
    };

    for (const item of items) {
      add(channelKeyOf(item), item);
    }

    for (const [key, bucketItems] of channelItemsByKey) {
      for (const item of bucketItems) {
        add(key, item);
      }
    }

    return map;
  }, [items, channelItemsByKey]);

  // Null while the selection's channel query hasn't resolved yet - channelGroups above stands in
  // meanwhile. `count`/`smartEmptied` come from
  // computeChannelDisplayCount, so they already fold in the client-side smart/match-tier filters
  // wherever knownChannelItemsByKey holds the whole channel, not just the triage toggles. `items`
  // here are whatever's been fetched into channelItemsByKey for that channel so far (see
  // ensureChannelItemsLoaded), empty until the analyst actually expands the group - unrelated to
  // knownChannelItemsByKey, which also folds in the flat `items` prefetch.
  const serverChannelResults = useMemo(() => {
    if (!channelsQuery.data) {
      return null;
    }

    return channelsQuery.data.results.map((c) => {
      const known = knownChannelItemsByKey.get(c.key);
      const {count, smartEmptied} = computeChannelDisplayCount(
        c,
        known ? [...known.values()] : [],
        {showClassified, showDismissed},
        {isSmartFilterRejected, isMatchTierRejected}
      );
      return {
        key: c.key,
        label: c.label,
        channelId: c.author_id,
        count,
        smartEmptied,
        items: channelItemsByKey.get(c.key) ?? []
      };
    });
  }, [
    channelsQuery.data,
    knownChannelItemsByKey,
    channelItemsByKey,
    showClassified,
    showDismissed,
    isSmartFilterRejected,
    isMatchTierRejected
  ]);

  // A channel triaged AND/OR smart/match-tier-filtered down to zero disappears from the list the
  // same way a fully-filtered-away group already does in the channelGroups fallback (built from
  // visibleItems, which is triage- and smart-filtered) - matching that is the whole point of
  // filtering on `count` here rather than just showing "0".
  const serverChannelGroups = useMemo<ChannelGroup[] | null>(() => {
    if (!serverChannelResults) {
      return null;
    }

    return serverChannelResults
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [serverChannelResults]);

  const displayChannelGroups = serverChannelGroups ?? channelGroups;

  // How many channels the smart filter alone emptied - "alone" meaning triage and the match-tier
  // filter aren't what's responsible (see computeChannelDisplayCount/countSmartHiddenChannels).
  // Computed per-channel above once the server aggregates land; until then it gets its own pass over
  // afterTriageItems instead. Gated on groupByChannel since it's only ever shown while grouped, so
  // an ungrouped job with thousands of items never pays for the fallback's own item-level pass.
  const smartHiddenChannelCount = useMemo(() => {
    if (!groupByChannel) {
      return 0;
    }

    if (serverChannelResults) {
      return serverChannelResults.filter((c) => c.smartEmptied).length;
    }

    return countSmartHiddenChannels(afterTriageItems, {isSmartFilterRejected, isMatchTierRejected});
  }, [
    groupByChannel,
    serverChannelResults,
    afterTriageItems,
    isSmartFilterRejected,
    isMatchTierRejected
  ]);

  // Job-mode grouping's headers and per-channel items come entirely from the server
  // (serverChannelGroups + ensureChannelItemsLoaded) - paging further into the background `items`
  // fetch changes nothing it displays, so the flat list's "Load more items"/infinite-scroll
  // sentinel would be dead weight (or actively confusing) below the channel groups.
  const showFlatItemPagination = !(groupByChannel && serverChannelGroups);

  // Whole-job dismissed/classified counts from the server - shown on a job's group headers
  // alongside the count, since smart/match-tier filtering (client-side, can't inform a server
  // count) already gets its own "N hidden by..." breakdown scoped to what's actually expanded.
  const channelStatsByKey = useMemo(() => {
    const map = new Map<
      string,
      {
        total: number;
        dismissedCount: number;
        classifiedCount: number;
        statusGroups: SubmissionStatusGroup[];
        flag: FlagColor | null;
        flagNote: string | null;
      }
    >();
    for (const c of channelsQuery.data?.results ?? []) {
      map.set(c.key, {
        total: c.total,
        dismissedCount: c.dismissed_count,
        classifiedCount: c.classified_count,
        statusGroups: c.submission_status_groups,
        flag: c.flag ?? null,
        flagNote: c.flag_note ?? null
      });
    }

    return map;
  }, [channelsQuery.data]);

  // The channel's own actor record, if one already exists locally - either scraped by the job, or
  // hydrated by an earlier "Classify channel" click this session. Looked up from `items` (not
  // visibleItems) so it's still found even if that record is itself already classified/dismissed.
  const actorItemByAuthorId = useMemo(() => {
    const map = new Map<string, Item>();
    for (const item of items) {
      if (item.content_type === 'actor' && item.author_id) {
        map.set(item.author_id, item);
      }
    }

    return map;
  }, [items]);

  // Destructured rather than depending on the whole useCheckChannelStatuses() result below -
  // TanStack Query v5's useMutation returns a fresh object literal every render, so depending on
  // it directly would make handleClassifyChannel (and everything memoized on it) unstable too.
  // mutateAsync itself is stable across renders.
  const {mutateAsync: checkChannelStatusesAsync} = useCheckChannelStatuses();
  const [hydratingChannelKey, setHydratingChannelKey] = useState<string | null>(null);

  // A video-only job never scrapes the channel as its own record, so there's nothing local to
  // classify yet. Fetch a live status (title/thumbnail/handle/country) to classify with real
  // channel data rather than just the id; if that lookup fails, fall back to the group's own
  // label instead of blocking the analyst from submitting at all.
  const handleClassifyChannel = useCallback(
    async (group: Pick<ChannelGroup, 'key' | 'channelId' | 'label'>) => {
      const {channelId} = group;
      const existing = channelId ? actorItemByAuthorId.get(channelId) : undefined;
      if (existing) {
        setClassifyItems([existing]);
        return;
      }

      if (!channelId) {
        return;
      }

      // The flat views have no pending state to show, so a repeat click while a lookup is in
      // flight is ignored rather than racing a second one.
      if (hydratingChannelKey) {
        return;
      }

      setHydratingChannelKey(group.key);
      try {
        const {results} = await checkChannelStatusesAsync([channelId]);
        setClassifyItems([buildActorItem(channelId, group.label, results[0])]);
      } catch {
        setClassifyItems([buildActorItem(channelId, group.label)]);
      } finally {
        setHydratingChannelKey(null);
      }
    },
    [actorItemByAuthorId, checkChannelStatusesAsync, hydratingChannelKey]
  );

  const toggleChannelExpanded = useCallback((key: string) => {
    setExpandedChannels((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  // A job's or folder's channel headers (serverChannelGroups) carry no items until this runs -
  // called from the expand click, once, per channel key. channelId (author_id) present: fetch that
  // channel's items from the server, scoped the same way the main list is, same filters. No
  // channelId (a handle- or name-keyed group - the API has no filter for those): fall back to
  // whatever's already in `items` for this channel, same "only what's loaded so far" limitation
  // groupItemsByChannel's own comment already documents.
  const ensureChannelItemsLoaded = useCallback(
    async (group: ChannelGroup) => {
      if (
        !channelAggregateScope ||
        channelItemsByKey.has(group.key) ||
        channelItemsLoadingKeysRef.current.has(group.key)
      ) {
        return;
      }

      const myGeneration = channelItemsGenerationRef.current;
      channelItemsLoadingKeysRef.current.add(group.key);
      setChannelItemsLoadingKeys((previous) => new Set(previous).add(group.key));
      setChannelItemsErrorKeys((previous) => {
        if (!previous.has(group.key)) {
          return previous;
        }

        const next = new Set(previous);
        next.delete(group.key);
        return next;
      });
      try {
        let loaded: Item[];
        let truncated = false;
        if (group.channelId) {
          const data = await itemsApi.listWithoutTotal({
            ...(channelAggregateScope.kind === 'folder'
              ? {folder_id: channelAggregateScope.id}
              : {job_id: channelAggregateScope.id}),
            author_id: group.channelId,
            page_size: CHANNEL_ITEMS_PAGE_SIZE,
            ...channelFilters,
            ...(sortOrder === DEFAULT_ITEM_SORT ? {} : {sort: sortOrder})
          });
          loaded = data.results;
          truncated = data.has_more;
        } else {
          loaded = items.filter((item) => channelKeyOf(item) === group.key);
        }

        // Filters (or the selection) changed while this was in flight - a newer request, if any,
        // now owns this key's loading/cache/error state. Dropping this response entirely (rather
        // than writing it anyway) is what actually fixes the staleness this cache exists to avoid.
        if (channelItemsGenerationRef.current !== myGeneration) {
          return;
        }

        setChannelItemsByKey((previous) => new Map(previous).set(group.key, loaded));
        setChannelItemsTruncatedKeys((previous) => {
          if (!truncated) {
            return previous;
          }

          return new Set(previous).add(group.key);
        });
      } catch {
        if (channelItemsGenerationRef.current !== myGeneration) {
          return;
        }

        // Left out of channelItemsByKey on purpose - the has() guard above then lets a retry click
        // (or the auto-reload effect, once anything else changes) fetch again instead of caching
        // the failure as if it were an empty channel.
        setChannelItemsErrorKeys((previous) => new Set(previous).add(group.key));
      } finally {
        if (channelItemsGenerationRef.current === myGeneration) {
          channelItemsLoadingKeysRef.current.delete(group.key);
          setChannelItemsLoadingKeys((previous) => {
            const next = new Set(previous);
            next.delete(group.key);
            return next;
          });
        }
      }
    },
    [channelAggregateScope, channelItemsByKey, items, channelFilters, sortOrder]
  );

  // Expand state left over from a previous job's/folder's channel ids/handles would otherwise make
  // a freshly-opened selection look like it already has channels expanded that just happen to share
  // a key. The fetched-items cache is keyed the same way, so it's just as stale across a switch.
  useEffect(() => {
    channelItemsGenerationRef.current += 1;
    setExpandedChannels(new Set());
    setRenderedChannelGroupCount(CHANNEL_GROUPS_PAGE_SIZE);
    setChannelItemsByKey(new Map());
    setChannelItemsTruncatedKeys(new Set());
    setChannelItemsErrorKeys(new Set());
    setChannelItemsLoadingKeys(new Set());
    channelItemsLoadingKeysRef.current = new Set();
  }, [selected?.id, selectedFolder?.id]);

  // The channelsQuery key includes channelFilters, so a filter/search change already refetches
  // the header counts - but ensureChannelItemsLoaded caches an expanded group's items by channel
  // key alone, with no filters in that key. Without this, changing a filter with a group open left
  // it rendering items fetched under the old filters, silently disagreeing with its own now-current
  // header count. Doesn't touch expandedChannels - a filter change shouldn't collapse what the
  // analyst has open, just what's inside it (the effect below reloads it). A sort change needs the
  // same reload, or an open group keeps its old order.
  useEffect(() => {
    channelItemsGenerationRef.current += 1;
    setChannelItemsByKey(new Map());
    setChannelItemsTruncatedKeys(new Set());
    setChannelItemsErrorKeys(new Set());
    setChannelItemsLoadingKeys(new Set());
    channelItemsLoadingKeysRef.current = new Set();
  }, [channelFilters, sortOrder]);

  // Reloads whatever's both expanded and missing from the cache - covers the filter-change reset
  // above, and is otherwise a no-op since the expand click already calls ensureChannelItemsLoaded
  // directly (itself a no-op once cached or already in flight).
  useEffect(() => {
    if (!serverChannelGroups) {
      return;
    }

    for (const group of serverChannelGroups) {
      if (expandedChannels.has(group.key) && !channelItemsByKey.has(group.key)) {
        void ensureChannelItemsLoaded(group);
      }
    }
  }, [serverChannelGroups, expandedChannels, channelItemsByKey, ensureChannelItemsLoaded]);

  const allVisibleSelected = useMemo(
    () => visibleItems.length > 0 && visibleItems.every((i) => selectedItemIds.has(i.id)),
    [visibleItems, selectedItemIds]
  );

  // In job-mode grouping, `visibleItems` is just the flat background page - disjoint from what's
  // actually on screen (the expanded groups' own channelItemsByKey items), since that list's own
  // pagination is hidden (see showFlatItemPagination). "Select all N visible items" would seed a
  // selection - and the bulk bar's Dismiss would then act on it - from items the analyst never saw
  // and the button's own label doesn't describe, so it's unavailable here rather than wrong.
  const selectAllUnavailableInGrouping = groupByChannel && Boolean(serverChannelGroups);

  // Everything client-side knows about right now, merging the flat page-fetched `items` with
  // whatever's been fetched into each expanded channel group (channelItemsByKey) - a card can be
  // rendered from either source (renderItems is shared by both), so per-item actions (dismiss/
  // restore, resolving a bulk selection for Classify/Translate) need to find it regardless of which
  // one it came from. `items` wins on a shared id - it's the one path with server-driven
  // pagination state (has_more, item_count) that other effects key off, so it's the more
  // authoritative copy where both happen to have loaded the same item.
  const allKnownItemsById = useMemo(() => {
    const map = new Map<string, Item>();
    for (const list of channelItemsByKey.values()) {
      for (const item of list) {
        map.set(item.id, item);
      }
    }

    for (const item of items) {
      map.set(item.id, item);
    }

    return map;
  }, [items, channelItemsByKey]);
  // Read by the status handlers below at click time, through a ref so they don't change identity
  // whenever `items` does. They reach every ItemCard/ItemListRow as onDismiss, so depending on the
  // map directly broke every card's memo on each prefetch round and page load (GT-16811).
  const allKnownItemsByIdRef = useRef(allKnownItemsById);
  useEffect(() => {
    allKnownItemsByIdRef.current = allKnownItemsById;
  }, [allKnownItemsById]);

  // Optimistic per-item write shared by the status and flag handlers and their error rollbacks -
  // applies to both `items` and every channelItemsByKey bucket, since a change needs to show up
  // wherever the affected card is actually rendered from.
  const patchItems = useCallback((patchById: ReadonlyMap<string, Partial<Item>>) => {
    setItems((previous) => {
      let changed = false;
      const next = previous.map((item) => {
        const itemPatch = patchById.get(item.id);
        if (!itemPatch) {
          return item;
        }

        changed = true;
        return {...item, ...itemPatch};
      });
      return changed ? next : previous;
    });
    setChannelItemsByKey((previous) => {
      let changedAny = false;
      const next = new Map(previous);
      for (const [key, list] of previous) {
        let changed = false;
        const newList = list.map((item) => {
          const itemPatch = patchById.get(item.id);
          if (!itemPatch) {
            return item;
          }

          changed = true;
          return {...item, ...itemPatch};
        });
        if (changed) {
          next.set(key, newList);
          changedAny = true;
        }
      }

      return changedAny ? next : previous;
    });
  }, []);

  const applyItemStatuses = useCallback(
    (statusById: ReadonlyMap<string, Item['item_status']>) => {
      patchItems(
        new Map([...statusById].map(([id, status]) => [id, {item_status: status ?? null}]))
      );
    },
    [patchItems]
  );

  // Resolves the current selection against allKnownItemsById rather than visibleItems - a checkbox
  // inside an expanded channel group is on a card whose item lives in channelItemsByKey, not
  // `items`, so filtering visibleItems (items-derived) against selectedItemIds silently drops it.
  const selectedItems = useMemo(
    () =>
      [...selectedItemIds]
        .map((id) => allKnownItemsById.get(id))
        .filter((item): item is Item => item !== undefined),
    [selectedItemIds, allKnownItemsById]
  );

  const copySelectedIdsRef = useRef<HTMLButtonElement>(null);

  // Ctrl/Cmd+C copies the selected items' platform ids, but only when it wouldn't shadow a normal
  // copy - an analyst highlighting a title, or typing in the search box or a dialog field, still
  // gets native copy behaviour. Presses the bulk bar's own Copy IDs button (which shows whenever
  // something is selected), so the shortcut lights it up as the confirmation instead of needing
  // separate UI.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCopyChord = (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'c';
      if (!isCopyChord || selectedItems.length === 0) {
        return;
      }

      const {target} = e;
      const isEditable =
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (isEditable || (globalThis.getSelection()?.toString() ?? '') !== '') {
        return;
      }

      e.preventDefault();
      copySelectedIdsRef.current?.click();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedItems]);

  // Dismissing/classifying items, or smart filtering enforcing the job's exclusions, can leave the
  // current pages mostly filtered out. Keep fetching a few more pages until a full page is visible
  // again, instead of leaving the analyst to click "Load more" repeatedly just to see items that
  // were already collected. Bounded by MAX_AUTO_LOAD_ROUNDS: a heavily-triaged job (most items
  // hidden by showClassified/showDismissed, both false by default) can never reach a full visible
  // page, so without a cap this would chain-fetch every remaining page on every open.
  useEffect(() => {
    if (
      visibleItems.length < PAGE_SIZE &&
      items.length > 0 &&
      itemHasMore &&
      !loadingMore &&
      !loadingItems &&
      autoLoadRounds < MAX_AUTO_LOAD_ROUNDS &&
      !loadingAllForGrouping
    ) {
      setAutoLoadRounds((rounds) => rounds + 1);
      void loadMore();
    }
  }, [
    visibleItems.length,
    items.length,
    itemHasMore,
    loadingMore,
    loadingItems,
    loadingAllForGrouping,
    autoLoadRounds,
    loadMore
  ]);

  // Scrolling can't get anywhere, so the sentinel stops being rendered and the analyst gets the
  // manual "Load more" button further down as the escape hatch. Two ways to get here:
  //
  // - The short-page effect above spent its budget without reaching a full visible page.
  // - Scrolling stalled (see the observer below).
  //
  // Client-side triage/smart filtering never refetches, so nothing resets either on its own.
  //
  // Grouping before serverChannelGroups has loaded still forces this: channelGroups is derived from visibleItems, so visibleItems.length stops tracking
  // rendered height once groups start collapsing (`expanded ? renderItems(...) : null`) - a page
  // landing wholly in still-collapsed groups grows the count without growing the list, which reads
  // as scroll progress and would chain-fetch every remaining page. Job-mode grouping doesn't need
  // this: the pagination trigger below is hidden outright once serverChannelGroups exists, since
  // that mode's counts and items come from the server, not from paging through `items`.
  const autoLoadExhausted =
    (groupByChannel && !serverChannelGroups) ||
    autoLoadStalled ||
    (visibleItems.length < PAGE_SIZE && autoLoadRounds >= MAX_AUTO_LOAD_ROUNDS);

  // Clears the stall as soon as anything makes the list taller again - a manual "Load more" click,
  // or the analyst toggling showClassified/showDismissed to reveal rows - so scrolling takes over
  // again rather than the button being a one-way door for the rest of the session.
  useEffect(() => {
    if (autoLoadStalled && visibleItems.length !== lastAutoLoadVisibleCountRef.current) {
      setAutoLoadStalled(false);
    }
  }, [autoLoadStalled, visibleItems.length]);

  // Auto-loads the next page of items once the sentinel at the bottom of the list scrolls into
  // view, rather than requiring a "Load more" click.
  useEffect(() => {
    const sentinel = itemsSentinelRef.current;
    if (!sentinel || !itemHasMore || autoLoadExhausted) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting || loadingMore || loadingAllForGrouping) {
        return;
      }

      // A page whose every row is hidden by client-side filtering (triage, smart filtering) adds no
      // height, so the sentinel stays intersecting and the observer recreated on the next render
      // fires again straight away. Without this, a large job bursts through every remaining page -
      // each one a full page query - with no scrolling at all. Stalling
      // here costs exactly one wasted fetch: the previous fire is what recorded this count, and the
      // effect above un-stalls the moment anything grows. Only sound because grouping mode is
      // excluded above - there, a growing count doesn't imply a growing list.
      if (visibleItems.length === lastAutoLoadVisibleCountRef.current) {
        setAutoLoadStalled(true);
        return;
      }

      lastAutoLoadVisibleCountRef.current = visibleItems.length;
      void loadMore();
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [
    itemHasMore,
    autoLoadExhausted,
    visibleItems.length,
    loadingMore,
    loadingAllForGrouping,
    loadMore
  ]);

  // Destructured (mutate/mutateAsync are stable across renders) rather than depending on the whole
  // mutation object below - same reasoning as checkChannelStatusesAsync above: useMutation returns
  // a fresh object every render, which would make setItemStatus/bulkSetItemStatus/
  // handleSetChannelStatus (and renderItems, ItemCard/ItemListRow's memo bailout with them)
  // unstable too.
  const {mutate: setItemStatusMutate} = useSetItemStatus();
  const {mutate: bulkSetItemStatusMutate} = useBulkSetItemStatus();
  const {mutate: bulkSetItemStatusByAuthorMutate} = useBulkSetItemStatusByAuthor();

  // One PATCH per MAX_BULK_ITEM_IDS-sized chunk (BulkSetJobItemStatus / BulkSetFolderItemStatus)
  // instead of one per item - shared by the floating bulk-selection bar's Dismiss button, "Dismiss
  // channel" below, and setItemStatus's own folder-scoped single-item path (there is no single-item
  // folder endpoint, only the bulk one). Defined ahead of setItemStatus because it calls into this.
  // Each chunk rolls back only its own ids on its own failure - one bad chunk shouldn't revert ids a
  // sibling chunk already committed.
  const bulkSetItemStatus = useCallback(
    (itemIds: readonly string[], status: 'active' | 'dismissed') => {
      if (!channelAggregateScope || itemIds.length === 0) {
        return;
      }

      const scope = channelAggregateScope;
      // Only the ids actually being changed, not every loaded item - this used to snapshot all of
      // `items` on every dismiss (per-item or "Dismiss channel"), which mattered once that array
      // could hold thousands of rows. Checked against allKnownItemsById so an id selected from
      // inside an expanded channel group (only in channelItemsByKey) still gets a real prior status
      // to roll back to on failure, instead of silently falling back to null.
      const priorStatuses = new Map(
        itemIds
          .map((id) => [id, allKnownItemsByIdRef.current.get(id)?.item_status] as const)
          .filter((entry): entry is [string, Item['item_status']] => entry[1] !== undefined)
      );
      applyItemStatuses(new Map(itemIds.map((id) => [id, status])));

      for (let start = 0; start < itemIds.length; start += MAX_BULK_ITEM_IDS) {
        const chunk = itemIds.slice(start, start + MAX_BULK_ITEM_IDS);
        bulkSetItemStatusMutate(
          {scope, itemIds: chunk, status},
          {
            onError(error) {
              applyItemStatuses(new Map(chunk.map((id) => [id, priorStatuses.get(id) ?? null])));
              showToast(
                getApiErrorMessage(
                  error,
                  `Failed to ${status === 'dismissed' ? 'dismiss' : 'restore'} items.`
                ),
                'error'
              );
            }
          }
        );
      }
    },
    [channelAggregateScope, applyItemStatuses, bulkSetItemStatusMutate]
  );

  // The analyst's own flag, set straight away and independently of classifying - it is on the item,
  // so any submission of it shows the same flag without anything being copied. Unlike dismiss it
  // isn't scoped to a job, so it works in every view. Items the SPA built itself (a
  // "Classify channel" actor) have no server row and are skipped rather than failing their chunk.
  const setItemFlags = useCallback(
    (itemIds: readonly string[], color: FlagColor | null) => {
      const priorFlags = new Map<string, FlagColor | null>();
      for (const id of itemIds) {
        const flag = allKnownItemsByIdRef.current.get(id)?.flag;
        if (flag !== undefined) {
          priorFlags.set(id, flag);
        }
      }

      const ids = [...priorFlags.keys()];
      if (ids.length === 0) {
        return;
      }

      patchItems(new Map(ids.map((id) => [id, {flag: color}])));
      for (let start = 0; start < ids.length; start += ITEM_FLAGS_MAX_IDS) {
        const chunk = ids.slice(start, start + ITEM_FLAGS_MAX_IDS);
        itemsApi.setFlags(chunk, color).catch((error: unknown) => {
          patchItems(new Map(chunk.map((id) => [id, {flag: priorFlags.get(id) ?? null}])));
          showToast(
            getApiErrorMessage(error, color ? 'Failed to flag items.' : 'Failed to clear flags.'),
            'error'
          );
        });
      }
    },
    [patchItems]
  );

  const setItemStatus = useCallback(
    (itemId: string, status: 'active' | 'dismissed') => {
      // Dismiss is per-job/per-folder (job_items.status), so there is nothing to write without one.
      if (!channelAggregateScope) {
        return;
      }

      // Checked against allKnownItemsById, not just `items` - a per-card dismiss/restore click
      // inside an expanded channel group is on an item that only lives in channelItemsByKey.
      const priorItem = allKnownItemsByIdRef.current.get(itemId);
      if (!priorItem) {
        return;
      }

      if (channelAggregateScope.kind === 'folder') {
        // No single-item folder endpoint - BulkSetFolderItemStatus is the only folder-scoped write,
        // so a one-item id list is the same request the selection bar's Dismiss already sends.
        bulkSetItemStatus([itemId], status);
        return;
      }

      applyItemStatuses(new Map([[itemId, status]]));
      setItemStatusMutate(
        {jobId: channelAggregateScope.id, itemId, status},
        {
          onError(error) {
            applyItemStatuses(new Map([[itemId, priorItem.item_status]]));
            showToast(
              getApiErrorMessage(
                error,
                `Failed to ${status === 'dismissed' ? 'dismiss' : 'restore'} item.`
              ),
              'error'
            );
          }
        }
      );
    },
    [channelAggregateScope, applyItemStatuses, setItemStatusMutate, bulkSetItemStatus]
  );

  // Job mode with a known author_id: one PATCH by author_id, no need for the channel's items to
  // already be loaded (the whole point of not bulk-fetching them anymore). A group with no author_id to key on (handle/name/"unknown"): falls back to the id-list PATCH
  // over whatever's actually loaded for that group, same as before serverChannelGroups existed.
  // Shared by "Dismiss channel" and "Restore channel" (the same button once every item in the
  // channel is already dismissed and "Show dismissed" is revealing it) - restoring is the same
  // whole-channel operation with the opposite status.
  const handleSetChannelStatus = useCallback(
    (group: ChannelGroup, status: 'active' | 'dismissed', visibleGroupItems: readonly Item[]) => {
      // The author_id fast path applies to every item this job has from the channel, server-side,
      // with no filters of its own - correct only while no SERVER-side filter is narrowing what's
      // shown (channelFiltersActive false; the client-side smart/match-tier filters deliberately
      // don't count, see its definition). With a search/filter active, falling through to the
      // id-list path below (whatever's actually loaded for this channel - itself fetched with the
      // same channelFilters, see ensureChannelItemsLoaded) keeps the action scoped to what's
      // visible, the same way every other filtered action in this view already is.
      //
      // Dismissing (not restoring) also needs showClassified itself checked, separately from
      // channelFiltersActive: the header count already excludes a channel's classified items
      // when showClassified is off, so the fast path dismissing them anyway - including ones
      // already `in hopper`/`sent to client` - would silently affect items the analyst can't see
      // and the old id-list path (built from triage-filtered visibleItems) never touched. Doesn't
      // apply to restoring: bringing every item in a fully-dismissed channel back is the whole
      // point of that action regardless of what's currently shown.
      const stats = channelStatsByKey.get(group.key);
      const classifiedSafeForFastPath =
        status !== 'dismissed' || showClassified || !stats || stats.classifiedCount === 0;

      if (
        channelAggregateScope &&
        serverChannelGroups &&
        group.channelId &&
        !channelFiltersActive &&
        classifiedSafeForFastPath
      ) {
        // Every id this client currently knows about for this channel - both whatever's cached in
        // channelItemsByKey (if expanded) and anything in the flat `items` page that happens to
        // belong to the same channel - so toggling grouping off right after doesn't show those
        // items with a stale status until the job/folder is reselected. Captured (with prior
        // status) before the optimistic update below so a failed PATCH can put everything back
        // exactly as it was.
        const knownIds = new Set<string>();
        for (const item of channelItemsByKey.get(group.key) ?? []) {
          knownIds.add(item.id);
        }

        for (const item of items) {
          if (channelKeyOf(item) === group.key) {
            knownIds.add(item.id);
          }
        }

        const priorStatuses = new Map(
          [...knownIds].map((id) => [id, allKnownItemsById.get(id)?.item_status ?? null])
        );

        bulkSetItemStatusByAuthorMutate(
          {scope: channelAggregateScope, authorId: group.channelId, status},
          {
            onError(error) {
              applyItemStatuses(priorStatuses);
              showToast(
                getApiErrorMessage(
                  error,
                  `Failed to ${status === 'dismissed' ? 'dismiss' : 'restore'} channel.`
                ),
                'error'
              );
            }
          }
        );
        applyItemStatuses(new Map([...knownIds].map((id) => [id, status])));
        return;
      }

      // Job/folder mode without the author-path available (no author_id to key on, or a
      // filter/search means its unfiltered semantics would affect more than what's shown): acts on
      // visibleGroupItems, not the raw group.items/channelItemsByKey bucket - that bucket is
      // whatever the server/flat-list fetch returned, before the client-side triage/smart-filter/
      // match-tier predicates the group's own body renders through, so using it here would affect
      // items the analyst can't actually see under the current filters (the same problem this
      // fallback exists to avoid). Empty until the group is expanded; the button disables itself
      // until then (see its `disabled`/`title` above) - this is a backstop, not the primary guard.
      if (channelAggregateScope && serverChannelGroups && visibleGroupItems.length === 0) {
        return;
      }

      bulkSetItemStatus(
        visibleGroupItems.map((i) => i.id),
        status
      );
    },
    [
      channelAggregateScope,
      serverChannelGroups,
      channelFiltersActive,
      channelStatsByKey,
      showClassified,
      channelItemsByKey,
      items,
      allKnownItemsById,
      applyItemStatuses,
      bulkSetItemStatusByAuthorMutate,
      bulkSetItemStatus
    ]
  );

  // Scoped to the current selection, same as Classify/Dismiss right next to it in the bulk bar -
  // "translate everything on this page regardless of selection" was inconsistent with a bar that
  // only appears once something is selected and whose other two actions both act on selectedItemIds.
  // "Select all N visible items" above still makes a whole-page translate one click away. A
  // whole-investigation run over thousands of items would need an async job (see the plan's
  // "Deliberately not doing" section); this covers what an analyst has actually selected,
  // synchronously, with no new service or job-status table.
  const handleTranslateSelected = async () => {
    setConfirmingTranslateSelected(false);
    setTranslatingSelected(true);
    try {
      // Resolved against allKnownItemsById (selectedItems above), not visibleItems - a selection
      // made inside an expanded channel group is on an item that only lives in channelItemsByKey.
      // Skip items already declared English (YouTube's defaultAudioLanguage/defaultLanguage) - a
      // bulk run over many items is exactly where translating en->en repeatedly adds up in real
      // Cloud Translation spend. The per-item toggle deliberately stays available regardless:
      // that metadata is known to be unreliable in both directions (getAutoSelectedLanguageCode's
      // own comment - a creator's account locale, not necessarily the video's actual language), so
      // an analyst who suspects a specific item is mistagged can still force-translate it by hand.
      const entries = selectedItems
        .filter((item) => getDeclaredLanguageSubtag(item.metadata) !== 'en')
        .map((item) => ({
          itemKey: item.id,
          texts: getTranslatableSegments(item.title, item.metadata)
        }))
        .filter((entry) => entry.texts.length > 0);

      // Chunked client-side (same reasoning as bulkSetItemStatus above) so a large page still fits
      // comfortably under TranslationsController's own per-request segment/character caps. Bounded
      // by item count, accumulated character count, AND accumulated segment count - each item
      // contributes title + description + one segment per tag, so an item-count-only cap can still
      // assemble a chunk past TranslationsController.MaxSegmentsPerRequest (4000, e.g. 200 items
      // averaging ~20 tags) or MaxTotalCharsPerRequest (1,000,000, e.g. long descriptions) and 400.
      // Splitting on whichever limit hits first (same shape as TranslationService.ChunkForApiLimits)
      // makes that unreachable by construction instead of by how much text/how many tags items
      // happen to carry today.
      const TRANSLATE_SELECTED_MAX_ITEMS_PER_CHUNK = 200;
      const TRANSLATE_SELECTED_MAX_CHARS_PER_CHUNK = 300_000;
      const TRANSLATE_SELECTED_MAX_SEGMENTS_PER_CHUNK = 4000;

      const chunks: Array<typeof entries> = [];
      let chunk: typeof entries = [];
      let charsInChunk = 0;
      let segmentsInChunk = 0;
      for (const entry of entries) {
        const entryChars = entry.texts.reduce((sum, t) => sum + t.length, 0);
        if (
          chunk.length > 0 &&
          (chunk.length >= TRANSLATE_SELECTED_MAX_ITEMS_PER_CHUNK ||
            charsInChunk + entryChars > TRANSLATE_SELECTED_MAX_CHARS_PER_CHUNK ||
            segmentsInChunk + entry.texts.length > TRANSLATE_SELECTED_MAX_SEGMENTS_PER_CHUNK)
        ) {
          chunks.push(chunk);
          chunk = [];
          charsInChunk = 0;
          segmentsInChunk = 0;
        }

        chunk.push(entry);
        charsInChunk += entryChars;
        segmentsInChunk += entry.texts.length;
      }

      if (chunk.length > 0) {
        chunks.push(chunk);
      }

      for (const c of chunks) {
        await translateMany(c);
      }
    } finally {
      setTranslatingSelected(false);
    }
  };

  // Stable across renders (useCallback, no per-item closures) so ItemCard/ItemListRow's memo can
  // actually bail out - a fresh closure per item per render defeats memo regardless of how cheap
  // the comparison is.
  const toggleItemChecked = useCallback((id: string) => {
    setSelectedItemIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const handleClassifyItem = useCallback((item: Item) => {
    setClassifyItems([item]);
  }, []);

  const handleClassifyItemChannel = useCallback(
    (item: Item) => {
      if (!item.author_id) {
        return;
      }

      void handleClassifyChannel({
        key: channelKeyOf(item),
        channelId: item.author_id,
        label: item.author_name ?? item.author_handle ?? item.author_id
      });
    },
    [handleClassifyChannel]
  );

  const handleShowItemDetails = useCallback((item: Item, siblings: readonly Item[]) => {
    setDetailsItem(item);
    setDetailsItems(siblings);
  }, []);

  const handleSetItemFlag = useCallback(
    (item: Item, color: FlagColor | null) => {
      setItemFlags([item.id], color);
    },
    [setItemFlags]
  );

  const handleToggleItemDismiss = useCallback(
    (item: Item) => {
      setItemStatus(item.id, item.item_status === 'dismissed' ? 'active' : 'dismissed');
    },
    [setItemStatus]
  );

  // Shared between the plain list and each collapsible channel section below, so grid/list mode
  // and every row action stay identical regardless of whether the items came from one channel
  // or all of them.
  const renderItems = useCallback(
    (itemsToRender: readonly Item[]) => (
      <ItemsCollection
        items={itemsToRender}
        view={view}
        gridClassName={cn('grid gap-3', GRID_COLS_BY_SIZE[thumbnailSize])}
        matchTiers={imageMatchTiersByVideoId}
        selectedItemIds={selectedItemIds}
        onToggleCheck={toggleItemChecked}
        onToggleAll={(select) => {
          setSelectedItemIds(select ? new Set(itemsToRender.map((i) => i.id)) : new Set());
        }}
        onClassify={handleClassifyItem}
        onClassifyChannel={handleClassifyItemChannel}
        onSetFlag={handleSetItemFlag}
        onDismiss={handleToggleItemDismiss}
        onShowDetails={handleShowItemDetails}
      />
    ),
    [
      handleShowItemDetails,
      view,
      thumbnailSize,
      imageMatchTiersByVideoId,
      selectedItemIds,
      handleClassifyItem,
      handleClassifyItemChannel,
      handleSetItemFlag,
      handleToggleItemDismiss,
      toggleItemChecked
    ]
  );

  const deleteJob = async (job: Job) => {
    try {
      await deleteJobMut.mutateAsync(job.id);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Could not delete investigation.'), 'error');
      return;
    }

    setJobs((previous) => previous.filter((j) => j.id !== job.id));
    if (selected?.id === job.id) {
      setSelected(null);
      setItems([]);
    }

    refreshFolderCounts();
    showToast(`Deleted ${jobDisplayLabel(job)}.`);
  };

  const handleRerun = async () => {
    if (!selected || rerunning) {
      return;
    }

    setRerunning(true);
    setRerunError(null);
    try {
      // Re-run reuses the same job id (no sibling job is created) — swap in the updated status/timestamps
      // and re-select it so the item list refreshes; newly collected items surface via "new only".
      const job = await rerunJobMut.mutateAsync(selected.id);
      setJobs((previous) => previous.map((j) => (j.id === job.id ? job : j)));
      await selectJob(job);
    } catch (error) {
      setRerunError(getApiErrorMessage(error, 'Could not re-run investigation.'));
    } finally {
      setRerunning(false);
    }
  };

  const handleRerunJob = async (job: Job) => {
    if (rerunning) {
      return;
    }

    setRerunning(true);
    if (selected?.id === job.id) {
      setRerunError(null);
    }

    try {
      const updated = await rerunJobMut.mutateAsync(job.id);
      setJobs((previous) => previous.map((j) => (j.id === updated.id ? updated : j)));
      if (selected?.id === updated.id) {
        await selectJob(updated);
      } else {
        showToast(`Re-running ${jobDisplayLabel(updated)}.`);
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Could not re-run investigation.');
      // The main column's alert belongs to the selected investigation, not this row's.
      if (selected?.id === job.id) {
        setRerunError(message);
      } else {
        showToast(message, 'error');
      }
    } finally {
      setRerunning(false);
    }
  };

  // "Re-run all" for a folder: fires JobsController.RerunJob once per investigation the folder
  // currently holds, in parallel - the same shape handleMoveJobs already uses for the folder-wide
  // setFolder loop above, rather than an endpoint of its own. Already-running/pending investigations
  // are skipped (RerunJob itself 409s on those) so a partial re-run doesn't read as a failure. One
  // toast reports how many actually fired rather than one per investigation.
  const handleRerunAll = async () => {
    if (!selectedFolder || rerunning) {
      return;
    }

    setRerunning(true);
    setRerunError(null);
    // The folder's subtree loads in the background when it is selected. Wait for that load (or retry
    // a failed one) rather than re-running only the part of the folder that has arrived so far.
    let folderJobs = selectedFolderJobs;
    if (selectedFolderIncomplete) {
      const fetched = await loadScope({kind: 'subtree', id: selectedFolder.id}, 'all');
      if (scopePagesRef.current.get(scopeKey({kind: 'subtree', id: selectedFolder.id}))?.failed) {
        setRerunError('Could not load every investigation in this folder - try again.');
        setRerunning(false);
        return;
      }

      const byId = new Map([...selectedFolderJobs, ...fetched].map((j) => [j.id, j]));
      folderJobs = [...byId.values()];
    }

    const toRerun = folderJobs.filter((j) => j.status !== 'pending' && j.status !== 'running');
    if (toRerun.length === 0) {
      setRerunning(false);
      return;
    }

    try {
      const results = await Promise.allSettled(
        toRerun.map(async (job) => rerunJobMut.mutateAsync(job.id))
      );
      const succeeded = results.filter(
        (r): r is PromiseFulfilledResult<Job> => r.status === 'fulfilled'
      );
      if (succeeded.length > 0) {
        const byId = new Map(succeeded.map((r) => [r.value.id, r.value]));
        setJobs((previous) => previous.map((j) => byId.get(j.id) ?? j));
      }

      const failedCount = results.length - succeeded.length;
      if (failedCount > 0) {
        setRerunError(
          `Re-ran ${succeeded.length} of ${results.length} investigations - ${failedCount} failed.`
        );
      } else {
        showToast(`Re-ran ${succeeded.length} investigation${succeeded.length === 1 ? '' : 's'}.`);
      }

      // Refreshes the aggregate view's item list/counts now that jobs are pending again.
      await selectFolder(selectedFolder);
    } finally {
      setRerunning(false);
    }
  };

  // What the header names: the investigation by its search, a folder by its name.
  const headerTitle = selected ? jobDisplayLabel(selected) : selectedFolder?.name;

  const refreshSelection = () => {
    if (selected) {
      void selectJob(selected);
    } else if (selectedFolder) {
      void selectFolder(selectedFolder);
    }
  };

  // The Move to folder… submenu, shared by a card's menus in the rail and the header's dots menu.
  // The job's own folder is shown checked and can't be picked.
  const renderMoveToFolderSub = (
    menu: RailMenu,
    job: Job,
    moveTo: (folderId: string | null) => void
  ) => (
    <menu.Sub>
      <menu.SubTrigger>
        <FolderInput className="h-3.5 w-3.5" />
        Move to folder…
      </menu.SubTrigger>
      <menu.SubContent className="max-h-80 w-56 overflow-y-auto">
        <menu.Item
          disabled={job.folder_id === null}
          onClick={() => {
            moveTo(null);
          }}>
          <span className="flex-1">Unfiled</span>
          {job.folder_id === null ? <Check className="size-3.5" /> : null}
        </menu.Item>
        {folders.length > 0 ? <menu.Separator /> : null}
        {flattenFolders(folders).map(({folder, depth}) => (
          <menu.Item
            key={folder.id}
            disabled={job.folder_id === folder.id}
            onClick={() => {
              moveTo(folder.id);
            }}>
            <span
              className="flex min-w-0 flex-1 items-center gap-1.5"
              style={{paddingLeft: depth * 12}}>
              <FolderIcon className="size-3.5 shrink-0" />
              <span className="truncate">{folder.name}</span>
            </span>
            {job.folder_id === folder.id ? <Check className="size-3.5" /> : null}
          </menu.Item>
        ))}
      </menu.SubContent>
    </menu.Sub>
  );

  // Re-run / Move to folder… / Delete. Re-run has no other home in the rail; Move to folder… is
  // the keyboard and right-click route to what dragging a card does, and moves the whole checked
  // set when the card is one of several checked, exactly as a drag would. There is no Rename: an
  // investigation is named by what it searched for, so only a folder renames.
  const renderJobMenuItems = (menu: RailMenu, job: Job) => (
    <>
      <menu.Item
        disabled={rerunning || job.status === 'running' || job.status === 'pending'}
        onClick={() => void handleRerunJob(job)}>
        <Play className="h-3.5 w-3.5" />
        Re-run
      </menu.Item>
      <menu.Separator />
      {renderMoveToFolderSub(menu, job, (folderId) => {
        const ids = checkedJobs.has(job.id) && checkedJobs.size > 1 ? [...checkedJobs] : [job.id];
        setCheckedJobs(new Set());
        void handleMoveJobs(ids, folderId);
      })}
      <menu.Separator />
      <menu.Item
        variant="destructive"
        onClick={() => {
          setPendingDelete({kind: 'investigation', job});
        }}>
        Delete investigation
      </menu.Item>
    </>
  );

  // The design's folder menu, with "Add subfolder" where the old menu had "New folder here" and
  // the folder's New investigation moved in here from its own row button.
  const renderFolderMenuItems = (menu: RailMenu, folder: Folder) => (
    <>
      <menu.Item
        onClick={(e) => {
          e.stopPropagation();
          setNewInvestigationIn(folder.id);
        }}>
        <Plus className="h-3.5 w-3.5" />
        New investigation
      </menu.Item>
      <menu.Separator />
      <menu.Item
        onClick={(e) => {
          startRenameFolder(e, folder);
        }}>
        <Pencil className="h-3.5 w-3.5" />
        Rename
      </menu.Item>
      <menu.Item
        onClick={(e) => {
          e.stopPropagation();
          setShareTarget({kind: 'folder', id: folder.id, label: folder.name});
        }}>
        <Share2 className="h-3.5 w-3.5" />
        Share…
      </menu.Item>
      <menu.Item
        onClick={(e) => {
          e.stopPropagation();
          setNewFolderParentId(folder.id);
          setShowNewFolder(true);
          setFolderError(null);
        }}>
        <FolderPlus className="h-3.5 w-3.5" />
        Add subfolder
      </menu.Item>
      <menu.Separator />
      <menu.Item
        variant="destructive"
        onClick={(e) => {
          e.stopPropagation();
          setPendingDelete({
            kind: 'folder',
            folder,
            hasSubfolders: folders.some((f) => f.parent_folder_id === folder.id)
          });
        }}>
        Delete folder
      </menu.Item>
    </>
  );

  // Unfiled is a system folder: it can take a new investigation but cannot be renamed, shared,
  // nested or deleted.
  const renderUnfiledMenuItems = (menu: RailMenu) => (
    <menu.Item
      onClick={(e) => {
        e.stopPropagation();
        setNewInvestigationIn(null);
      }}>
      <Plus className="h-3.5 w-3.5" />
      New investigation
    </menu.Item>
  );

  // An investigation is a card, per the design's folder tree: the status icon and name on top,
  // then what it collected and when, then who ran it. The design's second stat is total watch
  // time, which the API does not report, so the card shows the run's age in its place.
  const renderJobRow = (job: Job, flatIdx: number) => (
    <InvestigationTreeRow
      key={job.id}
      job={job}
      isSelected={selected?.id === job.id}
      isChecked={checkedJobs.has(job.id)}
      isDragging={draggingJobId === job.id}
      menuItems={(menu) => renderJobMenuItems(menu, job)}
      onActivate={(e) => {
        handleJobClick(e, job, flatIdx);
      }}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        setDraggingJobId(job.id);
        const count = checkedJobs.has(job.id) ? checkedJobs.size : 1;
        if (count > 1) {
          const ghost = document.createElement('div');
          ghost.className =
            'fixed -top-20 left-0 flex items-center gap-2 bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground';
          ghost.textContent = `Moving ${count} target items`;
          document.body.append(ghost);
          e.dataTransfer.setDragImage(ghost, 0, 0);
          dragGhostRef.current = ghost;
        }
      }}
      onDragEnd={() => {
        setDraggingJobId(null);
        setDragOverTarget(null);
        if (dragGhostRef.current) {
          dragGhostRef.current.remove();
          dragGhostRef.current = null;
        }
      }}
    />
  );

  const {
    chips: filterChips,
    categoryCounts: filterCategoryCounts,
    clearAll: clearAllFilters
  } = useActiveFilterChips({
    query: itemQuery,
    onQueryChange: setItemQuery,
    itemFilters,
    onItemFiltersChange: setItemFilters,
    flags: flagFilter,
    onFlagsChange: setFlagFilter,
    excludedTiers: matchTierFilterApplicable ? excludedMatchTiers : new Set<ImageMatchTier>(),
    onExcludedTiersChange: setExcludedMatchTiers,
    smartFilter: {
      active: smartFilterApplicable && smartFilterEnabled,
      query: smartQuery,
      includesEnabled: smartFilterIncludesEnabled,
      onDisable() {
        setSmartFilterEnabled(false);
      }
    }
  });

  return (
    <div className="bg-card text-foreground flex h-full w-full overflow-hidden">
      {/* The rail is resizable because folder names and nesting vary a lot between analysts; the
          width is in pixels and survives window resizes, so only a drag changes it. */}
      <ResizablePanelGroup
        className="min-w-0 flex-1"
        onLayoutChanged={(_layout, meta) => {
          if (meta.isUserInteraction && railWidthRef.current !== null) {
            setRailWidth(railWidthRef.current);
          }
        }}>
        {/* ── Left rail ── */}
        <ResizablePanel
          id="investigations-rail"
          defaultSize={railWidth}
          minSize={INVESTIGATIONS_RAIL_WIDTH_MIN}
          maxSize={INVESTIGATIONS_RAIL_WIDTH_MAX}
          groupResizeBehavior="preserve-pixel-size"
          panelRef={(handle) => {
            railPanelRef.current = handle;
          }}
          onResize={(size) => {
            railWidthRef.current = size.inPixels;
          }}>
          <aside
            aria-label="Investigation folders"
            className="bg-card text-card-foreground border-border flex h-full flex-col border-r select-none">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* Quick Filter Search */}
              <div className="flex shrink-0 flex-col gap-2 px-3 pt-2 pb-3">
                <div className="flex items-center gap-2">
                  <InputGroup className="min-w-0 flex-1">
                    <InputGroupAddon>
                      <Search />
                    </InputGroupAddon>
                    <InputGroupInput
                      aria-label="Search investigations"
                      placeholder="Search investigations..."
                      value={jobQuery}
                      onChange={(e) => {
                        setJobQuery(e.target.value);
                      }}
                    />
                  </InputGroup>

                  {/* The scope toggle is the analyst's own avatar beside the search, per the rail
                      design: pressed, the list is theirs alone. Only roles that see everyone's
                      investigations have anything to narrow. */}
                  {isBroadView && user ? (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-pressed={mineOnly}
                            aria-label={`Only show my investigations (${user.displayName})`}
                            className={cn(
                              'focus-visible:outline-ring inline-flex shrink-0 cursor-pointer rounded-full p-0.5 ring-2 transition-shadow focus-visible:outline-2 focus-visible:outline-offset-2',
                              mineOnly ? 'ring-primary' : 'hover:ring-border ring-transparent'
                            )}
                            onClick={() => {
                              setMineOnly(!mineOnly);
                            }}
                          />
                        }>
                        <Avatar className="size-7">
                          <AvatarFallback variant={mineOnly ? 'primary' : 'default'}>
                            {initialsOf(user)}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Only show my investigations</TooltipContent>
                    </Tooltip>
                  ) : null}

                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant={scheduledOnly ? 'default' : 'secondary'}
                          size="icon"
                          aria-label="Only show scheduled investigations"
                          aria-pressed={scheduledOnly}
                          onClick={() => {
                            setScheduledOnly(!scheduledOnly);
                          }}
                        />
                      }>
                      <CalendarClock />
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Only show scheduled investigations
                    </TooltipContent>
                  </Tooltip>
                </div>

                {/* Type, vertical and language filters all live behind one popover checklist
                    each, matching how folder row actions collapsed into one menu - the type chips
                    used to wrap across two lines on their own before the platform even grows past
                    a handful of collection types, the same density problem the reference-data-
                    driven verticals/language lists already solved by being dropdowns instead of a
                    flat chip row.
                    The trailing icon is the disclosure chevron (ChevronDown, matching every other
                    menu trigger in this file), not a per-filter category icon - the label text
                    already says which filter this is, so the icon's job is "this opens a menu"
                    rather than decoration, and dropping the decorative icon instead of the chevron
                    keeps that cue without costing any width. Each trigger still shrinks with its
                    label truncating (title carries the full text) rather than wrapping the row, so
                    a filter's count suffix can never push this back to two lines. */}
                <div className="flex items-center gap-1.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      title={typeFilterLabel}
                      render={
                        <Button
                          variant="secondary"
                          className="min-w-0 flex-1 justify-between px-2"
                        />
                      }>
                      <span className="truncate">{typeFilterLabel}</span>
                      <ChevronDown />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="max-h-80">
                      {JOB_TYPE_FILTERS.map(({strategy, label, icon: Icon}) => (
                        <DropdownMenuCheckboxItem
                          key={strategy}
                          checked={jobTypeFilter.has(strategy)}
                          onCheckedChange={() => {
                            setJobTypeFilter((previous) => {
                              const next = new Set(previous);
                              if (next.has(strategy)) {
                                next.delete(strategy);
                              } else {
                                next.add(strategy);
                              }

                              return next;
                            });
                          }}>
                          <Icon className="h-3.5 w-3.5" />
                          {label}
                        </DropdownMenuCheckboxItem>
                      ))}
                      {jobTypeFilter.size > 0 ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setJobTypeFilter(new Set());
                            }}>
                            Clear
                          </DropdownMenuItem>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {policyViolationOptions.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        title={verticalsFilterLabel}
                        render={
                          <Button
                            variant="secondary"
                            className="min-w-0 flex-1 justify-between px-2"
                          />
                        }>
                        <span className="truncate">{verticalsFilterLabel}</span>
                        <ChevronDown />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64 p-2">
                        <MultiSelectFilterBody
                          label="Verticals"
                          options={policyViolationOptions.map((value) => ({
                            value,
                            label: value
                          }))}
                          selected={[...policyViolationFilter]}
                          onChange={(next) => {
                            setPolicyViolationFilter(new Set(next));
                          }}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}

                  {languageOptions.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        title={languageFilterLabel}
                        render={
                          <Button
                            variant="secondary"
                            className="min-w-0 flex-1 justify-between px-2"
                          />
                        }>
                        <span className="truncate">{languageFilterLabel}</span>
                        <ChevronDown />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64 p-2">
                        <MultiSelectFilterBody
                          label="Languages"
                          options={languageOptions.map((value) => ({value, label: value}))}
                          selected={[...languageFilter]}
                          onChange={(next) => {
                            setLanguageFilter(new Set(next));
                          }}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}

                  {creatorOptions.length > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        title={creatorFilterLabel}
                        render={
                          <Button
                            variant="secondary"
                            className="min-w-0 flex-1 justify-between px-2"
                          />
                        }>
                        <span className="truncate">{creatorFilterLabel}</span>
                        <ChevronDown />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64 p-2">
                        <MultiSelectFilterBody
                          label="Created by"
                          options={creatorOptions.map((value) => ({
                            value,
                            label: value.split('@')[0] ?? value
                          }))}
                          selected={[...creatorFilter]}
                          onChange={(next) => {
                            setCreatorFilter(new Set(next));
                          }}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              </div>

              {/* Scrollable Folder/Job Navigation */}
              <div className="flex-1 overflow-y-auto pb-3">
                {/* The tree's own toolbar, per the design: expand/collapse on the left, the two
                    create actions on the right with New investigation as the one filled button. It
                    lives inside the scrolling list so its buttons share the rows' right edge,
                    scrollbar included. */}
                <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
                  {folders.length > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setExpandedFolders(
                          allFoldersExpanded ? new Set() : new Set(folders.map((f) => f.id))
                        );
                        setUnfiledExpanded(!allFoldersExpanded);
                      }}>
                      {allFoldersExpanded ? 'Collapse all' : 'Expand all'}
                    </Button>
                  ) : null}
                  <span className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="New folder"
                    onClick={() => {
                      setNewFolderParentId(null);
                      setShowNewFolder(true);
                      setFolderError(null);
                    }}>
                    <FolderPlus />
                    Folder
                  </Button>
                  <Button
                    size="sm"
                    aria-label="New investigation"
                    onClick={() => {
                      setNewInvestigationIn(null);
                    }}>
                    <Plus />
                    Investigation
                  </Button>
                </div>

                {/* Inline Folder Creator Input */}
                {showNewFolder ? (
                  <div className="animate-in fade-in slide-in-from-top-1 px-3 pb-2 duration-150">
                    <InputGroup>
                      <InputGroupAddon>
                        <FolderIcon />
                      </InputGroupAddon>
                      <InputGroupInput
                        ref={newFolderRef}
                        value={newFolderName}
                        placeholder={
                          newFolderParentId
                            ? `New folder inside ${folders.find((f) => f.id === newFolderParentId)?.name ?? 'folder'}...`
                            : 'Folder title...'
                        }
                        onChange={(e) => {
                          setNewFolderName(e.target.value);
                          setFolderError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            void handleCreateFolder();
                          }

                          if (e.key === 'Escape') {
                            setShowNewFolder(false);
                            setNewFolderName('');
                            setNewFolderParentId(null);
                            setFolderError(null);
                          }
                        }}
                      />
                      <InputGroupAddon align="inline-end">
                        {creatingFolder ? (
                          <Spinner />
                        ) : (
                          <InputGroupButton
                            disabled={!newFolderName.trim()}
                            onClick={() => void handleCreateFolder()}>
                            Save
                          </InputGroupButton>
                        )}
                      </InputGroupAddon>
                    </InputGroup>
                    {folderError ? (
                      <p className="text-destructive-ink mt-1 px-1 text-xs">{folderError}</p>
                    ) : null}
                  </div>
                ) : null}
                {folderRailItems.map((item) => {
                  const {folder, depth} = item;
                  const folderJobs = inFolder(folder.id);
                  const folderPages = scopePages.get(scopeKey({kind: 'folder', id: folder.id}));
                  const hasSubfolders = parentFolderIds.has(folder.id);
                  const isExpanded = expandedFolders.has(folder.id);
                  const isFolderSelected = selectedFolder?.id === folder.id;
                  const refusesDrop =
                    draggingFolderId !== null &&
                    invalidFolderDropTargets(draggingFolderId).has(folder.id);
                  if (item.kind === 'header') {
                    return (
                      <div key={folder.id}>
                        <FolderTreeRow
                          name={folder.name}
                          tooltip={folderRowTooltip(folder)}
                          depth={depth}
                          count={folderBadgeCount(folder, badgeScope, loadedSubtreeJobCounts)}
                          isExpanded={isExpanded}
                          state={
                            dragOverTarget === folder.id
                              ? 'drop'
                              : isFolderSelected
                                ? 'active'
                                : 'idle'
                          }
                          refusesDrop={refusesDrop}
                          rename={
                            renamingFolderId === folder.id ? (
                              <Input
                                ref={renameFolderRef}
                                autoFocus
                                value={renameFolderName}
                                className="h-6 min-w-0 flex-1 px-1.5"
                                onChange={(e) => {
                                  setRenameFolderName(e.target.value);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void saveRenameFolder(folder.id);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelRenameFolder();
                                  }
                                }}
                                onBlur={() => {
                                  void saveRenameFolder(folder.id);
                                }}
                              />
                            ) : undefined
                          }
                          menuItems={(menu) => renderFolderMenuItems(menu, folder)}
                          onToggle={() => {
                            toggleFolder(folder.id);
                          }}
                          onSelect={() => {
                            void selectFolder(folder);
                            if (!expandedFolders.has(folder.id)) {
                              setExpandedFolders((previous) => new Set([...previous, folder.id]));
                            }
                          }}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            setDraggingFolderId(folder.id);
                          }}
                          onDragEnd={() => {
                            setDraggingFolderId(null);
                            setDragOverTarget(null);
                          }}
                          onDragOver={(e) => {
                            if (!draggingJobId && !draggingFolderId) {
                              return;
                            }

                            if (refusesDrop) {
                              return;
                            }

                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverTarget(folder.id);
                            if (!expandedFolders.has(folder.id)) {
                              setExpandedFolders((previous) => new Set([...previous, folder.id]));
                            }
                          }}
                          onDragLeave={() => {
                            setDragOverTarget(null);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDrop(folder.id);
                          }}
                        />
                        {renamingFolderId === folder.id && renameFolderError ? (
                          <p
                            style={railIndent(depth, RAIL_CARDS_INSET)}
                            className="text-destructive-ink pr-3 text-xs">
                            {renameFolderError}
                          </p>
                        ) : null}
                      </div>
                    );
                  }

                  // The body (the folder's own investigations) is its own item, emitted after
                  // the folder's whole subtree so subfolders stay pinned above the jobs. A
                  // folder holding only subfolders renders no body of its own. Kept while it
                  // is a drop target so there is still somewhere to drop a job directly into it.
                  return (
                    <div key={`${folder.id}:body`}>
                      {folderJobs.length > 0 || !hasSubfolders || dragOverTarget === folder.id ? (
                        <div
                          style={railIndent(depth, RAIL_CARDS_INSET)}
                          className={cn(
                            'flex flex-col gap-1.5 pt-1 pr-3 pb-2 transition-colors',
                            dragOverTarget === folder.id && 'bg-primary/5'
                          )}
                          onDragOver={(e) => {
                            if (draggingJobId) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverTarget(folder.id);
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                              setDragOverTarget(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDrop(folder.id);
                          }}>
                          {folderJobs.length === 0 ? (
                            <p
                              className={cn(
                                'py-2 text-center text-xs italic',
                                dragOverTarget === folder.id
                                  ? 'text-primary-ink font-medium'
                                  : 'text-muted-foreground'
                              )}>
                              {dragOverTarget === folder.id ? 'Drop target here' : 'Empty folder'}
                            </p>
                          ) : (
                            folderJobs.map((j) =>
                              renderJobRow(j, filteredJobIndexById.get(j.id) ?? 0)
                            )
                          )}
                          {renderScopeMore({kind: 'folder', id: folder.id}, folderPages)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {/* Ungrouped Investigations Group */}
                {ungrouped.length > 0 ||
                draggingFolderHasParent ||
                (draggingJobId &&
                  folders.some(
                    (f) => jobs.find((j) => j.id === draggingJobId)?.folder_id === f.id
                  )) ? (
                  <div>
                    {/* Unfiled is drawn as a system folder, per the design: it collapses like
                        one and has a menu, with only New investigation in it. Clicking the row
                        toggles it, since there is no aggregate view to open. The row stays the drop
                        target for ungrouping a job or lifting a nested folder to the top level. */}
                    <ContextMenu>
                      <ContextMenuTrigger
                        className={cn(
                          RAIL_ROW,
                          'pl-1',
                          dragOverTarget === 'ungrouped' ? RAIL_ROW_DROP : RAIL_ROW_IDLE
                        )}
                        onClick={() => {
                          setUnfiledExpanded(!unfiledExpanded);
                        }}
                        onDragOver={(e) => {
                          if (!draggingJobId && !draggingFolderHasParent) {
                            return;
                          }

                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverTarget('ungrouped');
                        }}
                        onDragLeave={() => {
                          setDragOverTarget(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDrop(null);
                        }}>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={unfiledExpanded ? 'Collapse Unfiled' : 'Expand Unfiled'}
                          className="text-muted-foreground hover:text-foreground size-5 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUnfiledExpanded(!unfiledExpanded);
                          }}>
                          <ChevronRight
                            className={cn(
                              'transition-transform duration-150',
                              unfiledExpanded && 'rotate-90'
                            )}
                          />
                        </Button>
                        <FolderIcon className="size-3.5 shrink-0" />
                        <span className="text-foreground flex-1 truncate">
                          {dragOverTarget === 'ungrouped'
                            ? draggingFolderId
                              ? 'Drop to move to top level'
                              : 'Drop to ungroup'
                            : 'Unfiled'}
                        </span>
                        {dragOverTarget === 'ungrouped' ? null : (
                          <RailInvestigationCount count={ungrouped.length} />
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label="Actions for Unfiled"
                            title="Actions for Unfiled"
                            render={
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-foreground -my-1 size-6 shrink-0"
                              />
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                            }}>
                            <MoreHorizontal className="size-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {renderUnfiledMenuItems(DROPDOWN_MENU)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-52">
                        {renderUnfiledMenuItems(CONTEXT_MENU)}
                      </ContextMenuContent>
                    </ContextMenu>
                    {unfiledExpanded ? (
                      <div
                        style={railIndent(0, RAIL_CARDS_INSET)}
                        className={cn(
                          'flex flex-col gap-1.5 pt-1 pr-3 pb-2 transition-colors',
                          dragOverTarget === 'ungrouped' && 'bg-primary/5'
                        )}
                        onDragOver={(e) => {
                          if (draggingJobId || draggingFolderHasParent) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverTarget('ungrouped');
                          }
                        }}
                        onDragLeave={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setDragOverTarget(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDrop(null);
                        }}>
                        {ungrouped.map((j) => renderJobRow(j, filteredJobIndexById.get(j.id) ?? 0))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {filteredJobs.length === 0 ? (
                  <Empty className="h-32 p-4">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Film />
                      </EmptyMedia>
                      <EmptyTitle>No matching investigations</EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                ) : null}

                {unfiledHasMore ? <div ref={jobsSentinelRef} className="h-1" /> : null}

                {unfiledPages?.loading ? (
                  <div className="flex justify-center py-2">
                    <Spinner aria-label="Loading more investigations" />
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        </ResizablePanel>
        <ResizableHandle
          aria-label="Resize investigations rail"
          title="Drag to resize, double-click to reset"
          onDoubleClick={() => {
            railPanelRef.current?.resize(INVESTIGATIONS_RAIL_WIDTH_DEFAULT);
            setRailWidth(INVESTIGATIONS_RAIL_WIDTH_DEFAULT);
          }}
        />

        {/* ── Main column ── */}
        <ResizablePanel id="investigations-main">
          <main className="bg-background flex h-full min-h-0 flex-col overflow-hidden">
            {selected || selectedFolder ? (
              <>
                {/* Investigation header, per the design: the name with its schedule, one line of
                    what the run did and what is hidden, then the first-class actions with the
                    rest behind the dots. */}
                <header
                  ref={setHeaderElement}
                  className="bg-card flex shrink-0 flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 pt-4 pb-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      {selectedImageUrl ? (
                        <SensitiveMediaButton
                          compact
                          label="View the searched image"
                          src={selectedImageUrl}
                          alt="What this investigation was searched with"
                          className="border-border h-10 w-16 shrink-0 overflow-hidden rounded-md border"
                          imageClassName="size-full object-cover"
                          onClick={(blurred, greyed) => {
                            setImageLightboxRevealed(!blurred);
                            setImageLightboxColourised(!greyed);
                            setImageLightboxOpen(true);
                          }}>
                          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/40">
                            <Maximize2 className="size-3.5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                          </span>
                        </SensitiveMediaButton>
                      ) : null}
                      <h2
                        title={headerTitle}
                        className="font-heading text-foreground min-w-0 truncate text-xl font-semibold">
                        {headerTitle}
                      </h2>

                      {selectedFolder ? (
                        <Badge variant="secondary">
                          <FolderIcon />
                          {investigationCountLabel(selectedFolderTotal)}
                        </Badge>
                      ) : null}

                      {selected?.schedule_cron ? (
                        <Badge variant="secondary">
                          <Clock />
                          {describeCron(selected.schedule_cron)}
                          {selected.schedule_active ? null : ' · Paused'}
                        </Badge>
                      ) : null}
                    </div>

                    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs">
                      {selected ? (
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 whitespace-nowrap',
                            selected.status === 'failed' && 'text-destructive-ink'
                          )}>
                          {selected.status === 'completed' ? (
                            <Clock className="size-3" />
                          ) : (
                            <StatusIcon
                              tone={jobStatusTone(selected.status)}
                              busy={selected.status === 'running'}
                            />
                          )}
                          {jobRunLabel(selected)}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <Film className="size-3" />
                        {formatItemsTotal(itemsTotal, itemsTotalCapped)} {ITEM_NOUN_PLURAL}{' '}
                        collected
                      </span>
                      {classifiedCount > 0 ? (
                        <HiddenCount
                          count={classifiedCount}
                          what="classified"
                          shown={showClassified}
                          onToggle={() => {
                            setShowClassified((v) => !v);
                          }}
                        />
                      ) : null}
                      {dismissedCount > 0 ? (
                        <HiddenCount
                          count={dismissedCount}
                          what="dismissed"
                          shown={showDismissed}
                          onToggle={() => {
                            setShowDismissed((v) => !v);
                          }}
                        />
                      ) : null}
                      {/* The tiers' own counts are on the toggles; this says how many that hides,
                          against the loaded pages it was worked out over. */}
                      {matchTierFilteredCount > 0 ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <EyeOff className="size-3" />
                          {matchTierFilteredCount} of {afterTriageItems.length.toLocaleString()}{' '}
                          loaded hidden by match strength
                          <Button
                            variant="link"
                            size="xs"
                            className="h-auto p-0"
                            onClick={() => {
                              setExcludedMatchTiers(new Set());
                            }}>
                            Show
                          </Button>
                        </span>
                      ) : null}
                      {imageSearchFunnel ? (
                        <span
                          className="inline-flex items-center gap-1.5 whitespace-nowrap"
                          title="How much Cloud Vision found in total, against how much of it was a genuine YouTube thumbnail match.">
                          <Images className="size-3" />
                          {imageSearchFunnel.pagesMatched.toLocaleString()} web matches ·{' '}
                          {imageSearchFunnel.summary}
                        </span>
                      ) : null}
                      {selected?.target_policy_violations.length ? (
                        <span className="inline-flex items-center gap-1.5">
                          <ShieldAlert className="size-3" />
                          {selected.target_policy_violations.join(', ')}
                        </span>
                      ) : null}
                      {selected?.language ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <Languages className="size-3" />
                          {selected.language}
                        </span>
                      ) : null}
                      {selected?.intent ? (
                        <span className="text-foreground italic">
                          &quot;{selected.intent}&quot;
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {/* Analysts refresh often while a run is landing, so it stays a button rather
                        than going behind the dots with the rarer actions, until the header is too
                        narrow for it, unless no More actions menu is there to take it. */}
                    {headerCompact && (selected || selectedFolder) ? null : (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="secondary"
                              size="icon"
                              aria-label="Refresh items"
                              onClick={refreshSelection}
                            />
                          }>
                          {loadingItems ? <Spinner /> : <RefreshCw />}
                        </TooltipTrigger>
                        <TooltipContent>Refresh items</TooltipContent>
                      </Tooltip>
                    )}
                    {selected ? (
                      <>
                        {headerCompact ? null : (
                          <Button
                            variant="secondary"
                            disabled={unschedulableReason(selected) !== undefined}
                            title={unschedulableReason(selected)}
                            onClick={() => {
                              setScheduling(selected);
                            }}>
                            <CalendarClock />
                            Schedule
                          </Button>
                        )}
                        {headerCompact ? null : (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setShareTarget({
                                kind: 'job',
                                id: selected.id,
                                label: selected.search_term
                              });
                            }}>
                            <Share2 />
                            Share
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label="More actions"
                            render={<Button variant="secondary" size="icon" />}>
                            <MoreVertical />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {headerCompact ? (
                              <>
                                <DropdownMenuItem onClick={refreshSelection}>
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Refresh items
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={unschedulableReason(selected) !== undefined}
                                  onClick={() => {
                                    setScheduling(selected);
                                  }}>
                                  <CalendarClock className="h-3.5 w-3.5" />
                                  Schedule
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setShareTarget({
                                      kind: 'job',
                                      id: selected.id,
                                      label: selected.search_term
                                    });
                                  }}>
                                  <Share2 className="h-3.5 w-3.5" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            ) : null}
                            {renderMoveToFolderSub(DROPDOWN_MENU, selected, (folderId) => {
                              void handleMoveJobs([selected.id], folderId);
                            })}
                            <DropdownMenuItem
                              disabled={selected.strategy === 'image_search'}
                              title={
                                selected.strategy === 'image_search'
                                  ? 'An image search needs its image uploaded again, so start a new one'
                                  : undefined
                              }
                              onClick={() => {
                                setDuplicateOf(selected);
                                setNewInvestigationIn(selected.folder_id);
                              }}>
                              <Copy className="h-3.5 w-3.5" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => {
                                setPendingDelete({kind: 'investigation', job: selected});
                              }}>
                              Delete investigation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                disabled={
                                  rerunning ||
                                  selected.status === 'pending' ||
                                  selected.status === 'running'
                                }
                                onClick={() => void handleRerun()}
                              />
                            }>
                            {rerunning ? <Spinner /> : <Play />}
                            Re-run
                          </TooltipTrigger>
                          <TooltipContent>Re-run to collect newly published items</TooltipContent>
                        </Tooltip>
                      </>
                    ) : null}

                    {selectedFolder ? (
                      <>
                        {headerCompact ? null : (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setShareTarget({
                                kind: 'folder',
                                id: selectedFolder.id,
                                label: selectedFolder.name
                              });
                            }}>
                            <Share2 />
                            Share
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label="More actions"
                            render={<Button variant="secondary" size="icon" />}>
                            <MoreVertical />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {headerCompact ? (
                              <>
                                <DropdownMenuItem onClick={refreshSelection}>
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Refresh items
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setShareTarget({
                                      kind: 'folder',
                                      id: selectedFolder.id,
                                      label: selectedFolder.name
                                    });
                                  }}>
                                  <Share2 className="h-3.5 w-3.5" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            ) : null}
                            {renderFolderMenuItems(DROPDOWN_MENU, selectedFolder)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                disabled={
                                  rerunning ||
                                  selectedFolderJobs.every(
                                    (j) => j.status === 'pending' || j.status === 'running'
                                  )
                                }
                                onClick={() => void handleRerunAll()}
                              />
                            }>
                            {rerunning ? <Spinner /> : <Play />}
                            Re-run all
                          </TooltipTrigger>
                          <TooltipContent>
                            Re-run every investigation in this folder to collect newly published
                            items
                          </TooltipContent>
                        </Tooltip>
                      </>
                    ) : null}
                  </div>
                </header>

                {/* View toolbar, per the design: Items / Channels, the filters that narrow the
                    results, then how they are shown - blur, sort, size and layout - on the right. */}
                <FilterBar
                  className="bg-card border-border shrink-0 border-b px-5 py-2"
                  search={{
                    value: itemQuery,
                    onChange: setItemQuery,
                    placeholder: 'Filter current results...',
                    className: 'w-70'
                  }}
                  leading={
                    <Tabs
                      value={groupByChannel ? 'channels' : 'items'}
                      className="gap-0"
                      onValueChange={(value) => {
                        const next = value === 'channels';
                        if (next === groupByChannel) {
                          return;
                        }

                        setGroupByChannel(next);
                        if (!next) {
                          // Otherwise a group left expanded here can still get fetched by the reload
                          // effect below (it doesn't check groupByChannel, only
                          // expandedChannels/serverChannelGroups) even though nothing renders it while
                          // ungrouped - a wasted request the next toggle-on doesn't need.
                          setExpandedChannels(new Set());
                        }
                      }}>
                      <TabsList>
                        <TabsTrigger value="items">
                          Items{' '}
                          <span className="tabular-nums">
                            {formatItemsTotal(itemsTotal, itemsTotalCapped)}
                          </span>
                        </TabsTrigger>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <TabsTrigger
                                value="channels"
                                disabled={loadingAllForGrouping || loadingMore}
                              />
                            }>
                            {loadingAllForGrouping ? <Spinner /> : null}
                            Channels
                            {loadingAllForGrouping ? (
                              <span className="text-muted-foreground tabular-nums">
                                {groupingLoadTarget.toLocaleString()}/
                                {formatItemsTotal(itemsTotal, itemsTotalCapped)}
                              </span>
                            ) : groupByChannel ? (
                              <span className="tabular-nums">
                                {displayChannelGroups.length.toLocaleString()}
                              </span>
                            ) : null}
                          </TooltipTrigger>
                          <TooltipContent>
                            {loadingAllForGrouping
                              ? 'Fetching the rest of this investigation so channel counts are accurate'
                              : `Collapsible section per channel, with accurate counts for the whole ${selected ? 'investigation' : 'folder'}`}
                          </TooltipContent>
                        </Tooltip>
                      </TabsList>
                    </Tabs>
                  }>
                  <InvestigationFilters
                    activeCount={filterChips.filter((chip) => chip.category !== null).length}
                    categoryCounts={filterCategoryCounts}
                    flags={flagFilter}
                    itemFilters={itemFilters}
                    showLastPostSince={
                      PLATFORM_ID === 'pinterest' ||
                      lastPostSinceFilter !== '' ||
                      items.some((item) => item.content_type === 'actor')
                    }
                    showPublished={
                      PLATFORM_ID === 'pinterest' ||
                      publishedSinceFilter !== '' ||
                      publishedUntilFilter !== '' ||
                      items.some((item) => item.content_type !== 'actor')
                    }
                    onFlagsChange={setFlagFilter}
                    onItemFiltersChange={setItemFilters}
                    {...(matchTierFilterApplicable
                      ? {
                          matchStrength: {
                            excluded: excludedMatchTiers,
                            counts: matchTierCounts,
                            hiddenCount: matchTierFilteredCount,
                            totalCount: afterTriageItems.length,
                            onExcludedChange: setExcludedMatchTiers
                          }
                        }
                      : {})}
                    {...(smartFilterApplicable
                      ? {
                          smartFilter: {
                            query: smartQuery,
                            enabled: smartFilterEnabled,
                            includeMode: smartFilterIncludeMode,
                            includesEnabled: smartFilterIncludesEnabled,
                            stemming: smartFilterStemming,
                            hiddenCount: smartFilteredCount,
                            totalCount: afterTriageItems.length,
                            hiddenChannelCount: smartHiddenChannelCount,
                            onEnabledChange: setSmartFilterEnabled,
                            onIncludeModeChange: setSmartFilterIncludeMode,
                            onIncludesEnabledChange: setSmartFilterIncludesEnabled,
                            onStemmingChange: setSmartFilterStemming
                          }
                        }
                      : {})}
                    {...(channelAggregateScope
                      ? {
                          rdSignals: {
                            scope: channelAggregateScope,
                            filters: rdOptionFilters,
                            selections: rdFilters
                          }
                        }
                      : {})}
                  />

                  <div className="ml-auto flex items-center gap-1.5">
                    {/* Blur lives with the view controls, not the page header: it is how this
                        page's media is shown, like sort and layout. */}
                    <SensitiveMediaToggle />

                    <SortMenu
                      value={sortOrder}
                      onChange={(next) => {
                        setItemFilters({sortOrder: next});
                      }}
                    />

                    {view === 'grid' && !networkShown ? (
                      <ResponsiveThumbnailSizeControl
                        min={THUMBNAIL_SIZE_MIN}
                        max={THUMBNAIL_SIZE_MAX}
                        value={thumbnailSize}
                        onChange={(level) => {
                          setThumbnailSize(level as ThumbnailSize);
                        }}
                      />
                    ) : null}

                    <ItemsViewToggle
                      {...(showNetworkView
                        ? {network: {open: networkShown, onOpenChange: setNetworkOpen}}
                        : {})}
                    />
                  </div>
                </FilterBar>
                <ActiveFilterChips chips={filterChips} onClearAll={clearAllFilters} />

                {rerunError ? (
                  <Alert variant="destructive" className="mx-5 mt-3 w-auto">
                    <AlertCircle />
                    <AlertTitle>{rerunError}</AlertTitle>
                    <AlertAction>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Dismiss"
                        onClick={() => {
                          setRerunError(null);
                        }}>
                        <X />
                      </Button>
                    </AlertAction>
                  </Alert>
                ) : null}

                {/* Smart Filter Notice Banner. Gated on `smartFilterApplicable` explicitly rather than
                relying on `smartQuery` being forced empty for id/hash-based strategies. */}
                {smartFilterApplicable && smartFilteredCount > 0 ? (
                  <Alert className="mx-5 mt-3 w-auto">
                    <Wand2 />
                    <AlertTitle>
                      {smartFilteredCount} {smartFilteredCount === 1 ? ITEM_NOUN : ITEM_NOUN_PLURAL}
                      {groupByChannel && smartHiddenChannelCount > 0 ? (
                        <>
                          {' '}
                          and {smartHiddenChannelCount}{' '}
                          {smartHiddenChannelCount === 1 ? 'channel' : 'channels'}
                        </>
                      ) : null}{' '}
                      hidden by smart filter{' '}
                      {smartFilterIncludesEnabled
                        ? '(contain excluded terms, or are missing the search terms)'
                        : '(contain excluded terms)'}
                    </AlertTitle>
                    <AlertAction>
                      <Button
                        variant="link"
                        size="xs"
                        onClick={() => {
                          setSmartFilterEnabled(false);
                        }}>
                        Show all
                      </Button>
                    </AlertAction>
                  </Alert>
                ) : null}

                {/* Main Content Scroll View */}
                <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-4">
                  {networkShown && selected ? (
                    <FollowerNetworkGraph jobId={selected.id} onClassify={setClassifyItems} />
                  ) : loadingItems && items.length === 0 ? (
                    <div className="flex h-96 flex-col items-center justify-center gap-3">
                      <Spinner className="size-6" />
                      <p className="text-muted-foreground text-xs">
                        Loading target intelligence...
                      </p>
                    </div>
                  ) : // In job-mode grouping, visibleItems is just the flat background page (~50 rows),
                  // not what the groups render from (see showFlatItemPagination) - it can be empty
                  // while serverChannelGroups still has real channels with active items (e.g. that
                  // background page happened to auto-load a triaged-away stretch). Job-mode emptiness
                  // is decided by displayChannelGroups instead, in the branch below.
                  // A finished Falcon/Pinterest image_search job with no items: every matched pin was gone by the
                  // time pinterest_pin fetched it (or none was collected), so show what Vision matched as
                  // leads instead of the generic "no items" empty state. While the job is running, or once
                  // its items are all triaged away, the empty state below is the right thing; gated on the
                  // platform because ImageSearchLeads renders pinterest.com pin links.
                  PLATFORM_ID === 'pinterest' &&
                    !(groupByChannel && serverChannelGroups) &&
                    visibleItems.length === 0 &&
                    selected?.strategy === 'image_search' &&
                    selected.status !== 'running' &&
                    selectedJobImageMatches.data ? (
                    <ImageSearchLeads
                      matches={selectedJobImageMatches.data.matches}
                      offPlatformPageUrls={selectedJobImageMatches.data.off_platform_page_urls}
                    />
                  ) : !(groupByChannel && serverChannelGroups) &&
                    visibleItems.length === 0 &&
                    selected !== null &&
                    googleSearchState(selected) !== null ? (
                    <GoogleSearchProgress job={selected} />
                  ) : !(groupByChannel && serverChannelGroups) && visibleItems.length === 0 ? (
                    <Empty className="h-96">
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <Film />
                        </EmptyMedia>
                        <EmptyTitle>No media items available</EmptyTitle>
                        <EmptyDescription>
                          {selected?.status === 'running'
                            ? 'Investigation currently active. Media items will automatically populate upon stream processing.'
                            : 'No collected items match the current query filter settings.'}
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : groupByChannel && displayChannelGroups.length === 0 ? (
                    // Distinct from the "No media items available" case above: visibleItems (the flat
                    // list) isn't empty, but every channel computed to a visible count of 0 under the
                    // current showClassified/showDismissed toggles and/or the smart/match-tier filters
                    // (see computeChannelDisplayCount) - e.g. every item in the job has been dismissed,
                    // or every channel's videos carry an excluded term. Without this, that state
                    // rendered as a blank scroll area with no explanation, indistinguishable from an
                    // actual bug.
                    <Empty className="h-96">
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <EyeOff />
                        </EmptyMedia>
                        <EmptyTitle>Every channel is hidden by the current filters</EmptyTitle>
                        <EmptyDescription>
                          {smartHiddenChannelCount > 0
                            ? 'The smart filter hid every channel currently loaded.'
                            : classifiedCount > 0 || dismissedCount > 0
                              ? 'Turn on "Show classified" or "Show dismissed" above to bring channels back into view.'
                              : 'No collected items match the current query filter settings.'}
                        </EmptyDescription>
                      </EmptyHeader>
                      {smartHiddenChannelCount > 0 ? (
                        <EmptyContent>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSmartFilterEnabled(false);
                            }}>
                            Show all
                          </Button>
                        </EmptyContent>
                      ) : null}
                    </Empty>
                  ) : groupByChannel ? (
                    <div className="flex flex-col gap-4">
                      {displayChannelGroups.slice(0, renderedChannelGroupCount).map((group) => {
                        const expanded = expandedChannels.has(group.key);
                        const hydrating = hydratingChannelKey === group.key;
                        const loadingChannelItems = channelItemsLoadingKeys.has(group.key);
                        const channelItemsErrored = channelItemsErrorKeys.has(group.key);
                        const stats = channelStatsByKey.get(group.key);
                        // Every item the selection has from this channel is already dismissed, so the group only rendered at all because "Show
                        // dismissed" revealed it (see serverChannelGroups' own count filter). Swap the
                        // action to a restore instead of dismissing an already-fully-dismissed channel.
                        const fullyDismissed =
                          stats !== undefined &&
                          stats.total > 0 &&
                          stats.dismissedCount === stats.total;
                        // Mirrors handleSetChannelStatus's own gating exactly, so the button's
                        // disabled/title state never disagrees with which path a click actually takes -
                        // a channel with classified items hidden by showClassified can't use the
                        // author_id fast path to dismiss (it would silently dismiss items the analyst
                        // can't see), only to restore (bringing everything back is the point of that
                        // direction regardless of what's shown).
                        const classifiedSafeForFastPath =
                          fullyDismissed || showClassified || !stats || stats.classifiedCount === 0;
                        // The author_id fast path only works with an author_id, no active filter
                        // narrowing the view, and (when dismissing) nothing classified hidden from view
                        // (see handleSetChannelStatus) - otherwise the dismiss/restore action falls back
                        // to whatever's already loaded, empty until expanded.
                        const mustUseIdListDismiss =
                          Boolean(serverChannelGroups) &&
                          (!group.channelId || channelFiltersActive || !classifiedSafeForFastPath);
                        // Same filter chain the expanded body itself renders through (triage + smart +
                        // match-tier) - what "Select all" in this header selects should match what the
                        // analyst can actually see under this group, not group.items' raw, unfiltered
                        // set (which can include items hidden by the current toggles). smartHidden is
                        // what the body falls back to explaining when that leaves nothing to render.
                        const {visible: visibleGroupItems, smartHidden: smartHiddenGroupItems} =
                          filterGroupItems(group.items, showClassified, showDismissed, {
                            isSmartFilterRejected,
                            isMatchTierRejected
                          });
                        const allGroupItemsSelected =
                          visibleGroupItems.length > 0 &&
                          visibleGroupItems.every((i) => selectedItemIds.has(i.id));
                        const selectAllHint =
                          visibleGroupItems.length === 0
                            ? `Expand this channel to select its ${ITEM_NOUN_PLURAL}`
                            : allGroupItemsSelected
                              ? 'Deselect this channel'
                              : `Select all ${visibleGroupItems.length} ${ITEM_NOUN_PLURAL} in this channel`;
                        const dismissChannelHint = mustUseIdListDismiss
                          ? visibleGroupItems.length === 0
                            ? `Expand this channel to load its ${ITEM_NOUN_PLURAL} before ${fullyDismissed ? 'restoring' : 'dismissing'}`
                            : group.channelId
                              ? channelFiltersActive
                                ? `A filter or search is active - only the ${ITEM_NOUN_PLURAL} currently loaded for this channel are affected`
                                : `This channel has classified ${ITEM_NOUN_PLURAL} hidden by "Show classified" - only the ${ITEM_NOUN_PLURAL} currently loaded are affected`
                              : `No author id for this channel, so it can't be ${fullyDismissed ? 'restored' : 'dismissed'} by author - only the ${ITEM_NOUN_PLURAL} currently loaded are affected`
                          : `${fullyDismissed ? 'Restore' : 'Dismiss'} every ${ITEM_NOUN} from this channel`;
                        return (
                          // `block` drops the Collapsible's own trigger-to-panel gap, which would
                          // open a seam between the sticky header and the list under it. No
                          // overflow-hidden on the wrapper: anything but visible would make it
                          // (rather than the actual scrollable list) the reference container for
                          // this header's `sticky`, breaking it.
                          <Collapsible
                            key={group.key}
                            open={expanded}
                            className="border-border bg-card block rounded-lg border"
                            onOpenChange={() => {
                              toggleChannelExpanded(group.key);
                              if (serverChannelGroups) {
                                void ensureChannelItemsLoaded(group);
                              }
                            }}>
                            {/* -top-4 cancels the scroll container's own py-4: sticky's offset is
                            measured from that container's padding edge, not its visible top, so a
                            plain top-0 parks the header a py-4-sized gap below the banner. */}
                            <div className="bg-card border-border sticky -top-4 z-20 flex w-full items-center gap-2 border-b px-3 py-2">
                              <CollapsibleTrigger className="hover:text-foreground flex min-w-0 flex-1 items-center gap-2 text-left transition-colors">
                                <ChevronRight className="text-muted-foreground size-4 shrink-0 transition-transform in-data-panel-open:rotate-90" />
                                <span className="text-foreground truncate text-xs font-medium">
                                  {group.label}
                                </span>
                              </CollapsibleTrigger>
                              {group.channelId ? (
                                <div className="flex shrink-0 items-center gap-1">
                                  <span className="text-muted-foreground font-mono text-xs">
                                    {group.channelId}
                                  </span>
                                  {PLATFORM_ID === 'youtube' ? (
                                    <CopyUrlButton
                                      url={`https://www.youtube.com/channel/${encodeURIComponent(group.channelId)}`}
                                      label="Copy channel URL"
                                      className="text-muted-foreground"
                                    />
                                  ) : null}
                                </div>
                              ) : null}
                              <div className="flex shrink-0 items-center gap-2">
                                {/* Server-derived counts only, over the whole job - client-side smart
                                filtering can't inform these, so they're only shown once
                                serverChannelGroups is what's actually rendering (a selected job). */}
                                {stats && stats.dismissedCount > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger render={<Badge variant="outline" />}>
                                      {stats.dismissedCount} dismissed
                                    </TooltipTrigger>
                                    <TooltipContent>Dismissed in this investigation</TooltipContent>
                                  </Tooltip>
                                ) : null}
                                {stats ? (
                                  <SubmissionStatusTags
                                    tags={getSubmissionStatusTags(stats.statusGroups)}
                                    wrap={false}
                                  />
                                ) : null}
                                {stats && stats.classifiedCount > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger render={<Badge variant="outline" />}>
                                      {stats.classifiedCount} classified
                                    </TooltipTrigger>
                                    <TooltipContent>Already classified</TooltipContent>
                                  </Tooltip>
                                ) : null}
                                <Tooltip>
                                  <TooltipTrigger
                                    render={
                                      <Button
                                        variant="outline"
                                        size="xs"
                                        disabled={visibleGroupItems.length === 0}
                                        onClick={() => {
                                          setSelectedItemIds((previous) => {
                                            const next = new Set(previous);
                                            for (const item of visibleGroupItems) {
                                              if (allGroupItemsSelected) {
                                                next.delete(item.id);
                                              } else {
                                                next.add(item.id);
                                              }
                                            }

                                            return next;
                                          });
                                        }}
                                      />
                                    }>
                                    <Check />
                                    {allGroupItemsSelected ? 'Deselect all' : 'Select all'}
                                  </TooltipTrigger>
                                  <TooltipContent>{selectAllHint}</TooltipContent>
                                </Tooltip>
                                {group.channelId && stats ? (
                                  <ChannelFlagPicker
                                    authorId={group.channelId}
                                    label={group.label}
                                    flag={stats.flag}
                                    note={stats.flagNote}
                                  />
                                ) : null}
                                {group.channelId ? (
                                  <Button
                                    size="xs"
                                    disabled={hydrating}
                                    onClick={() => {
                                      void handleClassifyChannel(group);
                                    }}>
                                    {hydrating ? <Spinner /> : <Tag />}
                                    Classify channel
                                  </Button>
                                ) : null}
                                <Tooltip>
                                  <TooltipTrigger
                                    render={
                                      <Button
                                        variant="outline"
                                        size="xs"
                                        disabled={
                                          visibleGroupItems.length === 0 && mustUseIdListDismiss
                                        }
                                        onClick={() => {
                                          handleSetChannelStatus(
                                            group,
                                            fullyDismissed ? 'active' : 'dismissed',
                                            visibleGroupItems
                                          );
                                        }}
                                      />
                                    }>
                                    {fullyDismissed ? <RotateCcw /> : <X />}
                                    {fullyDismissed ? 'Restore channel' : 'Dismiss channel'}
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {dismissChannelHint}
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger render={<Badge variant="outline" />}>
                                    {group.count}
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {group.count} {ITEM_NOUN_PLURAL}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                            <CollapsibleContent>
                              <div className="max-h-[70vh] overflow-y-auto overscroll-contain p-3">
                                {loadingChannelItems ? (
                                  <div className="flex justify-center py-6">
                                    <Spinner className="size-5" />
                                  </div>
                                ) : channelItemsErrored ? (
                                  <div className="flex flex-col items-center gap-2 py-6">
                                    <p className="text-muted-foreground text-xs">
                                      Couldn&apos;t load this channel&apos;s {ITEM_NOUN_PLURAL}.
                                    </p>
                                    <Button
                                      variant="outline"
                                      size="xs"
                                      onClick={() => void ensureChannelItemsLoaded(group)}>
                                      Retry
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    {channelItemsTruncatedKeys.has(group.key) ? (
                                      <p className="text-muted-foreground mb-3 text-xs">
                                        Showing the first {CHANNEL_ITEMS_PAGE_SIZE.toLocaleString()}{' '}
                                        {ITEM_NOUN_PLURAL} from this channel.
                                      </p>
                                    ) : null}
                                    {/* Gated on smartHiddenGroupItems rather than just an empty
                                    visibleGroupItems, so a group emptied by the triage toggles or
                                    the match-tier filter doesn't get told the smart filter did it.
                                    "Show all" is the same setSmartFilterEnabled(false) the banner
                                    at the top of the list uses. */}
                                    {visibleGroupItems.length === 0 && smartHiddenGroupItems > 0 ? (
                                      <div className="flex flex-col items-center gap-2 py-6">
                                        <p className="text-muted-foreground text-xs">
                                          The smart filter hid all {smartHiddenGroupItems}{' '}
                                          {smartHiddenGroupItems === 1
                                            ? ITEM_NOUN
                                            : ITEM_NOUN_PLURAL}{' '}
                                          loaded for this channel.
                                        </p>
                                        <Button
                                          variant="outline"
                                          size="xs"
                                          onClick={() => {
                                            setSmartFilterEnabled(false);
                                          }}>
                                          Show all
                                        </Button>
                                      </div>
                                    ) : (
                                      renderItems(visibleGroupItems)
                                    )}
                                  </>
                                )}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                      {displayChannelGroups.length > renderedChannelGroupCount ? (
                        <div className="flex justify-center pt-4 pb-4">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setRenderedChannelGroupCount(
                                (count) => count + CHANNEL_GROUPS_PAGE_SIZE
                              );
                            }}>
                            Show more channels ({renderedChannelGroupCount.toLocaleString()} of{' '}
                            {displayChannelGroups.length.toLocaleString()})
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    renderItems(visibleItems)
                  )}

                  {/* Infinite Scroll Pagination Trigger, with a manual fallback once the auto-load
                  budget below is spent - client-side triage filtering never resets it, so without
                  this the sentinel could stay permanently un-fired. Hidden entirely in job-mode
                  grouping - see showFlatItemPagination. */}
                  {showFlatItemPagination && itemHasMore && !loadingItems && autoLoadExhausted ? (
                    <div className="flex justify-center pt-6 pb-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={loadingMore || loadingAllForGrouping}
                        onClick={() => void loadMore()}>
                        {loadingMore ? <Spinner /> : null}
                        {loadingMore ? 'Fetching media...' : 'Load more items'}
                      </Button>
                    </div>
                  ) : showFlatItemPagination && itemHasMore && !loadingItems ? (
                    <div ref={itemsSentinelRef} className="h-1" />
                  ) : null}
                  {showFlatItemPagination && loadingMore && !autoLoadExhausted ? (
                    <div className="flex items-center justify-center gap-2 pt-6 pb-4">
                      <Spinner />
                      <p className="text-muted-foreground text-xs">Fetching media...</p>
                    </div>
                  ) : null}
                </div>

                {/* Bulk selection bar */}
                <ItemsBulkBar
                  selectedCount={selectedItemIds.size}
                  totalCount={visibleItems.length}
                  showClearFlag={selectedItems.some((i) => i.flag)}
                  copyIds={() => itemIdsForClipboard(selectedItems)}
                  copyIdsRef={copySelectedIdsRef}
                  onSelectAll={
                    allVisibleSelected || selectAllUnavailableInGrouping
                      ? undefined
                      : () => {
                          setSelectedItemIds(new Set(visibleItems.map((i) => i.id)));
                        }
                  }
                  onFlag={(color) => {
                    setItemFlags([...selectedItemIds], color);
                  }}
                  onClassify={() => {
                    setClassifyItems(selectedItems);
                  }}
                  onClear={() => {
                    setSelectedItemIds(new Set());
                  }}>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            bulkSetItemStatus([...selectedItemIds], 'dismissed');
                            setSelectedItemIds(new Set());
                          }}
                        />
                      }>
                      <X />
                      Dismiss
                    </TooltipTrigger>
                    <TooltipContent>Dismiss the selected {ITEM_NOUN_PLURAL}</TooltipContent>
                  </Tooltip>

                  {confirmingTranslateSelected ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <span className="text-muted-foreground text-xs">
                        {(() => {
                          const alreadyEnglish = selectedItems.filter(
                            (i) => getDeclaredLanguageSubtag(i.metadata) === 'en'
                          ).length;
                          const toTranslate = selectedItems.length - alreadyEnglish;
                          return (
                            <>
                              Translate {toTranslate} item{toTranslate === 1 ? '' : 's'}?
                              {alreadyEnglish > 0
                                ? ` (${alreadyEnglish} already English, skipped)`
                                : null}
                            </>
                          );
                        })()}
                      </span>
                      <Button size="xs" onClick={() => void handleTranslateSelected()}>
                        Yes
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          setConfirmingTranslateSelected(false);
                        }}>
                        No
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={translatingSelected}
                      onClick={() => {
                        setConfirmingTranslateSelected(true);
                      }}>
                      {translatingSelected ? <Spinner /> : <Languages />}
                      Translate
                    </Button>
                  )}
                </ItemsBulkBar>
              </>
            ) : (
              <Empty className="h-full p-8">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Search />
                  </EmptyMedia>
                  <EmptyTitle>No investigation selected</EmptyTitle>
                  <EmptyDescription>
                    Choose an investigation from the list, or start a new one from the page header.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </main>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Dialog Modals */}
      <ItemDetailsSheet
        item={detailsItem}
        items={detailsItems}
        onNavigate={setDetailsItem}
        onClassify={handleClassifyItem}
        onClose={() => {
          setDetailsItem(null);
        }}
      />
      <ClassifyDialog
        items={classifyItems}
        classifications={classifications}
        languages={languages}
        topics={topics}
        locations={locations}
        origin="investigations"
        onClose={() => {
          setClassifyItems([]);
          setSelectedItemIds(new Set());
        }}
        onSubmitted={(succeeded, linkedItems, wantP0) => {
          const doneIds = new Set(succeeded.map((i) => i.id));
          setItems((previous) => {
            const knownIds = new Set(previous.map((i) => i.id));
            // The badge reads submission_status_groups, so set it too or it stays blank until a
            // refetch. Optimistic placeholder: the real state (e.g. pending qc review) arrives with
            // the next fetch. Existing groups are kept so another vertical's status isn't lost.
            const justSubmitted = (item: Item) => ({
              submission_status: 'in hopper' as const,
              submission_status_groups: item.submission_status_groups.some(
                (g) => g.status === 'in hopper'
              )
                ? item.submission_status_groups
                : [
                    ...item.submission_status_groups,
                    {status: 'in hopper', policy_violations: [], submitted_at: null}
                  ]
            });
            const updated = previous.map((i) =>
              doneIds.has(i.id) ? {...i, ...justSubmitted(i)} : i
            );
            // A "Classify channel" hydration builds its actor item client-side - it was never
            // fetched from /items, so it isn't in `previous` yet. Add it now so the channel
            // section shows it (and so a second click on the same channel reclassifies it
            // instead of hydrating and creating a duplicate actor item).
            const hydrated = succeeded
              .filter((i) => !knownIds.has(i.id))
              .map((i) => ({...i, ...justSubmitted(i)}));
            return [...updated, ...hydrated];
          });
          if (wantP0) {
            p0Dialog.launch(linkedItems);
          }
        }}
      />

      <CreateP0AlertDialog {...p0Dialog.dialogProps()} />

      <ImageLightbox
        open={imageLightboxOpen}
        imageUrl={selectedImageUrl}
        alt="What this investigation was searched with"
        revealed={imageLightboxRevealed}
        colourised={imageLightboxColourised}
        onClose={() => {
          setImageLightboxOpen(false);
        }}
      />

      {shareTarget ? (
        <ShareDialog
          open
          kind={shareTarget.kind}
          entityId={shareTarget.id}
          entityLabel={shareTarget.label}
          onClose={() => {
            setShareTarget(null);
          }}
        />
      ) : null}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}>
        <AlertDialogContent size="sm">
          {pendingDelete ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {pendingDelete.kind === 'folder'
                    ? `Delete folder “${pendingDelete.folder.name}”?`
                    : `Delete investigation “${jobDisplayLabel(pendingDelete.job)}”?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {pendingDelete.kind === 'folder'
                    ? folderDeleteDescription(pendingDelete.folder, pendingDelete.hasSubfolders)
                    : 'This permanently deletes the investigation, its results and shares, and any follower investigations it started, and its schedule if it has one.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    if (pendingDelete.kind === 'folder') {
                      void handleDeleteFolder(pendingDelete.folder.id);
                    } else {
                      void deleteJob(pendingDelete.job);
                    }

                    setPendingDelete(null);
                  }}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>

      {/* One dialog for every "New investigation": the rail's toolbar button, each folder's menu,
          Unfiled's, and the header's Duplicate. The state says which folder it files into; a
          duplicate re-keys it so the form starts from that investigation's values. */}
      <NewInvestigationDialog
        key={duplicateOf ? `duplicate-${duplicateOf.id}` : 'new-investigation'}
        trigger="none"
        open={newInvestigationIn !== undefined}
        {...(newInvestigationIn ? {folderId: newInvestigationIn} : {})}
        {...(duplicateOf ? {initialValue: formValueFor(duplicateOf)} : {})}
        onOpenChange={(open) => {
          if (!open) {
            setNewInvestigationIn(undefined);
            setDuplicateOf(null);
          }
        }}
        onCreated={(job) => {
          // Folder assignment can fail (onCreated then fires with the still-ungrouped job), so
          // the job's own folder_id decides which row to expand, not the one the dialog was
          // opened for.
          const folderId = job.folder_id;
          if (!folderId) {
            handleUnfiledJobCreated(job);
            return;
          }

          refreshFolderCounts();
          setJobs((previous) => [job, ...previous.filter((j) => j.id !== job.id)]);
          setExpandedFolders((previous) => new Set([...previous, folderId]));
          void selectJob(job);
        }}
      />

      {scheduling ? (
        <ScheduleDialog
          key={scheduling.id}
          job={scheduling}
          onClose={() => {
            setScheduling(null);
          }}
          onSaved={(job) => {
            setJobs((previous) => previous.map((j) => (j.id === job.id ? job : j)));
            setSelected((previous) => (previous?.id === job.id ? job : previous));
            setScheduling(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ── At-a-glance Flag Icons ──────────────────────────────────────────────────

// Strongest first — the same ordering the API returns matches in, and the order the funnel summary
// reads them out.
const TIER_ORDER = ['Exact', 'PageConfirmed', 'Partial', 'PageOnly'] as const;

/**
 Whether a result is hidden by the match-strength filter.

 An item we can't grade stays visible. Every dispatched id is recorded with a tier, so an untiered
 item is already the anomalous case - and hiding a row an analyst has no way to account for is worse
 than showing one whose badge is missing. An empty exclusion set, or a job with no tiers at all
 (i.e. anything but image_search), never hides anything.
 */
export function isHiddenByMatchTier(
  item: Item,
  tiersByVideoId: ReadonlyMap<string, ImageMatchTier>,
  excludedTiers: ReadonlySet<ImageMatchTier>
) {
  if (tiersByVideoId.size === 0 || excludedTiers.size === 0) {
    return false;
  }

  const youtubeId = imageMatchIdFor(item);
  const tier = youtubeId ? tiersByVideoId.get(youtubeId) : undefined;
  return tier !== undefined && excludedTiers.has(tier);
}
